import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Save, Plus, Trash2, Search, ChevronRight, ChevronLeft, Edit2, X, Check, Sparkles, Loader2, FilePen, Settings2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import CascadeValueEditor from '../components/CascadeValueEditor';
import MaintenanceSizeTiersSection from '../components/MaintenanceSizeTiersSection';
import { RecoatCleanupButton } from '../components/RecoatCleanupButton';
import { RatesReviewSheetButtons } from '@/components/RatesReviewSheetButtons';
import { canonicalMaterial } from '@/lib/materials';

const COMPONENTS = [
  { key: 'sheet', label: 'Sheets' }, { key: 'ridge', label: 'Ridges' }, { key: 'valley', label: 'Valleys' },
  { key: 'barge', label: 'Barges' }, { key: 'flashing', label: 'Flashings' }, { key: 'tile', label: 'Tiles' },
  { key: 'spouting', label: 'Spouting' }, { key: 'safety', label: 'Safety' }, { key: 'aquaseal', label: 'Dektite' },
  { key: 'cleaning', label: 'Cleaning' }, { key: 'misc', label: 'Misc' }, { key: 'urgent', label: 'Urgent' },
  { key: 'underlay', label: 'Underlay', extraFields: ['sqm_per_roll'] }, { key: 'fastening', label: 'Fastenings' }, { key: 'recoat', label: 'Re-Coat' },
  { key: 'travel', label: 'Travel' }, { key: 'other', label: 'Other' },
];

const RATE_FIELDS = ['buy_cost', 'materials_rate', 'labour_rate', 'contractor_rate'];
const RATE_LABELS = { buy_cost: 'Buy $', materials_rate: 'Materials $', labour_rate: 'Labour $', contractor_rate: 'Contractor $' };

// Extra non-rate text fields imported from the legacy Access dropdown tables
// (UrgentSupplier, UrgentMaterialsDesc, UrgentSupplierCode, UrgentLabourCode,
// UrgentLabourDetails, UrgentLabourDescription, etc.). Rendered in the table
// when present, all editable.
//
// `labour_code` is the legacy field name but the user re-purposed it as a
// shared Labour AND Material category cascade key. The canonical
// vocabulary is the unified roof-type family list:
//   Iron / Concrete / Clay / Tin Tile / Membrane / Clears
// — pulled from `/api/component-rate-tables/lab-mat-families` so the
// same source-of-truth backs the dropdown on every component type.
// Display label is `Lab/Mat` to reflect the unified semantic; the
// storage field stays `labour_code` so existing data continues to work
// without a migration. `useDatalist: true` swaps the edit-mode input
// for a combobox, and `globalSource: 'lab_mat_families'` tells the
// page to blend the canonical 6-family list with any one-off legacy
// values already present on rows.
//
// `supplier` uses the same combobox treatment AND `globalSource: 'suppliers'`
// — the page fetches `/api/suppliers` once and merges those names into the
// option list, so the operator can pick from the canonical Suppliers
// directory + any one-off supplier names typed directly on rate rows. This
// gives the operator a consistent picker across every component type.
const TEXT_FIELDS = [
  { k: 'supplier', l: 'Supplier', useDatalist: true, globalSource: 'suppliers' },
  { k: 'supplier_code', l: 'Supplier Code' },
  { k: 'materials_desc', l: 'Materials Desc' },
  // Lab/Mat — strict <select> dropdown sourced from the canonical
  // roof-type families (Iron / Concrete / Clay / Tin Tile / Membrane /
  // Clears / Spouting / Paint). `selectOnly: true` swaps the input
  // for a real <select> element so the operator can see every option
  // with one click instead of having to clear the field first to see
  // the autocomplete suggestions. Free-text is intentionally NOT
  // allowed here so the cascade families stay uniform across every
  // component type.
  { k: 'labour_code', l: 'Lab/Mat', selectOnly: true, globalSource: 'lab_mat_families' },
  { k: 'labour_details', l: 'Labour Details' },
  { k: 'labour_desc', l: 'Labour Description' },
];

// Fallback Sheet vocab — used until /component-rate-tables/sheet/vocab
// answers. Kept here so the structured-column dropdowns render
// immediately on first paint instead of as empty selects.
const SHEET_VOCAB_FALLBACK = {
  profiles: ['Corrugate', 'MultiRib', 'MultiDek', 'TrimRib', 'RibLine', 'Eurolite', 'Topglass'],
  materials: ['Galv', 'Maxam', 'Zinc', 'Alu', 'Copper', 'Polycarbonate'],
  compositions: ['Fibreglass', 'Polycarbonate'],
  gauges: ['0.40', '0.45', '0.48', '0.55'],
  rate_types: ['/mtr', '/m²', '/sheet'],
};

function normalizeEntry(entry) {
  const e = { ...entry };
  const keys = Object.keys(e);
  for (const k of keys) {
    const kl = k.toLowerCase();
    if (kl.includes('buycost') || kl.includes('buy_cost')) { if (e.buy_cost == null) e.buy_cost = parseFloat(e[k]) || 0; }
    if ((kl.includes('materialsrate') || kl.includes('ratematerials')) && !kl.includes('replace')) { if (e.materials_rate == null) e.materials_rate = parseFloat(e[k]) || 0; }
    if ((kl.includes('labourrate') || kl.includes('ratelabour')) && !kl.includes('replace')) { if (e.labour_rate == null) e.labour_rate = parseFloat(e[k]) || 0; }
    if (kl.includes('contractorrate') || kl.includes('bargecontractorrate')) { if (e.contractor_rate == null) e.contractor_rate = parseFloat(e[k]) || 0; }
    if (kl.includes('supplier') && !kl.includes('code')) { if (e.supplier == null) e.supplier = e[k]; }
    if (kl.includes('materialsdesc') || kl.includes('materailsdesc') || kl.includes('materialdesc') || kl.includes('materialsdescription')) { if (e.materials_desc == null) e.materials_desc = e[k]; }
    if (kl.includes('suppliercode')) { if (e.supplier_code == null) e.supplier_code = e[k]; }
    if (kl.includes('labourcode')) { if (e.labour_code == null) e.labour_code = e[k]; }
    if (kl.includes('labourdetails')) { if (e.labour_details == null) e.labour_details = e[k]; }
    if (kl.includes('labourdescription') || kl.includes('labourdesc')) { if (e.labour_desc == null) e.labour_desc = e[k]; }
  }
  return e;
}

/* Tiny "Manage values" pill — shared across every structured-filter
 * row. Rendered as a dashed-border button so it visually separates
 * from the canonical pill row. Click opens the cascade-value editor
 * modal scoped to that (component, field) combo.
 */
function ManageCascadePill({ component, field, label, onOpen }) {
  return (
    <button
      data-testid={`${component}-${field}-manage`}
      onClick={() => onOpen({ component, field })}
      className="ml-1 px-2 py-1 rounded-full text-[11px] font-medium border border-dashed border-slate-300 text-slate-500 hover:text-[#D35400] hover:border-[#D35400] hover:bg-white inline-flex items-center gap-1"
      title={`Manage ${label} values — add / rename / delete`}
    >
      <Settings2 size={11} />
      Manage
    </button>
  );
}

// Iter134 — compact chip multi-select used in the cascade-link cells
// (Roof Types + Profiles columns). Renders the picked values as chips
// + a small ⊕ dropdown. Empty = "applies to all roof types/profiles".
// `loadOptions` is an optional async fn that lets the parent lazy-load
// (and cache) per-row narrowed lists — e.g. brands-by-roof-type.
function ChipMultiSelect({ value = [], options = [], onChange, placeholder = 'All', loadOptions = null, hint = '' }) {
  const [open, setOpen] = React.useState(false);
  const [lazy, setLazy] = React.useState(null);   // null = not loaded yet
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);
  React.useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  React.useEffect(() => {
    // Reset lazy-loaded list when the parent's static `options` changes
    // (typically because upstream cascade context changed).
    setLazy(null);
  }, [options]);
  const openMenu = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (loadOptions && lazy === null) {
      setLoading(true);
      try {
        const res = await loadOptions();
        setLazy(Array.isArray(res) ? res : []);
      } catch { setLazy([]); }
      finally { setLoading(false); }
    }
  };
  const picked = Array.isArray(value) ? value : [];
  const liveOptions = lazy !== null && lazy.length > 0 ? lazy : options;
  const remaining = liveOptions.filter(o => !picked.includes(o));
  return (
    <div ref={wrapRef} className="relative">
      <div className="flex flex-wrap gap-0.5 items-center min-h-[24px]">
        {picked.length === 0 && (
          <span className="text-[10px] text-slate-400 italic">{placeholder}</span>
        )}
        {picked.map(v => (
          <span key={v} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-semibold">
            {v}
            <button
              type="button"
              onClick={() => onChange(picked.filter(x => x !== v))}
              className="hover:text-orange-600"
              title={`Remove ${v}`}
            >×</button>
          </span>
        ))}
        <button
          type="button"
          onClick={openMenu}
          className="text-[10px] font-bold px-1 py-0.5 rounded border border-slate-300 text-slate-600 hover:bg-slate-100"
          title="Link this rate row to roof types / profiles"
        >+</button>
      </div>
      {open && (
        <div className="absolute left-0 z-20 mt-1 w-48 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg p-1">
          {hint && <div className="px-2 py-1 text-[9px] text-slate-400 uppercase tracking-wide border-b border-slate-100 mb-1">{hint}</div>}
          {loading && <div className="px-2 py-1 text-[10px] text-slate-400">Loading…</div>}
          {!loading && remaining.length === 0 && <div className="px-2 py-1 text-[10px] text-slate-400">All linked</div>}
          {!loading && remaining.map(o => (
            <button
              key={o}
              type="button"
              onClick={() => { onChange([...picked, o]); }}
              className="block w-full text-left px-2 py-1 text-[11px] hover:bg-orange-50 rounded"
            >{o}</button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ComponentRateTablesPage() {
  const { api } = useAuth();
  // Allow deep-linking from other pages (e.g. Settings → Pipe Usages
  // sends `?component=aquaseal&variant=Retrofit`). We read once on
  // mount so any subsequent in-page sidebar clicks override naturally.
  const [active, setActive] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    // Iter258 — Normalise to lowercase. External deep-links (e.g. the
    // Contractor tab's "Edit rates" button) pass the DB-cased
    // `component_type` value (e.g. `Aquaseal`, `Sheet`), but our
    // internal cascade keys are all lowercase. Without this normalise,
    // `?component=Aquaseal` lands on an unknown key → counts lookup
    // returns 0 → operator sees an empty "Types (0)" list.
    return (params.get('component') || 'sheet').toLowerCase();
  });
  const [variantFromUrl] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('variant') || '';
  });
  // Iter 103: builder dropdowns deep-link with `?highlight={rate_id}` so
  // the operator can hop straight from a quote line to the catalogue
  // row that drives it. We auto-scroll + apply a pulse-ring class for
  // 4s, then drop the highlight so navigation back/forth doesn't keep
  // re-flashing the same row.
  const [highlightRowId, setHighlightRowId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('highlight') || '';
  });
  // Iter 108 — when entries finish loading + the URL carries a
  // highlight target that's actually present in `entries`, fire a
  // one-shot scroll-into-view by id. The per-row ref callback above
  // covers the initial mount, but if the operator switches components
  // via the slider after landing, the new render may need a fresh
  // scroll trigger.
  const [kind] = useState('type');
  const [entries, setEntries] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(() => {
    // Deep-link `?search=` (e.g. from the Materials Audit Console
    // "View in Rate Table" row action) prefills the search box so the
    // operator lands directly on the correct catalogue entry.
    const params = new URLSearchParams(window.location.search);
    return params.get('search') || '';
  });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  // Bulk normalise / review names modal — steps the operator through
  // every row of the active component one at a time, surfacing
  // smart-rename suggestions (Aquaseal retrofit "WITH" → "WITH CLIPS"
  // first, then generic cleanups like trailing whitespace + double
  // spaces). Operator can accept/skip/edit per row.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [addMode, setAddMode] = useState(false);
  const [newEntry, setNewEntry] = useState({ name: '' });
  // Iter276-audit — "Add (copy from row…)" picker. Lets the operator
  // pick ANY existing row (not just the last) as a template for a new
  // one — e.g. duplicating a Plain screw as its Coloured variant.
  const [copyPickerOpen, setCopyPickerOpen] = useState(false);
  const [copyPickerQuery, setCopyPickerQuery] = useState('');
  // Cascade-value editor modal — controls the in-context "Manage"
  // affordance on each filter-pill row. Holds `{component, field}`
  // when open, `null` when closed.
  const [cascadeEditor, setCascadeEditor] = useState(null);

  // Horizontal-scroll controller for the data subform. Lets the
  // operator step through the columns (NAME / PROFILE / MATERIAL / …
  // / SUPPLIER / LABOUR DETAILS / LABOUR DESCRIPTION) with chevron
  // arrows instead of having to grab the scrollbar. Attached to the
  // `overflow-auto` wrapper around the table so the same control
  // works on every component page.
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  // Mirrors the table's natural width so the top-scroll spacer is
  // the right size — keeps the top scrollbar thumb proportional to
  // the bottom one. Updated on every data load + window resize.
  const [tableScrollWidth, setTableScrollWidth] = useState(0);
  const scrollTable = (dir) => {
    const el = tableScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };
  // Two-way scroll sync between the top scrollbar strip and the
  // table's own (bottom) scrollbar. The `syncing` ref guards against
  // the recursive scroll-event loop that would otherwise fire when
  // each side mirrors the other.
  const syncingScroll = useRef(false);
  const handleTopScroll = () => {
    if (syncingScroll.current) { syncingScroll.current = false; return; }
    const top = topScrollRef.current;
    const bot = tableScrollRef.current;
    if (!top || !bot) return;
    syncingScroll.current = true;
    bot.scrollLeft = top.scrollLeft;
  };
  const handleBottomScroll = () => {
    if (syncingScroll.current) { syncingScroll.current = false; return; }
    const top = topScrollRef.current;
    const bot = tableScrollRef.current;
    if (!top || !bot) return;
    syncingScroll.current = true;
    top.scrollLeft = bot.scrollLeft;
  };
  // Re-measure the table's natural width whenever the data changes,
  // the active component changes, or the window resizes. Uses
  // requestAnimationFrame so the read happens after React has
  // painted the new <table>.
  useEffect(() => {
    const measure = () => {
      const el = tableScrollRef.current;
      if (!el) return;
      // The table is the first <table> child of the scroll container.
      const table = el.querySelector('table');
      const w = table ? table.scrollWidth : el.scrollWidth;
      setTableScrollWidth(w);
    };
    const raf = window.requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
    };
  });

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get('/component-rate-tables');
      const m = {};
      data.forEach(c => { m[c.component] = c; });
      setCounts(m);
    } catch {}
  }, [api]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/component-rate-tables/${active}/${kind}`);
      setEntries(data.filter(e => !e.archived_at).map(normalizeEntry));
    } catch { setEntries([]); }
    setLoading(false);
  }, [api, active, kind]);

  useEffect(() => { loadCounts(); }, [loadCounts]);

  // Iter134 — Save the cascade-link arrays for a rate row. We patch
  // the entry in-place and post a minimal PUT so the rest of the row
  // isn't touched.
  const saveCascadeLinks = async (rowId, patch) => {
    try {
      const target = entries.find(e => e._id === rowId);
      if (!target) return;
      const next = { ...target, ...patch };
      await api.put(`/component-rate-tables/${rowId}`, next);
      setEntries(prev => prev.map(e => e._id === rowId ? next : e));
    } catch (err) {
      console.error('cascade link save failed', err);
    }
  };


  // Iter134 — Roof Type & Profile cascade-link options. Sourced from
  // the same /api/dropdowns/* lists that drive the Roof Details
  // sub-form (Builder + Components > Roof Details Adjustments). Each
  // rate row carries `roof_type_links[]` + `profile_links[]`; the
  // Components tab uses these arrays to narrow the Type dropdown.
  const [roofTypeOptions, setRoofTypeOptions] = useState([]);
  const [roofBrandOptions, setRoofBrandOptions] = useState([]);
  const [profileOptions, setProfileOptions] = useState([]);
  // Iter134b — per-row cascade caches. Brand-by-roof-type and
  // Profile-by-brand. Keys are the upstream value(s); empty array =
  // confirmed empty (so we don't keep re-fetching).
  const brandsByTypeCacheRef = useRef({});
  const profilesByBrandCacheRef = useRef({});

  // Fetch the union of brands valid for ANY of the picked roof types.
  // Falls back to the full roof_brands list when nothing is picked.
  const fetchBrandsForTypes = useCallback(async (typeArr) => {
    if (!Array.isArray(typeArr) || typeArr.length === 0) return roofBrandOptions;
    const cache = brandsByTypeCacheRef.current;
    const out = new Set();
    for (const t of typeArr) {
      if (!t) continue;
      if (!cache[t]) {
        try {
          const r = await api.get(`/roof-brands?roof_type=${encodeURIComponent(t)}`);
          cache[t] = Array.isArray(r.data) ? r.data : [];
        } catch { cache[t] = []; }
      }
      cache[t].forEach(b => out.add(b));
    }
    const union = Array.from(out);
    return union.length ? union : roofBrandOptions;
  }, [api, roofBrandOptions]);

  const fetchProfilesForBrands = useCallback(async (brandArr) => {
    if (!Array.isArray(brandArr) || brandArr.length === 0) return profileOptions;
    const cache = profilesByBrandCacheRef.current;
    const out = new Set();
    for (const b of brandArr) {
      if (!b) continue;
      if (!cache[b]) {
        try {
          const r = await api.get(`/roof-profiles?brand=${encodeURIComponent(b)}`);
          cache[b] = Array.isArray(r.data) ? r.data : [];
        } catch { cache[b] = []; }
      }
      cache[b].forEach(p => out.add(p));
    }
    const union = Array.from(out);
    return union.length ? union : profileOptions;
  }, [api, profileOptions]);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [rt, rb, pr] = await Promise.all([
          api.get('/dropdowns/roof_types'),
          api.get('/dropdowns/roof_brands'),
          api.get('/dropdowns/roof_profiles'),
        ]);
        const rtArr = Array.isArray(rt.data) ? rt.data : (rt.data?.options || []);
        const rbArr = Array.isArray(rb.data) ? rb.data : (rb.data?.options || []);
        const prArr = Array.isArray(pr.data) ? pr.data : (pr.data?.options || []);
        if (!cancel) { setRoofTypeOptions(rtArr); setRoofBrandOptions(rbArr); setProfileOptions(prArr); }
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [api]);
  useEffect(() => { loadEntries(); setEditId(null); setAddMode(false); setSearch(''); setMaterialFilter('all'); setVariantFilter('all'); setSheetProfileFilter('all'); setSheetMaterialFilter('all'); setSheetGaugeFilter('all'); setRidgeClassFilter('all'); setRidgeMaterialFilter('all'); setValleyWorkFilter('all'); setValleyMaterialFilter('all'); setValleyProfileFilter('all'); setValleyClassFilter('all'); setFlashingMaterialFilter('all'); setFlashingWidthFilter('all'); setFlashingFoldsFilter('all'); setSpoutingKindFilter('all'); setSpoutingProfileFilter('all'); setSpoutingMaterialFilter('all'); setUnderlayBrandFilter('all'); setUnderlayVariantFilter('all'); setBargeClassFilter('all'); setBargeMaterialFilter('all'); setBargeProfileFilter('all'); setFastWorkFilter('all'); setFastRateFilter('all'); setFastVariantFilter('all'); setRecoatTypeFilter('all'); setCleaningTypeFilter('all'); setTileWorkFilter('all'); setTileTypeFilter('all'); setSafetyKindFilter('all'); setMiscCategoryFilter('all'); setUrgentTypeFilter('all'); setTravelTradeFilter('all'); }, [loadEntries]);

  // Iter 108 — re-fire scroll-into-view when entries finish loading
  // and the URL carries a `?highlight=` target that lives in the
  // current entries array. The per-row ref callback handles the
  // initial mount, but switching components via the slider re-runs
  // `loadEntries` and may rebuild the table after the ref already
  // fired — this effect catches that case.
  useEffect(() => {
    if (!highlightRowId || loading) return;
    if (!entries.some(e => e._id === highlightRowId)) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="rate-row-${highlightRowId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 350);
    return () => clearTimeout(t);
  }, [highlightRowId, entries, loading]);


  // On first mount, if a `?variant=Std|Retrofit` URL param was passed
  // alongside `?component=aquaseal`, honour it AFTER the initial
  // `loadEntries` effect (which resets every filter to 'all').
  useEffect(() => {
    if (variantFromUrl && active === 'aquaseal') setVariantFilter(variantFromUrl);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist `patch` (merged with the existing editForm) directly to
  // the backend. Used by auto-save flows where the operator picks a
  // strict-enum value (Lab/Mat select, etc.) and the change should
  // commit immediately — saves having to scroll right to find the
  // green-check Save button on wide structured-column rows. Returns
  // the merged patch so callers can update local state without
  // waiting for the reload.
  const persistEdit = async (patch) => {
    if (!editId) return null;
    const next = { ...editForm, ...patch };
    setEditForm(next);
    try {
      await api.put(`/component-rate-tables/${editId}`, next);
      // Don't drop out of edit mode — operator may be changing
      // several fields in the same row. Reload so the projected
      // `effective_*` fields refresh against the new override.
      loadEntries();
      return next;
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
      return null;
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/component-rate-tables/${editId}`, editForm);
      setEditId(null);
      loadEntries();
      loadCounts();
    } catch (e) { alert(e?.response?.data?.detail || 'Save failed'); }
  };

  const addEntry = async () => {
    if (!newEntry.name?.trim()) return;
    try {
      await api.post(`/component-rate-tables/${active}/${kind}`, newEntry);
      setAddMode(false);
      setNewEntry({ name: '' });
      loadEntries();
      loadCounts();
    } catch (e) { alert(e?.response?.data?.detail || 'Add failed'); }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Permanently delete this Quote Type / Profile? Cannot be undone.')) return;
    try {
      await api.delete(`/component-rate-tables/${id}`);
      loadEntries();
      loadCounts();
    } catch {}
  };

  // One-click variant override toggle for Aquaseal rows. Cycles
  // through the 4 catalogue states:
  //   Auto (no overrides) →
  //   Std pinned   (epdm material, is_retrofit=false) →
  //   Retrofit pin (epdm material, is_retrofit=true) →
  //   Lead pinned  (lead material) →
  //   back to Auto.
  // Persists `is_retrofit_override` + `material_override` on the
  // row so downstream auto-link uses the operator's intent.
  const cycleVariantOverride = async (row) => {
    if (!isAquaseal) return;
    const curMat = row.material_override;
    const curRet = row.is_retrofit_override;
    let nextMat, nextRet;
    if (curMat === 'lead') {
      // Lead → Auto (clear both)
      nextMat = null; nextRet = null;
    } else if (curMat === 'epdm' && curRet === true) {
      // Retrofit pinned → Lead pinned
      nextMat = 'lead'; nextRet = null;
    } else if (curMat === 'epdm' && curRet === false) {
      // Std pinned → Retrofit pinned
      nextMat = 'epdm'; nextRet = true;
    } else {
      // Auto / unpinned → Std pinned
      nextMat = 'epdm'; nextRet = false;
    }
    try {
      await api.put(`/component-rate-tables/${row._id}`, {
        ...row,
        material_override: nextMat,
        is_retrofit_override: nextRet,
      });
      loadEntries();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Variant update failed');
    }
  };

  // One-click bootstrap of the rate tables from existing legacy dropdowns.
  // Idempotent — never overwrites an existing rate or duplicates a Quote Type.
  const [bootstrapping, setBootstrapping] = useState(false);
  const [bootstrapResult, setBootstrapResult] = useState(null);
  const [materialFilter, setMaterialFilter] = useState('all');
  // The inner Components sidebar is duplicated by the top/bottom sliders
  // added recently, so default it to collapsed. The user can still expand
  // it via the chevron when they want the full vertical list.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const runBootstrap = async () => {
    if (!window.confirm('Populate Quote Types & Profiles for every component from the existing dropdown lists (Ridge profiles, Valley profiles, Sheet profiles, Cap Types, etc.)?\n\nRates are left at $0 so you can fill them in. Already-existing rate entries are NEVER touched. Safe to run more than once.')) return;
    setBootstrapping(true);
    setBootstrapResult(null);
    try {
      const { data } = await api.post('/component-rate-tables/bootstrap-from-dropdowns');
      setBootstrapResult(data);
      await loadCounts();
      await loadEntries();
    } catch (e) {
      setBootstrapResult({ ok: false, error: e?.response?.data?.detail || e.message });
    }
    setBootstrapping(false);
  };

  // Flashing-only: detect the legacy material-type prefix from the row name
  // so we can offer a quick pill-filter (Alu, AluGuard, Coloured, Copper,
  // Galv, Maxx, ZinaCore, Zinc, INTERNAL…). For every other component this
  // is a no-op.
  const detectMaterial = (rowName) => {
    const n = (rowName || '').trim();
    if (!n) return 'Other';
    // Match the leading word/token; fall back to "Other" for INTERNAL- etc.
    const head = n.split(/[\s-]/)[0];
    if (!head) return 'Other';
    // Treat "Galvansied" (legacy typo) as "Galv" so it joins the right bucket.
    if (head.toLowerCase().startsWith('galv')) return 'Galv';
    return head;
  };
  const isFlashing = active === 'flashing' && kind === 'type';
  // Aquaseal-specific: split the catalogue into Std (over-the-top
  // boot, hole-cut only) vs Retrofit (cut-and-wrap with clip kit, for
  // obstructed pipes) using the effective flag returned by the
  // backend — which honours an operator override on the row before
  // falling back to the parser's name-derived guess.
  const isAquaseal = active === 'aquaseal' && kind === 'type';
  const isSheet = active === 'sheet' && kind === 'type';
  const isRidge = active === 'ridge' && kind === 'type';
  const isValley = active === 'valley' && kind === 'type';
  const isSpouting = active === 'spouting' && kind === 'type';
  const isUnderlayStruct = active === 'underlay' && kind === 'type';
  const isBargeStruct = active === 'barge' && kind === 'type';
  const isFastening = active === 'fastening' && kind === 'type';
  const isCleaning = active === 'cleaning' && kind === 'type';
  const isTile = active === 'tile' && kind === 'type';
  const isSafety = active === 'safety' && kind === 'type';
  const isMisc = active === 'misc' && kind === 'type';
  const isUrgent = active === 'urgent' && kind === 'type';
  const isTravel = active === 'travel' && kind === 'type';
  const [cleaningTypeFilter, setCleaningTypeFilter] = useState('all');
  const cleaningBuckets = isCleaning ? entries.reduce((acc, e) => {
    const t = e.effective_clean_type || '—';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {}) : {};

  // Structured-column filters for the 5 small / heterogeneous
  // catalogues. Each has a single (or 2-field) enum bucket map.
  const [tileWorkFilter, setTileWorkFilter] = useState('all');
  const [tileTypeFilter, setTileTypeFilter] = useState('all');
  const [tileClassFilter, setTileClassFilter] = useState('all');
  const [tileProfileFilter, setTileProfileFilter] = useState('all');
  const tileBuckets = isTile ? entries.reduce((acc, e) => {
    const w = e.effective_work_type || '—';
    const t = e.effective_tile_type || '—';
    const c = e.effective_class || '—';
    const p = e.effective_profile || '—';
    acc.work[w] = (acc.work[w] || 0) + 1;
    acc.type[t] = (acc.type[t] || 0) + 1;
    acc.class[c] = (acc.class[c] || 0) + 1;
    acc.profile[p] = (acc.profile[p] || 0) + 1;
    return acc;
  }, { work: {}, type: {}, class: {}, profile: {} }) : { work: {}, type: {}, class: {}, profile: {} };

  const [safetyKindFilter, setSafetyKindFilter] = useState('all');
  const safetyBuckets = isSafety ? entries.reduce((acc, e) => {
    const k = e.effective_kind || '—';
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {}) : {};

  const [miscCategoryFilter, setMiscCategoryFilter] = useState('all');
  const miscBuckets = isMisc ? entries.reduce((acc, e) => {
    const c = e.effective_category || '—';
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {}) : {};

  const [urgentTypeFilter, setUrgentTypeFilter] = useState('all');
  const urgentBuckets = isUrgent ? entries.reduce((acc, e) => {
    const u = e.effective_urgency_type || '—';
    acc[u] = (acc[u] || 0) + 1;
    return acc;
  }, {}) : {};

  const [travelTradeFilter, setTravelTradeFilter] = useState('all');
  const travelBuckets = isTravel ? entries.reduce((acc, e) => {
    const t = e.effective_trade || '—';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {}) : {};
  const isRecoat = active === 'recoat' && kind === 'type';

  // Re-Coat structured column — single field "Recoat Type" with 4
  // canonical buckets (Chipped / New Concrete / Old Concrete / Iron)
  // plus a fallback "Concrete" bucket for the ambiguous plain-conc
  // rows. Filter pill row + ring colour map to each bucket so the
  // operator can drill by substrate at a glance.
  const [recoatTypeFilter, setRecoatTypeFilter] = useState('all');
  const recoatBuckets = isRecoat ? entries.reduce((acc, e) => {
    const t = e.effective_recoat_type || '—';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {}) : {};

  // Sheet vocab (Profile / Material / Rate Type lists) — pulled
  // from `/component-rate-tables/sheet/vocab` once when the user
  // first lands on the Sheet view. Falls back to the static
  // SHEET_VOCAB_FALLBACK if the request fails so the dropdowns
  // never render empty.
  const [sheetVocab, setSheetVocab] = useState(SHEET_VOCAB_FALLBACK);
  useEffect(() => {
    if (!isSheet) return;
    let cancelled = false;
    // Prefer the cascade-values endpoints so the dropdown refreshes
    // when the operator adds / renames / deletes values via the
    // Manage modal. Fall back to the static `/sheet/vocab`
    // endpoint if any single field doesn't have a doc yet (e.g.
    // fresh DB without seeds run).
    Promise.all([
      api.get('/cascade-values/sheet/profile').catch(() => ({ data: null })),
      api.get('/cascade-values/sheet/material').catch(() => ({ data: null })),
      api.get('/cascade-values/sheet/composition').catch(() => ({ data: null })),
      api.get('/cascade-values/sheet/gauge').catch(() => ({ data: null })),
      api.get('/component-rate-tables/sheet/vocab').catch(() => ({ data: null })),  // for rate_types
    ]).then(([profRes, matRes, compRes, gaugeRes, vocabRes]) => {
      if (cancelled) return;
      setSheetVocab(prev => ({
        profiles:     Array.isArray(profRes.data?.values) && profRes.data.values.length ? profRes.data.values : prev.profiles,
        materials:    Array.isArray(matRes.data?.values) && matRes.data.values.length ? matRes.data.values : prev.materials,
        compositions: Array.isArray(compRes.data?.values) && compRes.data.values.length ? compRes.data.values : prev.compositions,
        gauges:       Array.isArray(gaugeRes.data?.values) && gaugeRes.data.values.length ? gaugeRes.data.values : (prev.gauges || []),
        rate_types:   Array.isArray(vocabRes.data?.rate_types) && vocabRes.data.rate_types.length ? vocabRes.data.rate_types : prev.rate_types,
      }));
    });
    return () => { cancelled = true; };
    // Refresh when the cascade-value editor closes (cascadeEditor
    // goes from object → null) so Manage-modal changes flow through
    // to the edit-mode dropdowns immediately.
  }, [api, isSheet, cascadeEditor, entries]);

  // Ridge cascade-values vocab — loaded from the new
  // `/cascade-values/ridge/*` endpoints so the edit-mode dropdowns
  // stay in sync with anything the operator adds/renames/deletes
  // via the "Manage" modal. Defaults shadow the seeded canonical
  // list so the dropdown is never empty.
  const [ridgeVocab, setRidgeVocab] = useState({
    materials: ['Concrete', 'Clay', 'Galv', 'Maxam', 'Zinc'],
    classes:   ['iron', 'tile', 'mortar'],
  });
  useEffect(() => {
    if (!isRidge) return;
    let cancelled = false;
    Promise.all([
      api.get('/cascade-values/ridge/material').catch(() => ({ data: null })),
      api.get('/cascade-values/ridge/class').catch(() => ({ data: null })),
    ]).then(([matRes, clsRes]) => {
      if (cancelled) return;
      setRidgeVocab(prev => ({
        materials: Array.isArray(matRes.data?.values) && matRes.data.values.length ? matRes.data.values : prev.materials,
        classes:   Array.isArray(clsRes.data?.values) && clsRes.data.values.length ? clsRes.data.values : prev.classes,
      }));
    });
    return () => { cancelled = true; };
  }, [api, isRidge, cascadeEditor, entries]);

  // Valley cascade-values vocab — same pattern as ridgeVocab. Loaded
  // from `/cascade-values/valley/*` so add/rename/delete via the
  // Manage modal flows through to the edit dropdowns immediately.
  const [valleyVocab, setValleyVocab] = useState({
    classes:    ['iron', 'tile', 'mortar', 'concrete', 'clay'],
    work_types: ['New', 'Replace'],
    materials:  ['Galv', 'Maxam', 'Zinc'],
    profiles:   ['300G/3F', '300G/4F', '400G/4F', '400G/5F'],
  });
  useEffect(() => {
    if (!isValley) return;
    let cancelled = false;
    Promise.all([
      api.get('/cascade-values/valley/class').catch(() => ({ data: null })),
      api.get('/cascade-values/valley/work_type').catch(() => ({ data: null })),
      api.get('/cascade-values/valley/material').catch(() => ({ data: null })),
      api.get('/cascade-values/valley/profile').catch(() => ({ data: null })),
    ]).then(([clsRes, wkRes, matRes, profRes]) => {
      if (cancelled) return;
      setValleyVocab(prev => ({
        classes:    Array.isArray(clsRes.data?.values) && clsRes.data.values.length ? clsRes.data.values : prev.classes,
        work_types: Array.isArray(wkRes.data?.values) && wkRes.data.values.length ? wkRes.data.values : prev.work_types,
        materials:  Array.isArray(matRes.data?.values) && matRes.data.values.length ? matRes.data.values : prev.materials,
        profiles:   Array.isArray(profRes.data?.values) && profRes.data.values.length ? profRes.data.values : prev.profiles,
      }));
    });
    return () => { cancelled = true; };
  }, [api, isValley, cascadeEditor, entries]);

  // Barge cascade-values vocab — same pattern as valleyVocab. Loaded
  // from `/cascade-values/barge/*` so Manage-modal edits flow through
  // to the inline edit dropdowns immediately. Defaults shadow the
  // seeded canonical sets in `cascade_values.py` so the dropdowns
  // never render empty if the API request fails.
  const [bargeVocab, setBargeVocab] = useState({
    classes:    ['iron', 'tile', 'mortar', 'concrete', 'clay', 'butynol'],
    materials:  ['Concrete', 'Clay', 'Galv', 'Maxam', 'Zinc', 'Alu'],
    profiles:   ['Standard', 'Ribline', 'Trimrib', 'Corru', 'Steep Pitch', 'Decramastic', 'Scribe'],
    work_types: ['Supply', 'Replace', 'Bed & Point', 'Point'],
  });
  useEffect(() => {
    if (!isBargeStruct) return;
    let cancelled = false;
    Promise.all([
      api.get('/cascade-values/barge/class').catch(() => ({ data: null })),
      api.get('/cascade-values/barge/material').catch(() => ({ data: null })),
      api.get('/cascade-values/barge/profile').catch(() => ({ data: null })),
      api.get('/cascade-values/barge/work_type').catch(() => ({ data: null })),
    ]).then(([clsRes, matRes, profRes, wkRes]) => {
      if (cancelled) return;
      setBargeVocab(prev => ({
        classes:    Array.isArray(clsRes.data?.values) && clsRes.data.values.length ? clsRes.data.values : prev.classes,
        materials:  Array.isArray(matRes.data?.values) && matRes.data.values.length ? matRes.data.values : prev.materials,
        profiles:   Array.isArray(profRes.data?.values) && profRes.data.values.length ? profRes.data.values : prev.profiles,
        work_types: Array.isArray(wkRes.data?.values) && wkRes.data.values.length ? wkRes.data.values : prev.work_types,
      }));
    });
    return () => { cancelled = true; };
  }, [api, isBargeStruct, cascadeEditor, entries]);

  // Tile cascade-values vocab — Class / Material (legacy field name
  // `tile_type`) / Profile / Work Type. Operator-extendable via the
  // Manage modal. Same load + refresh pattern as bargeVocab.
  const [tileVocab, setTileVocab] = useState({
    classes:    ['concrete', 'clay', 'butynol'],
    materials:  ['Clay', 'Concrete', 'Decramastic', 'Decra', 'Colortile', 'Modern', 'Old', '2nds', 'Tin Tile'],
    profiles:   [],
    work_types: ['Lay', 'Replace', 'Supply', 'PanelBeat'],
  });
  useEffect(() => {
    if (!isTile) return;
    let cancelled = false;
    Promise.all([
      api.get('/cascade-values/tile/class').catch(() => ({ data: null })),
      api.get('/cascade-values/tile/tile_type').catch(() => ({ data: null })),
      api.get('/cascade-values/tile/profile').catch(() => ({ data: null })),
      api.get('/cascade-values/tile/work_type').catch(() => ({ data: null })),
    ]).then(([clsRes, matRes, profRes, wkRes]) => {
      if (cancelled) return;
      setTileVocab(prev => ({
        classes:    Array.isArray(clsRes.data?.values) && clsRes.data.values.length ? clsRes.data.values : prev.classes,
        materials:  Array.isArray(matRes.data?.values) && matRes.data.values.length ? matRes.data.values : prev.materials,
        profiles:   Array.isArray(profRes.data?.values) ? profRes.data.values : prev.profiles,
        work_types: Array.isArray(wkRes.data?.values) && wkRes.data.values.length ? wkRes.data.values : prev.work_types,
      }));
    });
    return () => { cancelled = true; };
  }, [api, isTile, cascadeEditor, entries]);

  // Spouting cascade-values vocab — Kind / Profile / Material.
  // Operator-extendable via the Manage modal. Same load + refresh
  // pattern as bargeVocab / tileVocab.
  const [spoutingVocab, setSpoutingVocab] = useState({
    kinds:     ['Gutter', 'Downpipe', 'Snow Strap', 'Bracket'],
    profiles:  ['1/4 round', 'Marley Rnd', 'Marley Sq', 'PVC', 'Continuous', 'Internal', 'External'],
    materials: ['Colorsteel', 'PVC', 'Marley', 'Taylor', 'Colorcote'],
  });
  useEffect(() => {
    if (!isSpouting) return;
    let cancelled = false;
    Promise.all([
      api.get('/cascade-values/spouting/kind').catch(() => ({ data: null })),
      api.get('/cascade-values/spouting/profile').catch(() => ({ data: null })),
      api.get('/cascade-values/spouting/material').catch(() => ({ data: null })),
    ]).then(([kindRes, profRes, matRes]) => {
      if (cancelled) return;
      setSpoutingVocab(prev => ({
        kinds:     Array.isArray(kindRes.data?.values) && kindRes.data.values.length ? kindRes.data.values : prev.kinds,
        profiles:  Array.isArray(profRes.data?.values) && profRes.data.values.length ? profRes.data.values : prev.profiles,
        materials: Array.isArray(matRes.data?.values) && matRes.data.values.length ? matRes.data.values : prev.materials,
      }));
    });
    return () => { cancelled = true; };
  }, [api, isSpouting, cascadeEditor, entries]);

  // Global supplier list — pulled once on mount and reused as a
  // `globalSource` for the Supplier combobox in edit mode (across
  // every component type). Stored as a flat sorted array of supplier
  // names so the datalist render is trivial. Falls back to an empty
  // array on error so the column still works — local-distinct values
  // + free-text typing remain available.
  const [globalSuppliers, setGlobalSuppliers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get('/suppliers').then(({ data }) => {
      if (cancelled) return;
      const names = (Array.isArray(data) ? data : [])
        .map((s) => (s?.name || '').trim())
        .filter(Boolean);
      setGlobalSuppliers(Array.from(new Set(names)).sort((a, b) => a.localeCompare(b)));
    }).catch(() => { /* keep empty fallback */ });
    return () => { cancelled = true; };
  }, [api]);

  // Canonical Lab/Mat cascade families — `["Iron", "Concrete", "Clay",
  // "Tin Tile", "Membrane", "Clears"]`. Loaded once from
  // `/component-rate-tables/lab-mat-families` so the same source-of-
  // truth list backs the dropdown across every component type. Static
  // fallback kept so the dropdown never renders empty if the request
  // fails. The list is blended with any legacy one-off cascade values
  // already on individual rows so existing data stays accessible.
  const LAB_MAT_FALLBACK = ['Iron', 'Concrete', 'Clay', 'Tin Tile', 'Butynol', 'Clears', 'Spouting', 'Paint'];
  const [labMatFamilies, setLabMatFamilies] = useState(LAB_MAT_FALLBACK);
  useEffect(() => {
    let cancelled = false;
    api.get('/component-rate-tables/lab-mat-families').then(({ data }) => {
      if (cancelled) return;
      if (Array.isArray(data?.families) && data.families.length) {
        setLabMatFamilies(data.families);
      }
    }).catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, [api]);

  // Canonical cascade-value map keyed by `<component>_<field>`. Loaded
  // once from `/cascade-values` (the master listing) so we can merge
  // canonical values into every structured-filter pill row — even the
  // ones with zero matching rows currently in the catalogue. This
  // way the operator can SEE that e.g. "Zinc (0)" is a valid bucket
  // before any data has been classified into it. Refreshed when the
  // Manage modal closes so add/rename/delete propagates instantly.
  const [canonicalMap, setCanonicalMap] = useState({});
  useEffect(() => {
    let cancelled = false;
    api.get('/cascade-values').then(({ data }) => {
      if (cancelled) return;
      if (!Array.isArray(data)) return;
      const map = {};
      data.forEach((row) => {
        const key = `${row.component}_${row.field}`;
        if (Array.isArray(row.values)) map[key] = row.values;
      });
      setCanonicalMap(map);
    }).catch(() => { /* keep empty — pills still render with non-zero buckets */ });
    return () => { cancelled = true; };
    // Refresh on cascadeEditor close so Manage modal edits flow in.
  }, [api, cascadeEditor]);

  /**
   * Merge a {value: count} bucket map with the canonical cascade
   * values for (component, field) so 0-count canonical values are
   * also rendered as pills. Non-canonical values stored on rows
   * (legacy / one-off overrides) stay visible alongside.
   */
  const mergeWithCanonical = (buckets, component, field) => {
    const canon = canonicalMap[`${component}_${field}`] || [];
    if (!canon.length) return buckets;
    const merged = { ...buckets };
    canon.forEach((v) => {
      if (merged[v] === undefined) merged[v] = 0;
    });
    return merged;
  };

  /**
   * Returns true when `effectiveValue` is in the canonical list for
   * (component, field). Used both by filter logic ('__canon__'
   * sentinel) and by the "All <Label>" pill count. Treats blank /
   * '—' as outside the canonical list so the operator can sweep
   * just the well-formed rows.
   */
  const isCanonicalValue = (component, field, effectiveValue) => {
    if (!effectiveValue || effectiveValue === '—') return false;
    const set = canonicalMap[`${component}_${field}`];
    return Array.isArray(set) && set.includes(effectiveValue);
  };

  /**
   * Counts rows in a bucket whose key matches a canonical value
   * (used by the "All <Label>" pill — shows N rows that fall inside
   * the canonical cascade vs N total rows).
   */
  const countCanonical = (buckets, component, field) => {
    const set = new Set(canonicalMap[`${component}_${field}`] || []);
    return Object.entries(buckets || {})
      .filter(([k]) => set.has(k))
      .reduce((sum, [, n]) => sum + n, 0);
  };

  /**
   * Generic filter check used in `entries.filter`:
   *   'all'        → accept every row
   *   '__canon__'  → accept rows where effective ∈ canonical set
   *   <other>      → accept rows where effective === filterValue
   */
  const passesPill = (filterValue, effectiveValue, component, field) => {
    if (filterValue === 'all') return true;
    if (filterValue === '__canon__') return isCanonicalValue(component, field, effectiveValue);
    return (effectiveValue || '—') === filterValue;
  };

  /**
   * Renders the "All <Label>" pill — clicking it selects only rows
   * whose value is in the canonical cascade list for this axis (e.g.
   * "All Class" filters to rows whose class is one of clay /
   * concrete / decra / iron, excluding blank / unknown classes).
   * Returns null when the cascade is empty (no need for the pill).
   *
   * When called with just (label, cascadeField, buckets, value,
   * setter, testid), `component` is auto-derived from the testid
   * prefix (testid format is "<component>-<field>-..." across every
   * pill row block).
   */
  const renderAllLabelPill = (component, label, cascadeField, buckets, value, setter, testid) => {
    // Auto-derive component when passed as null (shorter call sites).
    const comp = component || (testid || '').split('-')[0];
    if (!cascadeField) return null;
    const canon = canonicalMap[`${comp}_${cascadeField}`] || [];
    if (!canon.length) return null;
    const count = countCanonical(buckets, comp, cascadeField);
    return (
      <button
        data-testid={`${testid}-canonical`}
        onClick={() => setter('__canon__')}
        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === '__canon__' ? 'bg-[#1E40AF] text-white border-[#1E40AF]' : 'bg-white text-[#1E40AF] border-[#1E40AF]/40 hover:border-[#1E40AF] hover:bg-[#1E40AF]/5'}`}
        title={`Show only rows whose ${label.toLowerCase()} is one of the canonical values (${canon.join(', ')})`}
      >
        All {label} <span className="opacity-70 ml-0.5">({count})</span>
      </button>
    );
  };

  // Sheet sub-filters — Profile, Material, Gauge. Each pill set is
  // independent; 'all' clears the filter for that axis. Buckets are
  // computed from the projected `effective_*` fields the backend
  // returns, so an operator override on a row puts it in the right
  // bucket immediately (not after a parser re-run).
  const [sheetProfileFilter, setSheetProfileFilter] = useState('all');
  const [sheetMaterialFilter, setSheetMaterialFilter] = useState('all');
  const [sheetGaugeFilter, setSheetGaugeFilter] = useState('all');
  const sheetBuckets = isSheet ? entries.reduce((acc, e) => {
    const p = e.effective_profile || '—';
    const m = e.effective_material || '—';
    const g = e.effective_gauge || '—';
    acc.profile[p] = (acc.profile[p] || 0) + 1;
    acc.material[m] = (acc.material[m] || 0) + 1;
    acc.gauge[g] = (acc.gauge[g] || 0) + 1;
    return acc;
  }, { profile: {}, material: {}, gauge: {} }) : { profile: {}, material: {}, gauge: {} };

  // Ridge sub-filters — Class (iron/tile/mortar) and Material
  // (Galv/Maxx/Zinc/ZinaCore for iron; Modern/Old/Clay/Tudor/Steep/Conc
  // for tile). Pill rows behave the same as Sheet's filter bar.
  const [ridgeClassFilter, setRidgeClassFilter] = useState('all');
  const [ridgeMaterialFilter, setRidgeMaterialFilter] = useState('all');
  const ridgeBuckets = isRidge ? entries.reduce((acc, e) => {
    const c = e.effective_class || '—';
    const m = e.effective_material || '—';
    acc.class[c] = (acc.class[c] || 0) + 1;
    acc.material[m] = (acc.material[m] || 0) + 1;
    return acc;
  }, { class: {}, material: {} }) : { class: {}, material: {} };

  // Valley sub-filters — Work Type (New / Replace) and Material
  // (Galv / Maxx / Zinc / ZinaCore). 5 structured columns mirror
  // the parser fields: work_type / material / profile / width_mm / folds.
  // `profile` is the composite "300G/4F" girth-folds pattern so the
  // operator can pick "all 300G/4F valleys across materials" in one
  // click.
  const [valleyWorkFilter, setValleyWorkFilter] = useState('all');
  const [valleyMaterialFilter, setValleyMaterialFilter] = useState('all');
  const [valleyProfileFilter, setValleyProfileFilter] = useState('all');
  const [valleyClassFilter, setValleyClassFilter] = useState('all');
  const valleyBuckets = isValley ? entries.reduce((acc, e) => {
    const w = e.effective_work_type || '—';
    const m = e.effective_material || '—';
    const p = e.effective_profile || '—';
    const c = e.effective_class || '—';
    acc.work[w] = (acc.work[w] || 0) + 1;
    acc.material[m] = (acc.material[m] || 0) + 1;
    acc.profile[p] = (acc.profile[p] || 0) + 1;
    acc.class[c] = (acc.class[c] || 0) + 1;
    return acc;
  }, { work: {}, material: {}, profile: {}, class: {} }) : { work: {}, material: {}, profile: {}, class: {} };

  // Flashing sub-filters — Material / Width / Folds. Big catalogue
  // (1175 rows) so the buckets are sorted by count and the filter
  // bar is the operator's primary navigation aid.
  const [flashingMaterialFilter, setFlashingMaterialFilter] = useState('all');
  const [flashingWidthFilter, setFlashingWidthFilter] = useState('all');
  const [flashingFoldsFilter, setFlashingFoldsFilter] = useState('all');
  const flashingBuckets = isFlashing ? entries.reduce((acc, e) => {
    const m = e.effective_material || '—';
    const w = e.effective_width_mm != null ? String(e.effective_width_mm) : '—';
    const f = e.effective_folds != null ? String(e.effective_folds) : '—';
    acc.material[m] = (acc.material[m] || 0) + 1;
    acc.width[w] = (acc.width[w] || 0) + 1;
    acc.folds[f] = (acc.folds[f] || 0) + 1;
    return acc;
  }, { material: {}, width: {}, folds: {} }) : { material: {}, width: {}, folds: {} };

  // Spouting sub-filters — Kind / Profile / Material. Width and
  // thickness are too granular to pill-filter on; they show as
  // structured chips per-row instead.
  const [spoutingKindFilter, setSpoutingKindFilter] = useState('all');
  const [spoutingProfileFilter, setSpoutingProfileFilter] = useState('all');
  const [spoutingMaterialFilter, setSpoutingMaterialFilter] = useState('all');
  const spoutingBuckets = isSpouting ? entries.reduce((acc, e) => {
    const k = e.effective_kind || '—';
    const p = e.effective_profile || '—';
    const m = e.effective_material || '—';
    acc.kind[k] = (acc.kind[k] || 0) + 1;
    acc.profile[p] = (acc.profile[p] || 0) + 1;
    acc.material[m] = (acc.material[m] || 0) + 1;
    return acc;
  }, { kind: {}, profile: {}, material: {} }) : { kind: {}, profile: {}, material: {} };

  // Underlay sub-filters — Brand / Variant. Grade is too sparse (3
  // numeric values across 7 rows) to bother filtering on; it just
  // shows as a chip per row.
  const [underlayBrandFilter, setUnderlayBrandFilter] = useState('all');
  const [underlayVariantFilter, setUnderlayVariantFilter] = useState('all');
  const underlayBuckets = isUnderlayStruct ? entries.reduce((acc, e) => {
    const b = e.effective_brand || '—';
    const v = e.effective_variant || '—';
    acc.brand[b] = (acc.brand[b] || 0) + 1;
    acc.variant[v] = (acc.variant[v] || 0) + 1;
    return acc;
  }, { brand: {}, variant: {} }) : { brand: {}, variant: {} };

  // Barge sub-filters — Class + Material + Profile. Mixed catalogue
  // (28 rows: iron / tile / mortar / concrete / membrane) so Class
  // drills the coarse partition.
  const [bargeClassFilter, setBargeClassFilter] = useState('all');
  const [bargeMaterialFilter, setBargeMaterialFilter] = useState('all');
  const [bargeProfileFilter, setBargeProfileFilter] = useState('all');
  const bargeBuckets = isBargeStruct ? entries.reduce((acc, e) => {
    const c = e.effective_class || '—';
    const m = e.effective_material || '—';
    const p = e.effective_profile || '—';
    acc.class[c] = (acc.class[c] || 0) + 1;
    acc.material[m] = (acc.material[m] || 0) + 1;
    acc.profile[p] = (acc.profile[p] || 0) + 1;
    return acc;
  }, { class: {}, material: {}, profile: {} }) : { class: {}, material: {}, profile: {} };

  // Fastening sub-filters — Work Type + Rate Type + Variant.
  const [fastWorkFilter, setFastWorkFilter] = useState('all');
  const [fastRateFilter, setFastRateFilter] = useState('all');
  const [fastVariantFilter, setFastVariantFilter] = useState('all');
  const fastBuckets = isFastening ? entries.reduce((acc, e) => {
    const w = e.effective_work_type || '—';
    const r = e.effective_rate_type || '—';
    const v = e.effective_variant || '—';
    acc.work[w] = (acc.work[w] || 0) + 1;
    acc.rate[r] = (acc.rate[r] || 0) + 1;
    acc.variant[v] = (acc.variant[v] || 0) + 1;
    return acc;
  }, { work: {}, rate: {}, variant: {} }) : { work: {}, rate: {}, variant: {} };
  const [variantFilter, setVariantFilter] = useState(variantFromUrl || 'all');
  // Material filter is orthogonal to Std/Retrofit — Lead-based
  // aquaseals are for concrete & clay tile roofs, EPDM Dektites
  // are for metal roofs. Default 'all' so the operator can still
  // see the whole catalogue side-by-side.
  const [materialFilterAqua, setMaterialFilterAqua] = useState('all');
  const variantBuckets = isAquaseal
    ? entries.reduce((acc, e) => {
        const v = e.effective_material === 'lead'
          ? 'Lead'
          : (e.effective_is_retrofit ? 'Retrofit' : 'Std');
        acc[v] = (acc[v] || 0) + 1;
        return acc;
      }, {})
    : {};
  const materialBuckets = isFlashing
    ? entries.reduce((acc, e) => {
        const m = detectMaterial(e.name);
        acc[m] = (acc[m] || 0) + 1;
        return acc;
      }, {})
    : {};
  // Sort buckets by row-count desc so the busiest material sits first.
  const materialList = Object.entries(materialBuckets)
    .sort((a, b) => b[1] - a[1])
    .map(([k, n]) => ({ key: k, count: n }));

  // Distinct values for each text field that opts into the datalist UX
  // (currently `labour_code` aka Lab/Mat and `supplier`). Computed
  // across the loaded entries of the current component so picking an
  // existing value cascades among the rows the operator is already
  // looking at — much faster than a free-text guess + typo-prone label.
  // Fields with `globalSource: 'suppliers'` also fold in the company-
  // wide Suppliers directory so the picker stays consistent across
  // every component type, not just rows that already cite a supplier.
  const distinctByField = TEXT_FIELDS
    .filter(t => t.useDatalist || t.selectOnly)
    .reduce((acc, t) => {
      const seen = new Set();
      // For the Lab/Mat field: canonical families come FIRST so they
      // sort to the top of the picker. Legacy values get appended at
      // the end of the same `seen` set (Set preserves insertion order).
      if (t.globalSource === 'lab_mat_families') {
        for (const f of labMatFamilies) seen.add(f);
      }
      for (const r of entries) {
        const v = (r[t.k] || '').toString().trim();
        if (v) seen.add(v);
      }
      if (t.globalSource === 'suppliers') {
        for (const n of globalSuppliers) seen.add(n);
      }
      // Lab/Mat: keep canonical-first order, then sort legacy tail.
      // Other fields: full alpha sort.
      if (t.globalSource === 'lab_mat_families') {
        const canon = labMatFamilies.filter(f => seen.has(f));
        const extras = Array.from(seen).filter(v => !labMatFamilies.includes(v)).sort((a, b) => a.localeCompare(b));
        acc[t.k] = [...canon, ...extras];
      } else {
        acc[t.k] = Array.from(seen).sort((a, b) => a.localeCompare(b));
      }
      return acc;
    }, {});

  const filtered = entries.filter(e => {
    if (e.archived_at) return false;
    if (search && !(e.name || '').toLowerCase().includes(search.toLowerCase())) return false;
    if (isAquaseal && variantFilter !== 'all') {
      const v = e.effective_material === 'lead'
        ? 'Lead'
        : (e.effective_is_retrofit ? 'Retrofit' : 'Std');
      if (v !== variantFilter) return false;
    }
    if (isSheet) {
      if (!passesPill(sheetProfileFilter, e.effective_profile || '—', 'sheet', 'profile')) return false;
      if (!passesPill(sheetMaterialFilter, e.effective_material || '—', 'sheet', 'material')) return false;
      if (!passesPill(sheetGaugeFilter, e.effective_gauge || '—', 'sheet', 'gauge')) return false;
    }
    if (isRidge) {
      if (!passesPill(ridgeClassFilter, e.effective_class || '—', 'ridge', 'class')) return false;
      if (!passesPill(ridgeMaterialFilter, e.effective_material || '—', 'ridge', 'material')) return false;
    }
    if (isValley) {
      if (!passesPill(valleyWorkFilter, e.effective_work_type || '—', 'valley', 'work_type')) return false;
      if (!passesPill(valleyMaterialFilter, e.effective_material || '—', 'valley', 'material')) return false;
      if (!passesPill(valleyProfileFilter, e.effective_profile || '—', 'valley', 'profile')) return false;
      if (!passesPill(valleyClassFilter, e.effective_class || '—', 'valley', 'class')) return false;
    }
    if (isFlashing) {
      if (!passesPill(flashingMaterialFilter, e.effective_material || '—', 'flashing', 'material')) return false;
      const w = e.effective_width_mm != null ? String(e.effective_width_mm) : '—';
      const f = e.effective_folds != null ? String(e.effective_folds) : '—';
      if (flashingWidthFilter !== 'all' && w !== flashingWidthFilter) return false;
      if (flashingFoldsFilter !== 'all' && f !== flashingFoldsFilter) return false;
    }
    if (isSpouting) {
      if (!passesPill(spoutingKindFilter, e.effective_kind || '—', 'spouting', 'kind')) return false;
      if (!passesPill(spoutingProfileFilter, e.effective_profile || '—', 'spouting', 'profile')) return false;
      if (!passesPill(spoutingMaterialFilter, e.effective_material || '—', 'spouting', 'material')) return false;
    }
    if (isUnderlayStruct) {
      if (!passesPill(underlayBrandFilter, e.effective_brand || '—', 'underlay', 'brand')) return false;
      if (!passesPill(underlayVariantFilter, e.effective_variant || '—', 'underlay', 'variant')) return false;
    }
    if (isBargeStruct) {
      if (!passesPill(bargeClassFilter, e.effective_class || '—', 'barge', 'class')) return false;
      if (!passesPill(bargeMaterialFilter, e.effective_material || '—', 'barge', 'material')) return false;
      if (!passesPill(bargeProfileFilter, e.effective_profile || '—', 'barge', 'profile')) return false;
    }
    if (isFastening) {
      if (!passesPill(fastWorkFilter, e.effective_work_type || '—', 'fastening', 'work_type')) return false;
      if (!passesPill(fastRateFilter, e.effective_rate_type || '—', 'fastening', 'rate_type')) return false;
      if (!passesPill(fastVariantFilter, e.effective_variant || '—', 'fastening', 'variant')) return false;
    }
    if (isRecoat) {
      if (!passesPill(recoatTypeFilter, e.effective_recoat_type || '—', 'recoat', 'recoat_type')) return false;
    }
    if (isCleaning) {
      if (!passesPill(cleaningTypeFilter, e.effective_clean_type || '—', 'cleaning', 'clean_type')) return false;
    }
    if (isTile) {
      if (!passesPill(tileWorkFilter, e.effective_work_type || '—', 'tile', 'work_type')) return false;
      if (!passesPill(tileTypeFilter, e.effective_tile_type || '—', 'tile', 'tile_type')) return false;
      if (!passesPill(tileClassFilter, e.effective_class || '—', 'tile', 'class')) return false;
      if (!passesPill(tileProfileFilter, e.effective_profile || '—', 'tile', 'profile')) return false;
    }
    if (isSafety) {
      if (!passesPill(safetyKindFilter, e.effective_kind || '—', 'safety', 'kind')) return false;
    }
    if (isMisc) {
      if (!passesPill(miscCategoryFilter, e.effective_category || '—', 'misc', 'category')) return false;
    }
    if (isUrgent) {
      if (!passesPill(urgentTypeFilter, e.effective_urgency_type || '—', 'urgent', 'urgency_type')) return false;
    }
    if (isTravel) {
      if (!passesPill(travelTradeFilter, e.effective_trade || '—', 'travel', 'trade')) return false;
    }
    return true;
  });
  const compLabel = COMPONENTS.find(c => c.key === active)?.label || active;
  const activeComp = COMPONENTS.find(c => c.key === active);
  const extraFields = activeComp?.extraFields || [];

  // Shared prefill helper for both "copy from last" and "copy from row…"
  // buttons. Carries cascade chips, supplier and description fields —
  // deliberately drops Name + rate $ so the operator only fills the
  // unique bits for the new variant (e.g. Plain → Coloured Vortex).
  const prefillFromRow = (src) => {
    if (!src) return { name: '' };
    const CARRY_KEYS = [
      'roof_type_links', 'brand_links', 'profile_links',
      'class', 'work',
      'supplier', 'supplier_code',
      'materials_desc', 'labour_details', 'labour_desc', 'labour_code',
    ];
    const seed = { name: '' };
    CARRY_KEYS.forEach(k => {
      if (src[k] !== undefined && src[k] !== null && src[k] !== '') {
        seed[k] = Array.isArray(src[k]) ? [...src[k]] : src[k];
      }
    });
    TEXT_FIELDS.forEach(t => {
      if (!CARRY_KEYS.includes(t.k) && src[t.k] !== undefined && src[t.k] !== '') {
        seed[t.k] = src[t.k];
      }
    });
    extraFields.forEach(ef => {
      if (src[ef] !== undefined && src[ef] !== '' && src[ef] !== 0) {
        seed[ef] = src[ef];
      }
    });
    return seed;
  };

  return (
    <div data-testid="component-rate-tables-page" className="space-y-4">
      {/* Cascade-value editor modal — opens from the "Manage" button
          on each filter-pill row. Refreshes the entries list on
          change so renamed/deleted values flow through immediately. */}
      {cascadeEditor && (
        <CascadeValueEditor
          component={cascadeEditor.component}
          field={cascadeEditor.field}
          open={!!cascadeEditor}
          onClose={() => setCascadeEditor(null)}
          onChange={loadEntries}
        />
      )}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Component Rate Tables</h1>
          <p className="text-sm text-slate-500 mt-0.5">Buy / Materials / Labour / Contractor rates per Quote Type, per component. Select a component on the left, edit any row inline, delete unused entries.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
        <RatesReviewSheetButtons api={api} onApplied={() => { loadEntries(); loadCounts(); }} />
        <button
          data-testid="bootstrap-rates-btn"
          onClick={runBootstrap}
          disabled={bootstrapping}
          className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-md text-sm font-semibold transition-all disabled:opacity-60"
          title="Seed every component with its legacy Quote Types and Profiles from the existing dropdowns. Idempotent and safe to re-run."
        >
          {bootstrapping ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {bootstrapping ? 'Seeding…' : 'Seed from Dropdowns'}
        </button>
        </div>
      </div>

      {bootstrapResult && (
        <div data-testid="bootstrap-result" className={`rounded-md p-3 text-xs ${bootstrapResult.ok ? 'bg-emerald-50 border border-emerald-200 text-emerald-800' : 'bg-rose-50 border border-rose-200 text-rose-700'}`}>
          {bootstrapResult.ok ? (
            <>
              <strong>Seeded {bootstrapResult.total_inserted || 0} new entries</strong> across {Object.keys(bootstrapResult.summary || {}).length} components — all rates start at $0, ready to be filled in.
            </>
          ) : <><strong>Failed.</strong> {bootstrapResult.error || 'Unknown error.'}</>}
        </div>
      )}

      {/* Top horizontal slider — duplicates the left-nav as scrollable pills
          so the appraiser can hop between components without scrolling all
          the way back up to the sidebar on long rate tables. */}
      <ComponentSlider testIdSuffix="top" components={COMPONENTS} active={active} counts={counts} onPick={setActive} />

      <div className="flex gap-4" style={{ minHeight: '65vh' }}>
        {/* Left Nav — collapsible. When collapsed, the strip narrows to a
            36px column with a single expand-chevron + vertical "COMPONENTS"
            label. Expanded view gives the full vertical pick-list, useful
            when the user prefers it over the top/bottom sliders. */}
        <div
          data-testid="rate-tables-sidebar"
          className={`border border-slate-200 rounded-xl bg-white flex-shrink-0 overflow-hidden transition-all duration-200 ${sidebarCollapsed ? 'w-9' : 'w-48'}`}
        >
          <button
            type="button"
            data-testid="rate-tables-sidebar-toggle"
            onClick={() => setSidebarCollapsed(prev => !prev)}
            className="w-full p-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between text-slate-500 hover:text-[#D35400] hover:bg-orange-50/40 transition-colors"
            title={sidebarCollapsed ? 'Expand components list' : 'Collapse components list'}
          >
            {!sidebarCollapsed && <span className="text-xs font-bold uppercase tracking-wider">Components</span>}
            {sidebarCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
          </button>
          {sidebarCollapsed ? (
            <div className="py-3 flex flex-col items-center text-[10px] font-bold uppercase tracking-[0.3em] text-slate-400" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', minHeight: '180px' }}>
              {COMPONENTS.length} components
            </div>
          ) : (
            <div className="overflow-y-auto" style={{ maxHeight: '60vh' }}>
              {COMPONENTS.map(c => {
                const ct = counts[c.key];
                const isActive = active === c.key;
                return (
                  <button key={c.key} data-testid={`rate-tab-${c.key}`} onClick={() => { setActive(c.key); }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-sm border-b border-slate-100 transition-all ${isActive ? 'bg-orange-50 text-[#D35400] font-semibold border-l-2 border-l-[#D35400]' : 'text-slate-600 hover:bg-slate-50 border-l-2 border-l-transparent'}`}>
                    <span>{c.label}</span>
                    <div className="flex items-center gap-1">
                      {ct && <span className="text-[10px] text-slate-400">{ct.type_count}</span>}
                      <ChevronRight size={12} className="text-slate-300" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Main Panel */}
        <div className="flex-1 bg-white border border-slate-200 rounded-xl flex flex-col" style={{ minWidth: 0 }}>
          {/* Header */}
          <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-bold text-slate-800 font-[Manrope]">{compLabel}</h2>
              <span className="px-3 py-1 rounded-md text-xs font-semibold bg-slate-100 text-slate-600">
                Types ({counts[active]?.type_count || 0})
              </span>
              {/* Iter259 — Audit workspace link. Only surfaced for
                  component_types that have a schema defined in
                  /app/memory/CATALOGUE_SCHEMA.md. Add new types here
                  as they're rolled out. */}
              {active === 'aquaseal' && (
                <a
                  href={`/catalogue-audit/${active}`}
                  data-testid="catalogue-audit-link"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-bold bg-emerald-50 text-emerald-800 ring-1 ring-emerald-300 hover:bg-emerald-100 transition-colors"
                  title="Open the Catalogue Audit workspace — fill in structured fields so cascades read clean data"
                >
                  🧪 Audit fields →
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Horizontal scroll arrows — step the data subform left/right
                  by ~320px per click. Same control on every component page
                  so the operator never has to grab the scrollbar to see
                  the rightmost columns (SUPPLIER / LABOUR DETAILS / LABOUR
                  DESCRIPTION). */}
              <div className="flex items-center gap-0.5 mr-1">
                <button
                  type="button"
                  data-testid="table-scroll-prev"
                  onClick={() => scrollTable(-1)}
                  className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:text-[#D35400] hover:border-[#D35400] flex items-center justify-center transition-all"
                  title="Scroll columns left"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  type="button"
                  data-testid="table-scroll-next"
                  onClick={() => scrollTable(1)}
                  className="w-7 h-7 rounded-md border border-slate-200 text-slate-500 hover:text-[#D35400] hover:border-[#D35400] flex items-center justify-center transition-all"
                  title="Scroll columns right"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filter..." className="pl-8 pr-3 py-1.5 border border-slate-200 rounded-lg text-sm w-44" />
              </div>
              <button
                data-testid="review-names-btn"
                onClick={() => setReviewOpen(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-white text-[#1E40AF] ring-1 ring-[#1E40AF]/30 rounded-lg text-xs font-semibold hover:bg-[#1E40AF] hover:text-white transition-colors"
                title={`Step through every ${compLabel} row one-by-one — bulk-rename + apply smart suggestions (e.g. "WITH" → "WITH CLIPS" for Dektite retrofits).`}
              ><FilePen size={13} /> Review names</button>
              <button data-testid="add-rate-entry" onClick={() => { setAddMode(true); setNewEntry({ name: '' }); }} className="flex items-center gap-1 px-3 py-1.5 bg-[#D35400] text-white rounded-lg text-xs font-semibold hover:bg-[#A04000]"><Plus size={13} /> Add</button>
              {/* Iter147 — "Add row · Copy from last" prefills the new row
                  with cascade chips + supplier + descriptions from the
                  bottom row of the current filtered list. Name + rate-$
                  fields stay blank — operator only fills the unique
                  bits, saving 5× the clicks when seeding many similar
                  rows (e.g. 20 Maxam Sheet rows all linked to Iron). */}
              <button
                data-testid="add-rate-entry-copy"
                onClick={() => {
                  const last = filtered[filtered.length - 1];
                  if (!last) {
                    setAddMode(true);
                    setNewEntry({ name: '' });
                    return;
                  }
                  setNewEntry(prefillFromRow(last));
                  setAddMode(true);
                  toast.success(`Copied cascade + supplier from "${last.name || 'last row'}". Enter the new row's Name + rates.`);
                }}
                disabled={!filtered.length}
                title={filtered.length
                  ? `Open the Add panel pre-filled with cascade chips + supplier + descriptions from "${filtered[filtered.length - 1]?.name || 'last row'}". Name + rate-$ fields stay blank for you to enter.`
                  : 'Add at least one row first, then this button can copy from it.'}
                className="flex items-center gap-1 px-3 py-1.5 bg-white text-[#D35400] ring-1 ring-[#D35400] rounded-lg text-xs font-semibold hover:bg-orange-50 disabled:opacity-40 disabled:cursor-not-allowed"
              ><Copy size={13} /> Add (copy from last)</button>
              {/* Iter276-audit — "Add (copy from row…)" — opens a picker
                  so the operator can duplicate ANY row in the current
                  component (e.g. 50mm Plain VORTEX → 50mm Coloured
                  VORTEX). Same prefill helper as "copy from last". */}
              <button
                data-testid="add-rate-entry-copy-picker"
                onClick={() => { setCopyPickerQuery(''); setCopyPickerOpen(true); }}
                disabled={!filtered.length}
                title={filtered.length
                  ? `Pick any existing row as the template for a new one — same codes / descriptions / cascade chips, different name + price.`
                  : 'Add at least one row first, then this button can copy from it.'}
                className="flex items-center gap-1 px-3 py-1.5 bg-white text-[#1E40AF] ring-1 ring-[#1E40AF] rounded-lg text-xs font-semibold hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed"
              ><Copy size={13} /> Add (copy from row…)</button>
            </div>
          </div>

          {/* Material-type sub-filter (Flashings only). Lets the user drill
              into the 1,000+ legacy Flashing rows by their leading material
              token (Alu / Galv / Maxam / Zinc / …).
              SUPERSEDED by the structured Material / Width / Folds pill
              bar below — kept disabled for now in case the operator
              needs to fall back. */}
          {isFlashing && false && materialList.length > 1 && (
            <div data-testid="flashing-material-filter" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2 flex-wrap flex-shrink-0">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1">Material</span>
              <button
                data-testid="material-filter-all"
                onClick={() => setMaterialFilter('all')}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${materialFilter === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
              >
                All <span className="opacity-70 ml-0.5">({entries.length})</span>
              </button>
              {materialList.map(({ key, count }) => (
                <button
                  key={key}
                  data-testid={`material-filter-${key}`}
                  onClick={() => setMaterialFilter(key)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${materialFilter === key ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                >
                  {key} <span className="opacity-70 ml-0.5">({count})</span>
                </button>
              ))}
            </div>
          )}

          {/* Aquaseal variant filter — splits the 19 Dektite rows into
              Std vs Retrofit so the operator can review either bucket
              quickly. The same parser flag drives Pipe Usages' Std /
              Retrofit dropdowns so what's tagged here flows through. */}
          {isAquaseal && (
            <div data-testid="aquaseal-variant-filter" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center gap-2 flex-wrap flex-shrink-0">
              <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1">Variant</span>
              {[
                { k: 'all', label: 'All', count: entries.length },
                { k: 'Std', label: 'Std (over-the-top)', count: variantBuckets['Std'] || 0 },
                { k: 'Retrofit', label: 'Retrofit (cut + clip)', count: variantBuckets['Retrofit'] || 0 },
                { k: 'Lead', label: 'Lead (tile roofs)', count: variantBuckets['Lead'] || 0 },
              ].map(({ k, label, count }) => (
                <button
                  key={k}
                  data-testid={`variant-filter-${k}`}
                  onClick={() => setVariantFilter(k)}
                  className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${variantFilter === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                >
                  {label} <span className="opacity-70 ml-0.5">({count})</span>
                </button>
              ))}
              <span className="ml-auto text-[10px] text-slate-500 italic">
                Std / Retrofit = EPDM Dektites for <strong>metal roofs</strong>. Lead = malleable lead-sheet Dektites for <strong>concrete &amp; clay tile roofs</strong>. Variant auto-detects from the row name; click a chip to pin it.
              </span>
            </div>
          )}

          {/* Sheet structured sub-filters — Profile / Material / Gauge.
              Each pill set is independent so the operator can drill
              from "all 50 sheets" → "Corrugate · Galv · 0.40BMT" in
              three clicks. Buckets are sorted by row-count desc so
              the busiest combos sit first. */}
          {isSheet && (
            <div data-testid="sheet-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {[
                { label: 'Profile', buckets: sheetBuckets.profile, value: sheetProfileFilter, setter: setSheetProfileFilter, testid: 'sheet-profile-filter', cascadeField: 'profile' },
                { label: 'Material', buckets: sheetBuckets.material, value: sheetMaterialFilter, setter: setSheetMaterialFilter, testid: 'sheet-material-filter', cascadeField: 'material' },
                { label: 'Gauge', buckets: sheetBuckets.gauge, value: sheetGaugeFilter, setter: setSheetGaugeFilter, testid: 'sheet-gauge-filter', cascadeField: 'gauge' },
              ].map(({ label, buckets, value, setter, testid, cascadeField }) => {
                const merged = cascadeField ? mergeWithCanonical(buckets, 'sheet', cascadeField) : buckets;
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={testid}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{label}</span>
                    <button
                      data-testid={`${testid}-all`}
                      onClick={() => setter('all')}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                    >
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill(null, label, cascadeField, buckets, value, setter, testid)}
                    {sorted.map(([k, count]) => (
                      <button
                        key={k}
                        data-testid={`${testid}-${k}`}
                        onClick={() => setter(k)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                      >
                        {k} <span className="opacity-70 ml-0.5">({count})</span>
                      </button>
                    ))}
                    {cascadeField && (
                      <ManageCascadePill component="sheet" field={cascadeField} label={label} onOpen={setCascadeEditor} />
                    )}
                  </div>
                );
              })}
              <span className="text-[10px] text-slate-500 italic">
                Structured fields auto-parsed from each row name (e.g. <code>Corrugate Galv /mtr (0.40Gauge 762mm)</code>). Click <strong>Edit</strong> on a row to pin a Profile / Material / Rate / Gauge / Coverage override when the parser misses, or use <strong>Review names</strong> to crystallise the parsed values across the catalogue.
              </span>
            </div>
          )}

          {/* Datalist sources for the Sheet structured-column dropdowns.
              Operator can pick from a canonical list OR type a custom
              value (HTML5 datalist combobox behaviour). */}
          {isSheet && (
            <>
              <datalist id="sheet-profile-options">
                {sheetVocab.profiles.map((p) => <option key={p} value={p} />)}
              </datalist>
              <datalist id="sheet-material-options">
                {sheetVocab.materials.map((m) => <option key={m} value={m} />)}
              </datalist>
              <datalist id="sheet-rate-type-options">
                {sheetVocab.rate_types.map((r) => <option key={r} value={r} />)}
              </datalist>
            </>
          )}

          {/* Ridge structured sub-filters — Class (iron/tile/mortar) and
              Material. Iron ridges (metal roofs) break down by material
              token in the row name; tile/mortar ridges break down by
              era / style ("Modern" / "Old" / "Clay" / "Tudor" / "Steep" /
              "Conc"). Filtering by Class first then Material drills
              from "all 20" → "iron · Galv" in two clicks. */}
          {isRidge && (
            <div data-testid="ridge-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {[
                { label: 'Class', buckets: ridgeBuckets.class, value: ridgeClassFilter, setter: setRidgeClassFilter, testid: 'ridge-class-filter', cascadeField: 'class' },
                { label: 'Material', buckets: ridgeBuckets.material, value: ridgeMaterialFilter, setter: setRidgeMaterialFilter, testid: 'ridge-material-filter', cascadeField: 'material' },
              ].map(({ label, buckets, value, setter, testid, cascadeField }) => {
                const merged = cascadeField ? mergeWithCanonical(buckets, 'ridge', cascadeField) : buckets;
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={testid}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{label}</span>
                    <button
                      data-testid={`${testid}-all`}
                      onClick={() => setter('all')}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                    >
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill(null, label, cascadeField, buckets, value, setter, testid)}
                    {sorted.map(([k, count]) => (
                      <button
                        key={k}
                        data-testid={`${testid}-${k}`}
                        onClick={() => setter(k)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                      >
                        {k} <span className="opacity-70 ml-0.5">({count})</span>
                      </button>
                    ))}
                    {cascadeField && (
                      <ManageCascadePill component="ridge" field={cascadeField} label={label} onOpen={setCascadeEditor} />
                    )}
                  </div>
                );
              })}
              <span className="text-[10px] text-slate-500 italic">
                <strong>Iron</strong> = press-formed metal ridges for metal roofs (broken down by material: Galv, Maxam, Zinc). <strong>Tile</strong> = supply rows for concrete &amp; clay tile ridges (Concrete / Clay). <strong>Mortar</strong> = Bed &amp; Point / Point labour items.
              </span>
            </div>
          )}

          {/* Datalist sources for Ridge structured-column dropdowns. */}
          {isRidge && (
            <>
              <datalist id="ridge-class-options">
                {['iron', 'tile', 'mortar'].map((c) => <option key={c} value={c} />)}
              </datalist>
              <datalist id="ridge-material-options">
                {ridgeVocab.materials.map((m) => <option key={m} value={m} />)}
              </datalist>
              <datalist id="ridge-width-options">
                {['Standard', 'Wide'].map((w) => <option key={w} value={w} />)}
              </datalist>
              <datalist id="ridge-work-type-options">
                {['Bed & Point', 'Point', 'Replace', 'Supply'].map((w) => <option key={w} value={w} />)}
              </datalist>
            </>
          )}

          {/* Valley structured sub-filters — Work Type (New / Replace)
              and Material (Galv / Maxam / Zinc). Same pill
              pattern as Sheet/Ridge. */}
          {isValley && (
            <div data-testid="valley-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {[
                { label: 'Class', buckets: valleyBuckets.class, value: valleyClassFilter, setter: setValleyClassFilter, testid: 'valley-class-filter', cascadeField: 'class' },
                { label: 'Work', buckets: valleyBuckets.work, value: valleyWorkFilter, setter: setValleyWorkFilter, testid: 'valley-work-filter', cascadeField: 'work_type' },
                { label: 'Material', buckets: valleyBuckets.material, value: valleyMaterialFilter, setter: setValleyMaterialFilter, testid: 'valley-material-filter', cascadeField: 'material' },
                { label: 'Profile', buckets: valleyBuckets.profile, value: valleyProfileFilter, setter: setValleyProfileFilter, testid: 'valley-profile-filter', cascadeField: 'profile' },
              ].map(({ label, buckets, value, setter, testid, cascadeField }) => {
                const merged = cascadeField ? mergeWithCanonical(buckets, 'valley', cascadeField) : buckets;
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={testid}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{label}</span>
                    <button
                      data-testid={`${testid}-all`}
                      onClick={() => setter('all')}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                    >
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill(null, label, cascadeField, buckets, value, setter, testid)}
                    {sorted.map(([k, count]) => (
                      <button
                        key={k}
                        data-testid={`${testid}-${k}`}
                        onClick={() => setter(k)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                      >
                        {k} <span className="opacity-70 ml-0.5">({count})</span>
                      </button>
                    ))}
                    {cascadeField && (
                      <ManageCascadePill component="valley" field={cascadeField} label={label} onOpen={setCascadeEditor} />
                    )}
                  </div>
                );
              })}
              <span className="text-[10px] text-slate-500 italic">
                <strong>New</strong> = new-install valley flashings supplied + fitted (width = flashing girth in mm; folds = number of pressbrake folds). <strong>Replace</strong> = labour-only swap of an existing valley.
              </span>
            </div>
          )}

          {/* Datalist sources for Valley structured-column dropdowns. */}
          {isValley && (
            <>
              <datalist id="valley-work-options">
                {['New', 'Replace'].map((w) => <option key={w} value={w} />)}
              </datalist>
              <datalist id="valley-material-options">
                {valleyVocab.materials.map((m) => <option key={m} value={m} />)}
              </datalist>
            </>
          )}

          {/* Re-Coat structured sub-filter — single Recoat Type pill
              row (Chipped / New Concrete / Old Concrete / Iron /
              Concrete). Same pill pattern as the other components so
              the operator can drill to a single substrate family in
              one click. */}
          {isRecoat && (
            <div
              data-testid="recoat-deprecation-banner"
              className="mx-5 mt-3 mb-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 flex items-start gap-2 flex-shrink-0"
              role="status"
            >
              <span aria-hidden="true">ⓘ</span>
              <div className="flex-1">
                <strong className="font-semibold">This table is the source of truth for Recoat prices.</strong>
                {' '}The Recoat builder charges <strong>Materials $ + Labour $ (RTR rate)</strong> from these rows — roof systems per m²,
                extras (garage doors, gutters, fascia, fences) per each / m. Coat recipes live in{' '}
                <a href="/admin/paint-systems" className="underline font-semibold text-amber-950 hover:text-amber-800">Paint Systems Admin</a>
                {' '}and are scaled to match these rates.
              </div>
              <RecoatCleanupButton api={api} onApplied={() => { loadEntries(); loadCounts(); }} />
            </div>
          )}
          {isRecoat && (
            <div data-testid="recoat-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {(() => {
                const merged = mergeWithCanonical(recoatBuckets, 'recoat', 'recoat_type');
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div className="flex items-center gap-2 flex-wrap" data-testid="recoat-type-filter">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">Type</span>
                    <button
                      data-testid="recoat-type-filter-all"
                      onClick={() => setRecoatTypeFilter('all')}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${recoatTypeFilter === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                    >
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill('recoat', 'Type', 'recoat_type', recoatBuckets, recoatTypeFilter, setRecoatTypeFilter, 'recoat-type-filter')}
                    {sorted.map(([k, n]) => (
                      <button
                        key={k}
                        data-testid={`recoat-type-filter-${k}`}
                        onClick={() => setRecoatTypeFilter(k)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${recoatTypeFilter === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                      >
                        {k} <span className="opacity-70 ml-0.5">({n})</span>
                      </button>
                    ))}
                    <ManageCascadePill component="recoat" field="recoat_type" label="Recoat Type" onOpen={setCascadeEditor} />
                  </div>
                );
              })()}
            </div>
          )}

          {/* Cleaning structured sub-filter — Treatment / Wash. Single
              pill row + Manage so the operator can add a 3rd
              bucket (e.g. "Spray Treatment") without code changes. */}
          {isCleaning && (
            <div data-testid="cleaning-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {(() => {
                const merged = mergeWithCanonical(cleaningBuckets, 'cleaning', 'clean_type');
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div className="flex items-center gap-2 flex-wrap" data-testid="cleaning-clean-type-filter">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">Type</span>
                    <button
                      data-testid="cleaning-clean-type-filter-all"
                      onClick={() => setCleaningTypeFilter('all')}
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${cleaningTypeFilter === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                    >
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill('cleaning', 'Type', 'clean_type', cleaningBuckets, cleaningTypeFilter, setCleaningTypeFilter, 'cleaning-clean-type-filter')}
                    {sorted.map(([k, n]) => (
                      <button
                        key={k}
                        data-testid={`cleaning-clean-type-filter-${k}`}
                        onClick={() => setCleaningTypeFilter(k)}
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${cleaningTypeFilter === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}
                      >
                        {k} <span className="opacity-70 ml-0.5">({n})</span>
                      </button>
                    ))}
                    <ManageCascadePill component="cleaning" field="clean_type" label="Clean Type" onOpen={setCascadeEditor} />
                  </div>
                );
              })()}
            </div>
          )}

          {/* Tile structured filter — Class / Material / Profile / Work
              pill rows. Class & Profile cascades mirror Valley/Barge —
              auto-detect operator-added canonical values in row names. */}
          {isTile && (
            <div data-testid="tile-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {[
                { label: 'Class', buckets: tileBuckets.class, value: tileClassFilter, setter: setTileClassFilter, testid: 'tile-class-filter', cascadeField: 'class' },
                { label: 'Material', buckets: tileBuckets.type, value: tileTypeFilter, setter: setTileTypeFilter, testid: 'tile-material-filter', cascadeField: 'tile_type' },
                { label: 'Profile', buckets: tileBuckets.profile, value: tileProfileFilter, setter: setTileProfileFilter, testid: 'tile-profile-filter', cascadeField: 'profile' },
                { label: 'Work', buckets: tileBuckets.work, value: tileWorkFilter, setter: setTileWorkFilter, testid: 'tile-work-filter', cascadeField: 'work_type' },
              ].map(({ label, buckets, value, setter, testid, cascadeField }) => {
                const merged = cascadeField ? mergeWithCanonical(buckets, 'tile', cascadeField) : buckets;
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={testid}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{label}</span>
                    <button onClick={() => setter('all')} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill('tile', label, cascadeField, buckets, value, setter, testid)}
                    {sorted.map(([k, n]) => (
                      <button key={k} onClick={() => setter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                        {k} <span className="opacity-70 ml-0.5">({n})</span>
                      </button>
                    ))}
                    {cascadeField && <ManageCascadePill component="tile" field={cascadeField} label={label} onOpen={setCascadeEditor} />}
                  </div>
                );
              })}
              <span className="text-[10px] text-slate-500 italic">
                <strong>Class</strong> = roof family (concrete / clay / membrane). <strong>Material</strong> = tile family (Clay / Concrete / Decramastic / Colortile / Modern / Old / 2nds). <strong>Profile</strong> = operator-extendable canonical list. <strong>Lay</strong>=install labour, <strong>Replace</strong>=swap broken, <strong>Supply</strong>=material only, <strong>PanelBeat</strong>=decramastic dent repair.
              </span>
            </div>
          )}

          {/* Safety / Misc / Urgent / Travel — single-field structured
              filters. Each is a tiny catalogue (4-34 rows). */}
          {(isSafety || isMisc || isUrgent || isTravel) && (() => {
            const spec = isSafety ? { component: 'safety', field: 'kind', label: 'Kind', buckets: safetyBuckets, value: safetyKindFilter, setter: setSafetyKindFilter, testid: 'safety-kind-filter', blurb: '6 standard categories — Edge Protection, Harness, Height Safety, Power Sleaving, Safety Set Up, Scaffold. Add more via Manage.' }
              : isMisc ? { component: 'misc', field: 'category', label: 'Category', buckets: miscBuckets, value: miscCategoryFilter, setter: setMiscCategoryFilter, testid: 'misc-category-filter', blurb: 'Spinaway (ventilation flues) · Tradesman/Labourer (hourly) · Strip (roof tear-off rates) · Rubbish · Adjustment · Materials · Other.' }
              : isUrgent ? { component: 'urgent', field: 'urgency_type', label: 'Type', buckets: urgentBuckets, value: urgentTypeFilter, setter: setUrgentTypeFilter, testid: 'urgent-type-filter', blurb: 'Appraisal · Remedial · Small Job · Call Out — used for emergency / urgent quote line items.' }
              : { component: 'travel', field: 'trade', label: 'Trade', buckets: travelBuckets, value: travelTradeFilter, setter: setTravelTradeFilter, testid: 'travel-trade-filter', blurb: 'Contractor · Painter · Iron · Concrete · Local · Mileage. Used to cost travel/transport per trade.' };
            const merged = mergeWithCanonical(spec.buckets, spec.component, spec.field);
            const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
            return (
              <div data-testid={`${spec.component}-structured-filters`} className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
                <div className="flex items-center gap-2 flex-wrap" data-testid={spec.testid}>
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{spec.label}</span>
                  <button onClick={() => spec.setter('all')} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${spec.value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                    All <span className="opacity-70 ml-0.5">({entries.length})</span>
                  </button>
                  {renderAllLabelPill(spec.component, spec.label, spec.field, spec.buckets, spec.value, spec.setter, spec.testid)}
                  {sorted.map(([k, n]) => (
                    <button key={k} onClick={() => spec.setter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${spec.value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                      {k} <span className="opacity-70 ml-0.5">({n})</span>
                    </button>
                  ))}
                  <ManageCascadePill component={spec.component} field={spec.field} label={spec.label} onOpen={setCascadeEditor} />
                </div>
                <span className="text-[10px] text-slate-500 italic">{spec.blurb}</span>
              </div>
            );
          })()}

          {/* Flashing structured sub-filters — Material / Width / Folds.
              Big catalogue (1175 rows) so all three pill rows show.
              Width pill row only renders up to the 10 most common
              widths to keep the bar readable; same for Folds. */}
          {isFlashing && (
            <div data-testid="flashing-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {[
                { label: 'Material', buckets: flashingBuckets.material, value: flashingMaterialFilter, setter: setFlashingMaterialFilter, testid: 'flashing-material-pill', cap: 99, cascadeField: 'material' },
                { label: 'Width', buckets: flashingBuckets.width, value: flashingWidthFilter, setter: setFlashingWidthFilter, testid: 'flashing-width-pill', cap: 12 },
                { label: 'Folds', buckets: flashingBuckets.folds, value: flashingFoldsFilter, setter: setFlashingFoldsFilter, testid: 'flashing-folds-pill', cap: 12 },
              ].map(({ label, buckets, value, setter, testid, cap, cascadeField }) => {
                const merged = cascadeField ? mergeWithCanonical(buckets, 'flashing', cascadeField) : buckets;
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' })).slice(0, cap);
                return (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={testid}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{label}</span>
                    <button onClick={() => setter('all')} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill(null, label, cascadeField, buckets, value, setter, testid)}
                    {sorted.map(([k, count]) => (
                      <button key={k} onClick={() => setter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                        {label === 'Width' ? `${k}mm` : k} <span className="opacity-70 ml-0.5">({count})</span>
                      </button>
                    ))}
                    {cascadeField && (
                      <ManageCascadePill component="flashing" field={cascadeField} label={label} onOpen={setCascadeEditor} />
                    )}
                  </div>
                );
              })}
              <span className="text-[10px] text-slate-500 italic">
                Naming convention <code>{`<Material> <Width>W/<Folds>F`}</code> (e.g. <code>Galv 1000W/1F</code>) parses straight into the columns below. Click <strong>Edit</strong> on a row to pin an override; structured fields cascade by Material so a Galv flashing picks up Galv ridge + Galv valley rates.
              </span>
            </div>
          )}

          {/* Spouting structured sub-filters — Kind / Profile / Material.
              Mixed catalogue (gutters / brackets / downpipes / clips /
              accessories / PVC) so Kind drills coarse, Profile + Material
              refine. */}
          {isSpouting && (
            <div data-testid="spouting-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {[
                { label: 'Kind', buckets: spoutingBuckets.kind, value: spoutingKindFilter, setter: setSpoutingKindFilter, testid: 'spouting-kind-pill', cascadeField: 'kind' },
                { label: 'Profile', buckets: spoutingBuckets.profile, value: spoutingProfileFilter, setter: setSpoutingProfileFilter, testid: 'spouting-profile-pill', cascadeField: 'profile' },
                { label: 'Material', buckets: spoutingBuckets.material, value: spoutingMaterialFilter, setter: setSpoutingMaterialFilter, testid: 'spouting-material-pill', cascadeField: 'material' },
              ].map(({ label, buckets, value, setter, testid, cascadeField }) => {
                const merged = cascadeField ? mergeWithCanonical(buckets, 'spouting', cascadeField) : buckets;
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={testid}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{label}</span>
                    <button onClick={() => setter('all')} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill(null, label, cascadeField, buckets, value, setter, testid)}
                    {sorted.map(([k, count]) => (
                      <button key={k} onClick={() => setter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                        {k} <span className="opacity-70 ml-0.5">({count})</span>
                      </button>
                    ))}
                    {cascadeField && (
                      <ManageCascadePill component="spouting" field={cascadeField} label={label} onOpen={setCascadeEditor} />
                    )}
                  </div>
                );
              })}
              <span className="text-[10px] text-slate-500 italic">
                <strong>Gutter</strong> rows parse Profile (Quarter Round / Square / Half Round / Internal Boxed) · Width (mm) · Thickness (mm) · Material (Galv / Zincalume / ZinaCore / Copper / MagnaFlow / PVC). Brackets / downpipes / clips still tag the Kind chip even when other fields stay blank.
              </span>
            </div>
          )}

          {/* Datalist sources for Flashing / Spouting / Underlay /
              Fastening structured-column dropdowns are no longer
              needed — those edits use strict <select> with the
              canonical+legacy union pattern (see edit-row IIFE). */}

          {/* Underlay structured sub-filters — Brand / Variant. Both
              filters are typically only 2-4 options each (the catalogue
              is small) so the pill row stays compact. */}
          {isUnderlayStruct && (
            <div data-testid="underlay-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {[
                { label: 'Brand', buckets: underlayBuckets.brand, value: underlayBrandFilter, setter: setUnderlayBrandFilter, testid: 'underlay-brand-pill', cascadeField: 'brand' },
                { label: 'Variant', buckets: underlayBuckets.variant, value: underlayVariantFilter, setter: setUnderlayVariantFilter, testid: 'underlay-variant-pill', cascadeField: 'variant' },
              ].map(({ label, buckets, value, setter, testid, cascadeField }) => {
                const merged = cascadeField ? mergeWithCanonical(buckets, 'underlay', cascadeField) : buckets;
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={testid}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{label}</span>
                    <button onClick={() => setter('all')} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill(null, label, cascadeField, buckets, value, setter, testid)}
                    {sorted.map(([k, count]) => (
                      <button key={k} onClick={() => setter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                        {k} <span className="opacity-70 ml-0.5">({count})</span>
                      </button>
                    ))}
                    {cascadeField && (
                      <ManageCascadePill component="underlay" field={cascadeField} label={label} onOpen={setCascadeEditor} />
                    )}
                  </div>
                );
              })}
              <span className="text-[10px] text-slate-500 italic">
                Naming convention <code>{`<Brand> <Grade> <Variant> [<Coverage>m²] [(<Width>mm x <Length>m)]`}</code>. <strong>SS</strong> = Self Support (no battens needed); <strong>HW</strong> = Heavy Weight (laid over battens). Grade is the bitumen-impregnation code (215 / 403 / 407).
              </span>
            </div>
          )}

          {/* Underlay edit dropdowns now use strict <select> with the
              canonical+legacy union pattern — old <datalist> removed. */}

          {/* Barge structured sub-filters — Class / Material / Profile.
              Mixed catalogue (iron press-formed + tile supply + mortar
              labour + concrete + membrane). Class drills the coarse
              partition; Material then narrows within iron, tile etc. */}
          {isBargeStruct && (
            <div data-testid="barge-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {[
                { label: 'Class', buckets: bargeBuckets.class, value: bargeClassFilter, setter: setBargeClassFilter, testid: 'barge-class-pill', cascadeField: 'class' },
                { label: 'Material', buckets: bargeBuckets.material, value: bargeMaterialFilter, setter: setBargeMaterialFilter, testid: 'barge-material-pill', cascadeField: 'material' },
                { label: 'Profile', buckets: bargeBuckets.profile, value: bargeProfileFilter, setter: setBargeProfileFilter, testid: 'barge-profile-pill', cascadeField: 'profile' },
              ].map(({ label, buckets, value, setter, testid, cascadeField }) => {
                const merged = cascadeField ? mergeWithCanonical(buckets, 'barge', cascadeField) : buckets;
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={testid}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{label}</span>
                    <button onClick={() => setter('all')} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill(null, label, cascadeField, buckets, value, setter, testid)}
                    {sorted.map(([k, count]) => (
                      <button key={k} onClick={() => setter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                        {k} <span className="opacity-70 ml-0.5">({count})</span>
                      </button>
                    ))}
                    {cascadeField && (
                      <ManageCascadePill component="barge" field={cascadeField} label={label} onOpen={setCascadeEditor} />
                    )}
                  </div>
                );
              })}
              <span className="text-[10px] text-slate-500 italic">
                <strong>Iron</strong> = press-formed metal barges (Corru / Ribline / Trimrib × Galv / Maxam / Zinc / Alu). <strong>Tile</strong> = clay / concrete / Decramastic / Monier supply rows. <strong>Mortar</strong> = Bed &amp; Point / Point labour. <strong>Concrete</strong> = "Concrete Barging New". <strong>Butynol</strong> = Butynol barging.
              </span>
            </div>
          )}

          {/* Barge edit dropdowns now use strict <select> tied to
              `bargeVocab` (loaded from /cascade-values/barge/*). The
              old <datalist> sources are intentionally removed — see
              the recurring `<input list>` filter bug fix. */}

          {/* Fastening structured sub-filters — Work / Rate / Variant.
              Variant captures sub-category tokens (ColourTile / Timbertek
              / Multidek / AR8 / steep) so the operator can scope by
              installation context. */}
          {isFastening && (
            <div data-testid="fastening-structured-filters" className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 flex flex-col gap-2 flex-shrink-0">
              {[
                { label: 'Work', buckets: fastBuckets.work, value: fastWorkFilter, setter: setFastWorkFilter, testid: 'fastening-work-pill', cascadeField: 'work_type' },
                { label: 'Rate', buckets: fastBuckets.rate, value: fastRateFilter, setter: setFastRateFilter, testid: 'fastening-rate-pill', cascadeField: 'rate_type' },
                { label: 'Variant', buckets: fastBuckets.variant, value: fastVariantFilter, setter: setFastVariantFilter, testid: 'fastening-variant-pill', cascadeField: 'variant' },
              ].map(({ label, buckets, value, setter, testid, cascadeField }) => {
                const merged = cascadeField ? mergeWithCanonical(buckets, 'fastening', cascadeField) : buckets;
                const sorted = Object.entries(merged).sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
                return (
                  <div key={label} className="flex items-center gap-2 flex-wrap" data-testid={testid}>
                    <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mr-1 w-16">{label}</span>
                    <button onClick={() => setter('all')} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === 'all' ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                      All <span className="opacity-70 ml-0.5">({entries.length})</span>
                    </button>
                    {renderAllLabelPill(null, label, cascadeField, buckets, value, setter, testid)}
                    {sorted.map(([k, count]) => (
                      <button key={k} onClick={() => setter(k)} className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-all ${value === k ? 'bg-[#D35400] text-white border-[#D35400]' : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'}`}>
                        {k} <span className="opacity-70 ml-0.5">({count})</span>
                      </button>
                    ))}
                    {cascadeField && (
                      <ManageCascadePill component="fastening" field={cascadeField} label={label} onOpen={setCascadeEditor} />
                    )}
                  </div>
                );
              })}
              <span className="text-[10px] text-slate-500 italic">
                Naming convention <code>{`<Work> [Variant] (<Gauge>*<Length>)/<Rate>`}</code> — e.g. <code>Rescrew (12*50)/m²</code> → Work=Rescrew, Gauge=12, Length=50mm, Rate=/m². Variant tokens (ColourTile / Timbertek / Multidek / AR8 / steep) sub-categorise installation context.
              </span>
            </div>
          )}

          {/* Fastening edit dropdowns now use strict <select> with the
              canonical+legacy union pattern — old <datalist> removed. */}

          {/* Datalist sources for text-field combobox columns (currently
              just `labour_code` / Lab-Mat). The list is built from every
              distinct value already present in the current component —
              picking an existing code lets the operator group rows under
              the same cascade key (e.g. "Iron") with one keystroke. */}
          {TEXT_FIELDS.filter(t => t.useDatalist && !t.selectOnly).map(t => (
            <datalist key={t.k} id={`text-field-options-${t.k}`}>
              {(distinctByField[t.k] || []).map(v => <option key={v} value={v} />)}
            </datalist>
          ))}

          {/* Top horizontal scrollbar — mirrors the table's own
              (bottom) scrollbar, so the operator can scroll columns
              left/right without first paging down to find the
              bottom scrollbar. Synced two-ways via
              `handleTopScroll` / `handleBottomScroll`. Renders on
              every component page because every page shares this
              panel + table shell. */}
          <div
            ref={topScrollRef}
            data-testid="table-top-scrollbar"
            onScroll={handleTopScroll}
            className="overflow-x-auto overflow-y-hidden mx-4 mt-3 border border-slate-200 rounded bg-slate-50"
            style={{ height: 14 }}
          >
            <div style={{ width: tableScrollWidth || 1, height: 1 }} />
          </div>

          {/* Table — horizontal scroll when columns exceed viewport width
              (Barges, Sheets, Spouting, Underlay, etc. all have wide
              structured-column layouts). Vertical scroll keeps the
              <thead> in view via `sticky top-0` so the operator
              never loses the NAME / CLASS / MATERIAL labels while
              paging through long catalogues. Applies to every
              component because every component renders the same
              <table> shell below. */}
          <div ref={tableScrollRef} onScroll={handleBottomScroll} className="flex-1 overflow-auto p-4" style={{ maxHeight: '70vh' }}>
            {copyPickerOpen && (
              <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={() => setCopyPickerOpen(false)} data-testid="copy-from-row-picker">
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                  <div className="px-4 py-3 border-b flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-bold text-slate-800">Copy from row · {compLabel}</h3>
                      <p className="text-[11px] text-slate-500">Pick any row to seed a new entry with the same cascade chips, supplier, codes & descriptions — Name + prices stay blank for you to fill.</p>
                    </div>
                    <button onClick={() => setCopyPickerOpen(false)} className="text-slate-400 hover:text-red-500" data-testid="copy-picker-close"><X size={16} /></button>
                  </div>
                  <div className="px-4 py-2 border-b bg-slate-50">
                    <input
                      type="text"
                      autoFocus
                      value={copyPickerQuery}
                      onChange={(e) => setCopyPickerQuery(e.target.value)}
                      placeholder={`Search ${filtered.length} rows by name, code or supplier…`}
                      data-testid="copy-picker-search"
                      className="w-full text-sm border border-slate-300 rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400"
                    />
                  </div>
                  <div className="flex-1 overflow-auto">
                    {(() => {
                      const q = copyPickerQuery.trim().toLowerCase();
                      const rows = filtered.filter(r => {
                        if (!q) return true;
                        return (
                          (r.name || '').toLowerCase().includes(q) ||
                          (r.supplier_code || '').toLowerCase().includes(q) ||
                          (r.supplier || '').toLowerCase().includes(q)
                        );
                      });
                      if (!rows.length) return (
                        <div className="p-6 text-center text-slate-400 text-sm italic">No rows match your search.</div>
                      );
                      return (
                        <ul className="divide-y divide-slate-100">
                          {rows.map((r) => (
                            <li key={r._id}>
                              <button
                                onClick={() => {
                                  setNewEntry(prefillFromRow(r));
                                  setAddMode(true);
                                  setCopyPickerOpen(false);
                                  toast.success(`Copied from "${r.name}" — enter new name + prices.`);
                                }}
                                data-testid={`copy-picker-row-${r._id}`}
                                className="w-full text-left px-4 py-2 hover:bg-blue-50 transition-colors flex items-center justify-between gap-4"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm text-slate-800 font-medium truncate">{r.name || '—'}</div>
                                  <div className="text-[11px] text-slate-500 truncate">
                                    {r.supplier ? <span className="mr-2">{r.supplier}</span> : null}
                                    {r.supplier_code ? <span className="font-mono">{r.supplier_code}</span> : null}
                                    {r.materials_desc ? <span className="ml-2 text-slate-400">· {r.materials_desc}</span> : null}
                                  </div>
                                </div>
                                <span className="text-[11px] text-blue-700 font-semibold flex-shrink-0"><Copy size={11} className="inline mr-1" />Use as template</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}
            {addMode && (
              <div className="mb-3 border-2 border-green-400 rounded-lg p-3 bg-green-50/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-green-800 uppercase">New {kind === 'type' ? 'Type' : 'Profile'}</span>
                  <button onClick={() => setAddMode(false)} className="text-slate-400 hover:text-red-500"><X size={14} /></button>
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="text-[10px] text-slate-500 font-semibold">Name</label>
                    <input value={newEntry.name || ''} onChange={e => setNewEntry({ ...newEntry, name: e.target.value })} placeholder="Enter type name..." className="w-full px-2 py-2 border border-slate-200 rounded text-sm" autoFocus />
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {RATE_FIELDS.map(f => (
                      <div key={f}>
                        <label className="text-[10px] text-slate-400">{RATE_LABELS[f]}</label>
                        <input type="number" step="0.01" value={newEntry[f] || ''} onChange={e => setNewEntry({ ...newEntry, [f]: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs" />
                      </div>
                    ))}
                    {extraFields.map(ef => (
                      <div key={ef}>
                        <label className="text-[10px] text-slate-400">{ef === 'sqm_per_roll' ? 'Sqm/Roll' : ef}</label>
                        <input type="number" step="0.01" value={newEntry[ef] || ''} onChange={e => setNewEntry({ ...newEntry, [ef]: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs" />
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {TEXT_FIELDS.map(t => (
                      <div key={t.k}>
                        <label className="text-[10px] text-slate-400">{t.l}</label>
                        {t.selectOnly ? (
                          <select
                            value={newEntry[t.k] || ''}
                            onChange={e => setNewEntry({ ...newEntry, [t.k]: e.target.value })}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-white"
                          >
                            <option value="">— select —</option>
                            {(distinctByField[t.k] || []).map(v => (
                              <option key={v} value={v}>{v}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            value={newEntry[t.k] || ''}
                            onChange={e => setNewEntry({ ...newEntry, [t.k]: e.target.value })}
                            list={t.useDatalist ? `text-field-options-${t.k}` : undefined}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <button onClick={addEntry} disabled={!newEntry.name?.trim()} className="px-4 py-1.5 bg-green-600 text-white rounded text-xs font-semibold hover:bg-green-700 disabled:opacity-40"><Check size={12} className="inline mr-1" />Add</button>
                </div>
              </div>
            )}

            {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> : filtered.length === 0 ? (
              <div className="text-center py-12 text-slate-400 text-sm">No {kind}s found{search ? ` matching "${search}"` : ''}</div>
            ) : (
              <>
                {/* Iter 108 — highlight-row visibility guard. If the
                    `?highlight={id}` URL param targets a row that
                    exists in `entries` but has been filtered out by
                    the current search / filter pills, scroll won't
                    work and the operator sees nothing. We surface a
                    banner with a "Clear filters & show row" affordance
                    so the deep-link always lands its target. */}
                {highlightRowId && (() => {
                  const target = entries.find(e => e._id === highlightRowId);
                  const inFiltered = !!target && filtered.some(e => e._id === highlightRowId);
                  if (!target) return null;
                  if (inFiltered) return null;
                  return (
                    <div data-testid="highlight-hidden-banner"
                         className="mb-3 flex items-center justify-between gap-3 bg-amber-50 border border-amber-300 rounded px-3 py-2 text-xs text-amber-900">
                      <span>
                        Highlighted row <strong className="font-semibold">{target.name}</strong> is hidden by the current filters or search.
                      </span>
                      <button type="button"
                              data-testid="highlight-clear-filters"
                              onClick={() => {
                                // Reset everything that could be
                                // hiding the row. We intentionally
                                // clear ALL filter pills (the same
                                // list used by `loadEntries` on
                                // component switch) so the banner
                                // always rescues the highlighted row.
                                setSearch('');
                                setMaterialFilter('all'); setVariantFilter('all');
                                setSheetProfileFilter('all'); setSheetMaterialFilter('all'); setSheetGaugeFilter('all');
                                setRidgeClassFilter('all'); setRidgeMaterialFilter('all');
                                setValleyWorkFilter('all'); setValleyMaterialFilter('all'); setValleyProfileFilter('all'); setValleyClassFilter('all');
                                setFlashingMaterialFilter('all'); setFlashingWidthFilter('all'); setFlashingFoldsFilter('all');
                                setSpoutingKindFilter('all'); setSpoutingProfileFilter('all'); setSpoutingMaterialFilter('all');
                                setUnderlayBrandFilter('all'); setUnderlayVariantFilter('all');
                                setBargeClassFilter('all'); setBargeMaterialFilter('all'); setBargeProfileFilter('all');
                                setFastWorkFilter('all'); setFastRateFilter('all'); setFastVariantFilter('all');
                                setRecoatTypeFilter('all'); setCleaningTypeFilter('all');
                                setTileWorkFilter('all'); setTileTypeFilter('all');
                                setSafetyKindFilter('all'); setMiscCategoryFilter('all'); setUrgentTypeFilter('all');
                                setTravelTradeFilter('all');
                              }}
                              className="px-2 py-1 text-[11px] font-bold text-amber-900 bg-amber-100 hover:bg-amber-200 border border-amber-400 rounded">
                        Clear filters &amp; show row
                      </button>
                    </div>
                  );
                })()}
              <table className="text-sm" style={{ minWidth: 'max-content' }}>
                <thead className="sticky top-0 z-20 bg-slate-50 shadow-[0_1px_0_0_rgba(0,0,0,0.06)]">
                  <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                    <th className="text-left px-3 py-2">Name</th>
                    {isAquaseal && <th className="text-left px-3 py-2 w-24">Variant</th>}
                    {isAquaseal && <th className="text-left px-3 py-2 w-28">Ø Range</th>}
                    {isSheet && <th className="text-left px-3 py-2 w-28">Profile</th>}
                    {isSheet && <th className="text-left px-3 py-2 w-24">Material</th>}
                    {isSheet && <th className="text-left px-3 py-2 w-28">Composition</th>}
                    {isSheet && <th className="text-left px-3 py-2 w-24">Gauge (mm)</th>}
                    {isSheet && <th className="text-right px-3 py-2 w-24">Cover (mm)</th>}
                    {isSheet && <th className="text-left px-3 py-2 w-20">Rate</th>}
                    {isRidge && <th className="text-left px-3 py-2 w-24">Material</th>}
                    {isRidge && <th className="text-left px-3 py-2 w-24">Width</th>}
                    {isRidge && <th className="text-right px-3 py-2 w-20">Len (mm)</th>}
                    {isValley && <th className="text-left px-3 py-2 w-24">Material</th>}
                    {isValley && <th className="text-left px-3 py-2 w-24">Profile</th>}
                    {isValley && <th className="text-right px-3 py-2 w-24">Width (mm)</th>}
                    {isValley && <th className="text-right px-3 py-2 w-16">Folds</th>}
                    {isFlashing && <th className="text-left px-3 py-2 w-24">Material</th>}
                    {isFlashing && <th className="text-right px-3 py-2 w-24">Width (mm)</th>}
                    {isFlashing && <th className="text-right px-3 py-2 w-16">Folds</th>}
                    {isSpouting && <th className="text-left px-3 py-2 w-24">Kind</th>}
                    {isSpouting && <th className="text-left px-3 py-2 w-28">Profile</th>}
                    {isSpouting && <th className="text-right px-3 py-2 w-20">Width</th>}
                    {isSpouting && <th className="text-right px-3 py-2 w-20">Thk (mm)</th>}
                    {isSpouting && <th className="text-left px-3 py-2 w-24">Material</th>}
                    {isUnderlayStruct && <th className="text-left px-3 py-2 w-24">Brand</th>}
                    {isUnderlayStruct && <th className="text-right px-3 py-2 w-16">Grade</th>}
                    {isUnderlayStruct && <th className="text-left px-3 py-2 w-28">Variant</th>}
                    {isUnderlayStruct && <th className="text-right px-3 py-2 w-20">Cov (m²)</th>}
                    {isUnderlayStruct && <th className="text-right px-3 py-2 w-20">Width (mm)</th>}
                    {isUnderlayStruct && <th className="text-right px-3 py-2 w-20">Length (m)</th>}
                    {isBargeStruct && <th className="text-left px-3 py-2 w-24">Material</th>}
                    {isBargeStruct && <th className="text-left px-3 py-2 w-20">Profile</th>}
                    {isBargeStruct && <th className="text-right px-3 py-2 w-20">Width (mm)</th>}
                    {isBargeStruct && <th className="text-right px-3 py-2 w-16">Folds</th>}
                    {isBargeStruct && <th className="text-right px-3 py-2 w-20">Thk (mm)</th>}
                    {isFastening && <th className="text-right px-3 py-2 w-16">Gauge</th>}
                    {isFastening && <th className="text-right px-3 py-2 w-20">Length (mm)</th>}
                    {isFastening && <th className="text-left px-3 py-2 w-16">Rate</th>}
                    {isFastening && <th className="text-left px-3 py-2 w-24">Variant</th>}
                    {isCleaning && <th className="text-left px-3 py-2 w-28">Clean Type</th>}
                    {isRecoat && <th className="text-left px-3 py-2 w-32">Recoat Type</th>}
                    {isTile && <th className="text-left px-3 py-2 w-28">Material</th>}
                    {isTile && <th className="text-left px-3 py-2 w-24">Profile</th>}
                    {isSafety && <th className="text-left px-3 py-2 w-36">Kind</th>}
                    {isMisc && <th className="text-left px-3 py-2 w-28">Category</th>}
                    {isUrgent && <th className="text-left px-3 py-2 w-32">Urgency Type</th>}
                    {isTravel && <th className="text-left px-3 py-2 w-28">Trade</th>}
                    <th className="text-right px-3 py-2">Buy $</th>
                    <th className="text-right px-3 py-2">Materials $</th>
                    <th className="text-right px-3 py-2">Labour $</th>
                    <th className="text-right px-3 py-2">Contractor $</th>
                    <th className="text-right px-3 py-2 bg-emerald-50 text-emerald-800" data-testid="rtr-rate-header" title="Materials $ + Labour $ — the client price">RTR Rate</th>
                    {/* Cascade-link columns + categorical (Class / Work)
                        columns relocated here in iter139 — the operator
                        asked for these to live AFTER the rate $ columns
                        so the workflow scans "name → numbers → tags"
                        instead of "name → tags → numbers". */}
                    <th className="text-left px-3 py-2 w-44 bg-orange-50/40">Roof Types</th>
                    <th className="text-left px-3 py-2 w-40 bg-orange-50/40">Brands</th>
                    <th className="text-left px-3 py-2 w-40 bg-orange-50/40">Profiles</th>
                    {isRidge && <th className="text-left px-3 py-2 w-20">Class</th>}
                    {isRidge && <th className="text-left px-3 py-2 w-24">Work Type</th>}
                    {isValley && <th className="text-left px-3 py-2 w-24">Class</th>}
                    {isValley && <th className="text-left px-3 py-2 w-24">Work</th>}
                    {isBargeStruct && <th className="text-left px-3 py-2 w-20">Class</th>}
                    {isBargeStruct && <th className="text-left px-3 py-2 w-24">Work</th>}
                    {isFastening && <th className="text-left px-3 py-2 w-20">Work</th>}
                    {isTile && <th className="text-left px-3 py-2 w-20">Class</th>}
                    {isTile && <th className="text-left px-3 py-2 w-24">Work</th>}
                    {TEXT_FIELDS.map(t => <th key={t.k} className="text-left px-3 py-2">{t.l}</th>)}
                    {extraFields.map(ef => <th key={ef} className="text-right px-3 py-2">{ef === 'sqm_per_roll' ? 'Sqm/Roll' : ef}</th>)}
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map(e => {
                    // For aquaseal: derive variant + Ø range from the
                    // parser fields the backend projects (`parsed`).
                    // Falls back to ('—') chips when the row name doesn't
                    // match the canonical pattern.
                    const parsed = e.parsed || {};
                    // Effective variant honours the operator-set
                    // override before falling back to parser. Shown as
                    // a colour-coded chip + a small ⚙ indicator when
                    // the value is operator-pinned (so the operator
                    // can spot rows that won't follow a future name
                    // change).
                    const isRetrofit = !!e.effective_is_retrofit;
                    const isLead = e.effective_material === 'lead';
                    const hasOverride = !!e.has_variant_override;
                    const hasMatOverride = !!e.has_material_override;
                    const variantChip = isAquaseal ? (
                      <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); cycleVariantOverride(e); }}
                        className="inline-flex items-center gap-1 group cursor-pointer"
                        title={isLead
                          ? `Lead-based aquaseal (concrete / clay tile roofs)${hasMatOverride ? ' — operator-pinned' : ' — auto-detected from name'}. Click to cycle Std → Retrofit → Lead → Auto.`
                          : hasOverride
                            ? `Pinned ${isRetrofit ? 'Retrofit' : 'Std'} by operator. Click to cycle through variants.`
                            : `Auto-detected ${isRetrofit ? 'Retrofit' : 'Std'} from name. Click to cycle Std → Retrofit → Lead → Auto.`}
                        data-testid={`aquaseal-variant-toggle-${e._id}`}
                      >
                        {isLead ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-zinc-200 text-zinc-800 ring-1 ring-zinc-300 group-hover:ring-zinc-500 transition">Lead</span>
                        ) : isRetrofit ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 ring-1 ring-amber-200 group-hover:ring-amber-400 transition">Retrofit</span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-800 ring-1 ring-sky-200 group-hover:ring-sky-400 transition">Std</span>
                        )}
                        {(hasOverride || hasMatOverride) && <span className="text-[9px] text-slate-400" title="Operator override — pinned, won't auto-update when the name changes">⚙</span>}
                      </button>
                    ) : null;
                    const rangeChip = isAquaseal ? (
                      (parsed.dia_min && parsed.dia_max) ? (
                        <span className="font-mono-num text-xs text-slate-700">{parsed.dia_min}–{parsed.dia_max} mm</span>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )
                    ) : null;
                    // Sheet structured columns — same projection
                    // pattern as Aquaseal. Each field shows the
                    // effective value (override OR parsed); a small
                    // ⚙ indicator marks rows that carry an operator
                    // override on that specific field so the operator
                    // can spot pinned cells at a glance.
                    const sheetCells = isSheet ? {
                      profile: e.effective_profile || '',
                      material: e.effective_material || '',
                      composition: e.effective_composition || '',
                      gauge: e.effective_gauge || '',
                      coverage_mm: e.effective_coverage_mm ?? null,
                      rate_type: e.effective_rate_type || '',
                      has_profile_ovr: !!e.has_profile_override,
                      has_material_ovr: !!e.has_material_override,
                      has_composition_ovr: !!e.has_composition_override,
                      has_gauge_ovr: !!e.has_gauge_override,
                      has_cover_ovr: !!e.has_coverage_mm_override,
                      has_rate_ovr: !!e.has_rate_type_override,
                    } : null;
                    const sheetChip = (val, hasOvr, ringColour, testid) => (
                      <span className="inline-flex items-center gap-1" data-testid={testid}>
                        {val ? (
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white ring-1 ${ringColour} text-slate-700`}>{val}</span>
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                        {hasOvr && <span className="text-[9px] text-slate-400" title="Operator override — pinned, won't auto-update when the row name changes">⚙</span>}
                      </span>
                    );
                    // Ridge structured cells — same shape as Sheet but
                    // five distinct fields: class / material / width /
                    // length_mm / work_type. The chip helper is reused
                    // with class-coded ring colours so iron / tile /
                    // mortar pop visually.
                    const ridgeCells = isRidge ? {
                      cls: e.effective_class || '',
                      material: e.effective_material || '',
                      width: e.effective_width || '',
                      length_mm: e.effective_length_mm ?? null,
                      work_type: e.effective_work_type || '',
                      has_class_ovr: !!e.has_class_override,
                      has_material_ovr: !!e.has_material_override,
                      has_width_ovr: !!e.has_width_override,
                      has_length_ovr: !!e.has_length_mm_override,
                      has_work_ovr: !!e.has_work_type_override,
                    } : null;
                    const classRing = ridgeCells?.cls === 'iron' ? 'ring-sky-300'
                                    : ridgeCells?.cls === 'tile' ? 'ring-zinc-300'
                                    : ridgeCells?.cls === 'mortar' ? 'ring-amber-300'
                                    : 'ring-slate-200';
                    // Valley structured cells — 4 fields total. Work
                    // Type is 'New' (supply + fit) vs 'Replace' (labour
                    // only); Material is one of Galv/Maxx/Zinc/ZinaCore;
                    // Width is the flashing girth in mm; Folds is the
                    // pressbrake fold count.
                    // Valley structured cells — 6 fields total. Class
                    // (iron/tile/mortar/concrete/clay - shared with
                    // ridges) is inferred from material; Work Type is
                    // 'New' (supply + fit) vs 'Replace' (labour only);
                    // Material is one of Galv/Maxx/Zinc/ZCore; Profile
                    // is the composite "300G/4F" girth+folds pattern;
                    // Width is the flashing girth in mm; Folds is the
                    // pressbrake fold count.
                    const valleyCells = isValley ? {
                      class_val: e.effective_class || '',
                      work_type: e.effective_work_type || '',
                      material: e.effective_material || '',
                      profile: e.effective_profile || '',
                      width_mm: e.effective_width_mm ?? null,
                      folds: e.effective_folds ?? null,
                      has_class_ovr: !!e.has_class_override,
                      has_work_ovr: !!e.has_work_type_override,
                      has_material_ovr: !!e.has_material_override,
                      has_profile_ovr: !!e.has_profile_override,
                      has_width_ovr: !!e.has_width_mm_override,
                      has_folds_ovr: !!e.has_folds_override,
                    } : null;
                    const valleyWorkRing = valleyCells?.work_type === 'New' ? 'ring-sky-300'
                                         : valleyCells?.work_type === 'Replace' ? 'ring-amber-300'
                                         : 'ring-slate-200';
                    // Flashing structured cells — material + width_mm + folds.
                    // Simple cascade: pick a material on the appraisal
                    // and the operator filters to just those rows.
                    const flashingCells = isFlashing ? {
                      material: e.effective_material || '',
                      width_mm: e.effective_width_mm ?? null,
                      folds: e.effective_folds ?? null,
                      has_material_ovr: !!e.has_material_override,
                      has_width_ovr: !!e.has_width_mm_override,
                      has_folds_ovr: !!e.has_folds_override,
                    } : null;
                    // Spouting structured cells — kind / profile /
                    // width_mm / thickness_mm / material. Kind chip uses
                    // distinct colours per family so the bracket /
                    // downpipe / pvc rows pop visually.
                    const spoutingCells = isSpouting ? {
                      kind: e.effective_kind || '',
                      profile: e.effective_profile || '',
                      width_mm: e.effective_width_mm ?? null,
                      thickness_mm: e.effective_thickness_mm ?? null,
                      material: e.effective_material || '',
                      has_kind_ovr: !!e.has_kind_override,
                      has_profile_ovr: !!e.has_profile_override,
                      has_width_ovr: !!e.has_width_mm_override,
                      has_thickness_ovr: !!e.has_thickness_mm_override,
                      has_material_ovr: !!e.has_material_override,
                    } : null;
                    const spoutingKindRing = spoutingCells?.kind === 'gutter' ? 'ring-sky-300'
                                           : spoutingCells?.kind === 'bracket' ? 'ring-emerald-300'
                                           : spoutingCells?.kind === 'downpipe' ? 'ring-amber-300'
                                           : spoutingCells?.kind === 'clip' ? 'ring-violet-300'
                                           : spoutingCells?.kind === 'accessory' ? 'ring-slate-300'
                                           : spoutingCells?.kind === 'pvc-system' ? 'ring-rose-300'
                                           : 'ring-slate-200';
                    // Underlay structured cells — brand / grade /
                    // variant / coverage_m2 / width_mm / length_m.
                    // Variant chip uses sky for Self Support (no
                    // battens needed) and amber for Heavy Weight.
                    const underlayCells = isUnderlayStruct ? {
                      brand: e.effective_brand || '',
                      grade: e.effective_grade || '',
                      variant: e.effective_variant || '',
                      coverage_m2: e.effective_coverage_m2 ?? null,
                      width_mm: e.effective_width_mm ?? null,
                      length_m: e.effective_length_m ?? null,
                      has_brand_ovr: !!e.has_brand_override,
                      has_grade_ovr: !!e.has_grade_override,
                      has_variant_ovr: !!e.has_variant_override,
                      has_coverage_ovr: !!e.has_coverage_m2_override,
                      has_width_ovr: !!e.has_width_mm_override,
                      has_length_ovr: !!e.has_length_m_override,
                    } : null;
                    const underlayVariantRing = underlayCells?.variant === 'Self Support' ? 'ring-sky-300'
                                              : underlayCells?.variant === 'Heavy Weight' ? 'ring-amber-300'
                                              : 'ring-slate-200';
                    // Barge structured cells — same shape as Ridge with
                    // the addition of `profile` (Corru / Ribline / Trimrib)
                    // and `thickness_mm` (gauge of the iron sheet).
                    const bargeCells = isBargeStruct ? {
                      cls: e.effective_class || '',
                      material: e.effective_material || '',
                      profile: e.effective_profile || '',
                      width_mm: e.effective_width_mm ?? null,
                      folds: e.effective_folds ?? null,
                      thickness_mm: e.effective_thickness_mm ?? null,
                      work_type: e.effective_work_type || '',
                      has_class_ovr: !!e.has_class_override,
                      has_material_ovr: !!e.has_material_override,
                      has_profile_ovr: !!e.has_profile_override,
                      has_width_ovr: !!e.has_width_mm_override,
                      has_folds_ovr: !!e.has_folds_override,
                      has_thickness_ovr: !!e.has_thickness_mm_override,
                      has_work_ovr: !!e.has_work_type_override,
                    } : null;
                    const bargeClassRing = bargeCells?.cls === 'iron' ? 'ring-sky-300'
                                         : bargeCells?.cls === 'tile' ? 'ring-zinc-300'
                                         : bargeCells?.cls === 'mortar' ? 'ring-amber-300'
                                         : bargeCells?.cls === 'concrete' ? 'ring-stone-300'
                                         : bargeCells?.cls === 'butynol' ? 'ring-rose-300'
                                         : 'ring-slate-200';
                    // Fastening structured cells.
                    const fastCells = isFastening ? {
                      work_type: e.effective_work_type || '',
                      screw_gauge: e.effective_screw_gauge ?? null,
                      screw_length_mm: e.effective_screw_length_mm ?? null,
                      rate_type: e.effective_rate_type || '',
                      variant: e.effective_variant || '',
                      has_work_ovr: !!e.has_work_type_override,
                      has_gauge_ovr: !!e.has_screw_gauge_override,
                      has_length_ovr: !!e.has_screw_length_mm_override,
                      has_rate_ovr: !!e.has_rate_type_override,
                      has_variant_ovr: !!e.has_variant_override,
                    } : null;
                    const fastWorkRing = fastCells?.work_type === 'Rescrew' ? 'ring-sky-300'
                                       : fastCells?.work_type === 'Replace' ? 'ring-amber-300'
                                       : fastCells?.work_type === 'Supply' ? 'ring-emerald-300'
                                       : 'ring-slate-200';
                    // Cleaning structured cell — just `clean_type`
                    // (Treatment vs Wash). Ring colour distinguishes
                    // chemical-treatment rows (violet) from
                    // labour-wash rows (sky).
                    const cleaningCells = isCleaning ? {
                      clean_type: e.effective_clean_type || '',
                      has_clean_type_ovr: !!e.has_clean_type_override,
                    } : null;
                    const cleaningRing = cleaningCells?.clean_type === 'Treatment' ? 'ring-violet-300'
                                      : cleaningCells?.clean_type === 'Wash' ? 'ring-sky-300'
                                      : 'ring-slate-200';
                    // Re-Coat structured cell — single `recoat_type`
                    // field. Ring colour distinguishes substrate
                    // families at a glance: amber for Iron, slate
                    // for Concretes, sky for Chipped.
                    const recoatCells = isRecoat ? {
                      recoat_type: e.effective_recoat_type || '',
                      has_recoat_type_ovr: !!e.has_recoat_type_override,
                    } : null;
                    const recoatRing = recoatCells?.recoat_type === 'Iron' ? 'ring-amber-300'
                                    : recoatCells?.recoat_type === 'Chipped' ? 'ring-sky-300'
                                    : recoatCells?.recoat_type === 'New Concrete' ? 'ring-emerald-300'
                                    : recoatCells?.recoat_type === 'Old Concrete' ? 'ring-stone-400'
                                    : recoatCells?.recoat_type === 'Concrete' ? 'ring-slate-300'
                                    : 'ring-slate-200';
                    if (editId === e._id) {
                      // 4-way variant picker: Auto (from name) / Std-pinned
                      // / Retrofit-pinned / Lead-pinned. Maps to a pair of
                      // override fields the backend honours independently:
                      //   - material_override:    null | 'epdm' | 'lead'
                      //   - is_retrofit_override: null | true   | false
                      // Lead overrides material to 'lead' and clears the
                      // is_retrofit pin (lead conforms to the pipe either way).
                      const overrideStr = editForm.material_override === 'lead' ? 'lead'
                                        : editForm.is_retrofit_override === true ? 'true'
                                        : editForm.is_retrofit_override === false ? 'false'
                                        : '';
                      return (
                        <tr key={e._id} className="bg-amber-50/30">
                          <td className="px-3 py-2"><input value={editForm.name || ''} onChange={ev => setEditForm({ ...editForm, name: ev.target.value })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs" /></td>
                          {isAquaseal && (
                            <td className="px-3 py-2">
                              <select
                                data-testid={`aquaseal-variant-edit-${e._id}`}
                                value={overrideStr}
                                onChange={ev => {
                                  const v = ev.target.value;
                                  let mat = null, ret = null;
                                  if (v === 'lead')       { mat = 'lead'; ret = null; }
                                  else if (v === 'true')  { mat = 'epdm'; ret = true; }
                                  else if (v === 'false') { mat = 'epdm'; ret = false; }
                                  setEditForm({
                                    ...editForm,
                                    material_override: mat,
                                    is_retrofit_override: ret,
                                  });
                                }}
                                className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                title="Std/Retrofit = EPDM Dektite for metal roofs. Lead = lead-sheet aquaseal for tile roofs. Auto = guess from the row name."
                              >
                                <option value="">Auto (from name)</option>
                                <option value="false">Std (pinned)</option>
                                <option value="true">Retrofit (pinned)</option>
                                <option value="lead">Lead (pinned · tile roofs)</option>
                              </select>
                            </td>
                          )}
                          {isAquaseal && <td className="px-3 py-2">{rangeChip}</td>}
                          {isSheet && (
                            <>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`sheet-profile-edit-${e._id}`}
                                  value={editForm.profile_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, profile_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Sheet profile — operator-managed list. Click 'Manage' on the filter row to add/rename/delete values."
                                >
                                  <option value="">— parser —</option>
                                  {(sheetVocab.profiles || []).map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`sheet-material-edit-${e._id}`}
                                  value={editForm.material_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, material_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Sheet material — operator-managed list. Click 'Manage' on the filter row to add/rename/delete values."
                                >
                                  <option value="">— parser —</option>
                                  {(sheetVocab.materials || []).map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`sheet-gauge-edit-${e._id}`}
                                  value={editForm.gauge_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, gauge_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white font-mono-num"
                                  placeholder={e.parsed?.gauge || '0.40BMT'}
                                  title="Sheet gauge — canonical form is e.g. 0.40BMT, 0.55BMT, 0.48BMT. Blank = use the parser's guess (shown as placeholder). Translucent / MDek Clips rows don't have a gauge — leave blank."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`sheet-coverage-edit-${e._id}`}
                                  type="number" step="0.1" min="0"
                                  value={editForm.coverage_mm_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, coverage_mm_override: raw === '' ? null : parseFloat(raw) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.coverage_mm ?? '—'}
                                  title="Sheet coverage in millimetres (decimals OK). Blank = use the parser's guess."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`sheet-rate-type-edit-${e._id}`}
                                  value={editForm.rate_type_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, rate_type_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Rate type — /mtr (linear metre), /m² (square metre), or /sheet (per sheet). Blank = use the parser's guess."
                                >
                                  <option value="">— parser —</option>
                                  {(sheetVocab.rate_types || ['/mtr', '/m²', '/sheet']).map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                              </td>
                            </>
                          )}
                          {isRidge && (
                            <>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`ridge-material-edit-${e._id}`}
                                  value={editForm.material_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, material_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Ridge material — operator-managed list. Click 'Manage' on the filter row to add/rename/delete values. Detection works on row names too."
                                >
                                  <option value="">— parser guess —</option>
                                  {ridgeVocab.materials.map(m => (
                                    <option key={m} value={m}>{m}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`ridge-width-edit-${e._id}`}
                                  list="ridge-width-options"
                                  value={editForm.width_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, width_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  placeholder={e.parsed?.width || '—'}
                                  title="Ridge width — Standard or Wide (only meaningful for iron ridges)."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`ridge-length-edit-${e._id}`}
                                  type="number" step="1" min="0"
                                  value={editForm.length_mm_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, length_mm_override: raw === '' ? null : parseInt(raw, 10) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.length_mm ?? '—'}
                                  title="Tile ridge length in millimetres (e.g. 420, 450, 490, 510). Blank = parser guess."
                                />
                              </td>
                            </>
                          )}
                          {isValley && (
                            <>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`valley-material-edit-${e._id}`}
                                  value={editForm.material_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, material_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Valley material — operator-managed list (click Manage on the Material filter row)."
                                >
                                  <option value="">— parser —</option>
                                  {(valleyVocab.materials || []).map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`valley-profile-edit-${e._id}`}
                                  value={editForm.profile_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, profile_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Valley profile — composite girth/folds pattern (e.g. 300G/4F). Operator-managed."
                                >
                                  <option value="">— parser —</option>
                                  {(valleyVocab.profiles || []).map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`valley-width-edit-${e._id}`}
                                  type="number" step="10" min="0"
                                  value={editForm.width_mm_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, width_mm_override: raw === '' ? null : parseInt(raw, 10) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.width_mm ?? '—'}
                                  title="Valley flashing girth in millimetres (typically 300 or 400). Blank = parser guess."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`valley-folds-edit-${e._id}`}
                                  type="number" step="1" min="0" max="15"
                                  value={editForm.folds_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, folds_override: raw === '' ? null : parseInt(raw, 10) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.folds ?? '—'}
                                  title="Number of pressbrake folds in the valley flashing (typically 3 or 5). Blank = parser guess."
                                />
                              </td>
                            </>
                          )}
                          {isFlashing && (() => {
                            // Union canonical material values with legacy
                            // data values so the operator sees every
                            // material the catalogue actually uses
                            // (Alu/AluGuard/etc.) plus the canonical
                            // cascade list.
                            const canonical = canonicalMap['flashing_material'] || [];
                            const materialOpts = Array.from(new Set([...canonical, ...Object.keys(flashingBuckets.material || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            return (
                            <>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`flashing-material-edit-${e._id}`}
                                  value={editForm.material_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, material_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Flashing material — full canonical list + legacy values still in the catalogue."
                                >
                                  <option value="">— (parsed: {e.parsed?.material || '—'})</option>
                                  {materialOpts.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`flashing-width-edit-${e._id}`}
                                  type="number" step="10" min="0"
                                  value={editForm.width_mm_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, width_mm_override: raw === '' ? null : parseInt(raw, 10) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.width_mm ?? '—'}
                                  title="Max flashing width in millimetres (the W number in the catalogue name)."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`flashing-folds-edit-${e._id}`}
                                  type="number" step="1" min="0" max="15"
                                  value={editForm.folds_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, folds_override: raw === '' ? null : parseInt(raw, 10) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.folds ?? '—'}
                                  title="Number of pressbrake folds in the flashing (the F number in the catalogue name). 1-8 typical."
                                />
                              </td>
                            </>
                            );
                          })()}
                          {isSpouting && (() => {
                            // Build option lists that union the canonical
                            // cascade values with any legacy values still
                            // present in the data (same merge logic as
                            // the filter pill rows). This way the edit
                            // dropdown shows EVERY value the operator
                            // might encounter — not just the canonical
                            // subset.
                            const kindOpts     = Array.from(new Set([...(spoutingVocab.kinds || []), ...Object.keys(spoutingBuckets.kind || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const profileOpts  = Array.from(new Set([...(spoutingVocab.profiles || []), ...Object.keys(spoutingBuckets.profile || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const materialOpts = Array.from(new Set([...(spoutingVocab.materials || []), ...Object.keys(spoutingBuckets.material || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            return (
                            <>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`spouting-kind-edit-${e._id}`}
                                  value={editForm.kind_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, kind_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Spouting kind — Gutter / Downpipe / Snow Strap / Bracket (plus any legacy values still in the catalogue)."
                                >
                                  <option value="">— (parsed: {e.parsed?.kind || '—'})</option>
                                  {kindOpts.map(k => <option key={k} value={k}>{k}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`spouting-profile-edit-${e._id}`}
                                  value={editForm.profile_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, profile_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Gutter profile — full canonical list + legacy values currently in the catalogue."
                                >
                                  <option value="">— (parsed: {e.parsed?.profile || '—'})</option>
                                  {profileOpts.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`spouting-width-edit-${e._id}`}
                                  type="number" step="5" min="0"
                                  value={editForm.width_mm_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, width_mm_override: raw === '' ? null : parseInt(raw, 10) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.width_mm ?? '—'}
                                  title="Gutter width / nominal mm (typically 125, 150)."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`spouting-thickness-edit-${e._id}`}
                                  type="number" step="0.05" min="0"
                                  value={editForm.thickness_mm_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, thickness_mm_override: raw === '' ? null : parseFloat(raw) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.thickness_mm ?? '—'}
                                  title="Material thickness in millimetres (typically 0.55 for steel, 2.75 for HDPE brackets)."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`spouting-material-edit-${e._id}`}
                                  value={editForm.material_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, material_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Material — full canonical list + legacy values currently in the catalogue."
                                >
                                  <option value="">— (parsed: {e.parsed?.material || '—'})</option>
                                  {materialOpts.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                              </td>
                            </>
                            );
                          })()}
                          {isUnderlayStruct && (() => {
                            const canonBrand   = canonicalMap['underlay_brand'] || [];
                            const canonVariant = canonicalMap['underlay_variant'] || [];
                            const brandOpts   = Array.from(new Set([...canonBrand,   ...Object.keys(underlayBuckets.brand   || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const variantOpts = Array.from(new Set([...canonVariant, ...Object.keys(underlayBuckets.variant || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            return (
                            <>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`underlay-brand-edit-${e._id}`}
                                  value={editForm.brand_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, brand_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Underlay brand — full canonical list + legacy values currently in the catalogue."
                                >
                                  <option value="">— (parsed: {e.parsed?.brand || '—'})</option>
                                  {brandOpts.map(b => <option key={b} value={b}>{b}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`underlay-grade-edit-${e._id}`}
                                  value={editForm.grade_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, grade_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.grade || '—'}
                                  title="Underlay grade — bitumen-impregnation product code (e.g. 215, 403, 407 for CoverTek)."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`underlay-variant-edit-${e._id}`}
                                  value={editForm.variant_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, variant_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Underlay variant — full canonical list + legacy values currently in the catalogue."
                                >
                                  <option value="">— (parsed: {e.parsed?.variant || '—'})</option>
                                  {variantOpts.map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`underlay-coverage-edit-${e._id}`}
                                  type="number" step="1" min="0"
                                  value={editForm.coverage_m2_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, coverage_m2_override: raw === '' ? null : parseFloat(raw) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.coverage_m2 ?? '—'}
                                  title="Coverage per roll in square metres (e.g. 50). Multiply by buy cost to derive $/m²."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`underlay-width-edit-${e._id}`}
                                  type="number" step="10" min="0"
                                  value={editForm.width_mm_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, width_mm_override: raw === '' ? null : parseInt(raw, 10) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.width_mm ?? '—'}
                                  title="Roll width in millimetres (typically 1350)."
                                />
                              </td>
                              <td className="px-3 py-2">
                                <input
                                  data-testid={`underlay-length-edit-${e._id}`}
                                  type="number" step="1" min="0"
                                  value={editForm.length_m_override ?? ''}
                                  onChange={ev => {
                                    const raw = ev.target.value;
                                    setEditForm({ ...editForm, length_m_override: raw === '' ? null : parseInt(raw, 10) });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num"
                                  placeholder={e.parsed?.length_m ?? '—'}
                                  title="Roll length in metres (typically 37). Coverage = width × length / 1000."
                                />
                              </td>
                            </>
                            );
                          })()}
                          {isBargeStruct && (() => {
                            // Union canonical cascade values with legacy
                            // values still present in the data so the
                            // operator sees every bucket the filter row
                            // shows above. Same merge pattern as
                            // Spouting / Tile.
                            const classOpts    = Array.from(new Set([...(bargeVocab.classes || []),    ...Object.keys(bargeBuckets.class    || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const materialOpts = Array.from(new Set([...(bargeVocab.materials || []),  ...Object.keys(bargeBuckets.material || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const profileOpts  = Array.from(new Set([...(bargeVocab.profiles || []),   ...Object.keys(bargeBuckets.profile  || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const workOpts     = Array.from(new Set([...(bargeVocab.work_types || [])])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            return (
                            <>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`barge-material-edit-${e._id}`}
                                  value={editForm.material_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, material_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                >
                                  <option value="">— (parsed: {e.parsed?.material || '—'})</option>
                                  {materialOpts.map(m => <option key={m} value={m}>{m}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`barge-profile-edit-${e._id}`}
                                  value={editForm.profile_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, profile_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                >
                                  <option value="">— (parsed: {e.parsed?.profile || '—'})</option>
                                  {profileOpts.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2"><input data-testid={`barge-width-edit-${e._id}`} type="number" step="10" min="0" value={editForm.width_mm_override ?? ''} onChange={ev => { const raw = ev.target.value; setEditForm({ ...editForm, width_mm_override: raw === '' ? null : parseInt(raw, 10) }); }} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num" placeholder={e.parsed?.width_mm ?? '—'} /></td>
                              <td className="px-3 py-2"><input data-testid={`barge-folds-edit-${e._id}`} type="number" step="1" min="0" max="15" value={editForm.folds_override ?? ''} onChange={ev => { const raw = ev.target.value; setEditForm({ ...editForm, folds_override: raw === '' ? null : parseInt(raw, 10) }); }} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num" placeholder={e.parsed?.folds ?? '—'} /></td>
                              <td className="px-3 py-2"><input data-testid={`barge-thickness-edit-${e._id}`} type="number" step="0.05" min="0" value={editForm.thickness_mm_override ?? ''} onChange={ev => { const raw = ev.target.value; setEditForm({ ...editForm, thickness_mm_override: raw === '' ? null : parseFloat(raw) }); }} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num" placeholder={e.parsed?.thickness_mm ?? '—'} /></td>
                            </>
                            );
                          })()}
                          {isFastening && (() => {
                            const canonWork    = canonicalMap['fastening_work_type'] || [];
                            const canonRate    = canonicalMap['fastening_rate_type'] || [];
                            const canonVariant = canonicalMap['fastening_variant'] || [];
                            const workOpts    = Array.from(new Set([...canonWork,    ...Object.keys(fastBuckets.work    || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const rateOpts    = Array.from(new Set([...canonRate,    ...Object.keys(fastBuckets.rate    || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const variantOpts = Array.from(new Set([...canonVariant, ...Object.keys(fastBuckets.variant || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            return (
                            <>
                              <td className="px-3 py-2"><input data-testid={`fastening-gauge-edit-${e._id}`} type="number" step="1" min="0" value={editForm.screw_gauge_override ?? ''} onChange={ev => { const raw = ev.target.value; setEditForm({ ...editForm, screw_gauge_override: raw === '' ? null : parseInt(raw, 10) }); }} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num" placeholder={e.parsed?.screw_gauge ?? '—'} title="Screw gauge (first number in '12*50' — typically 12 or 14)." /></td>
                              <td className="px-3 py-2"><input data-testid={`fastening-length-edit-${e._id}`} type="number" step="5" min="0" value={editForm.screw_length_mm_override ?? ''} onChange={ev => { const raw = ev.target.value; setEditForm({ ...editForm, screw_length_mm_override: raw === '' ? null : parseInt(raw, 10) }); }} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white text-right font-mono-num" placeholder={e.parsed?.screw_length_mm ?? '—'} title="Screw length in millimetres (second number in '12*50' — typically 35, 50, 65, 75)." /></td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`fastening-rate-edit-${e._id}`}
                                  value={editForm.rate_type_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, rate_type_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Rate type — /m² (per square metre), /m (per linear metre), /each. Cascades into quote calculation."
                                >
                                  <option value="">— (parsed: {e.parsed?.rate_type || '/m²'})</option>
                                  {rateOpts.map(r => <option key={r} value={r}>{r}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`fastening-variant-edit-${e._id}`}
                                  value={editForm.variant_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, variant_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Optional sub-category — ColourTile, Multidek, Timbertek, AR8, steep (plus any legacy values still in the catalogue)."
                                >
                                  <option value="">— (parsed: {e.parsed?.variant || '—'})</option>
                                  {variantOpts.map(v => <option key={v} value={v}>{v}</option>)}
                                </select>
                              </td>
                            </>
                            );
                          })()}
                          {isCleaning && (
                            <td className="px-3 py-2">
                              <select
                                data-testid={`cleaning-clean-type-edit-${e._id}`}
                                value={editForm.clean_type_override ?? ''}
                                onChange={ev => setEditForm({ ...editForm, clean_type_override: ev.target.value || null })}
                                className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                title="Clean Type — Treatment (chemical moss/lichen kill) or Wash (water-blast labour). Blank = parser guess from the row name."
                              >
                                <option value="">— parser guess —</option>
                                {['Treatment', 'Wash'].map(c => (
                                  <option key={c} value={c}>{c}</option>
                                ))}
                              </select>
                            </td>
                          )}
                          {isRecoat && (
                            <td className="px-3 py-2">
                              <select
                                data-testid={`recoat-type-edit-${e._id}`}
                                value={editForm.recoat_type_override ?? ''}
                                onChange={ev => setEditForm({ ...editForm, recoat_type_override: ev.target.value || null })}
                                className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                title="Recoat Type — Chipped / New Concrete / Old Concrete / Iron. Concrete (unqualified) lives in its own bucket; flip it onto New/Old here if needed."
                              >
                                <option value="">— parser guess —</option>
                                {['Chipped', 'New Concrete', 'Old Concrete', 'Iron', 'Concrete'].map(t => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                            </td>
                          )}
                          {isTile && (() => {
                            // Union canonical cascade values with legacy
                            // data values so the operator sees every
                            // bucket present in the catalogue plus the
                            // canonical extras.
                            const classOpts    = Array.from(new Set([...(tileVocab.classes || []),    ...Object.keys(tileBuckets.class   || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const materialOpts = Array.from(new Set([...(tileVocab.materials || []),  ...Object.keys(tileBuckets.type    || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const profileOpts  = Array.from(new Set([...(tileVocab.profiles || []),   ...Object.keys(tileBuckets.profile || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const workOpts     = Array.from(new Set([...(tileVocab.work_types || []), ...Object.keys(tileBuckets.work    || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            return (
                            <>
                              <td className="px-3 py-2">
                                <select data-testid={`tile-material-edit-${e._id}`} value={editForm.tile_type_override ?? ''} onChange={ev => setEditForm({ ...editForm, tile_type_override: ev.target.value || null })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white">
                                  <option value="">— (parsed: {e.parsed?.tile_type || '—'})</option>
                                  {materialOpts.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select data-testid={`tile-profile-edit-${e._id}`} value={editForm.profile_override ?? ''} onChange={ev => setEditForm({ ...editForm, profile_override: ev.target.value || null })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white">
                                  <option value="">— (parsed: {e.parsed?.profile || '—'})</option>
                                  {profileOpts.map(p => <option key={p} value={p}>{p}</option>)}
                                </select>
                              </td>
                            </>
                            );
                          })()}
                          {isSafety && (
                            <td className="px-3 py-2">
                              <select data-testid={`safety-kind-edit-${e._id}`} value={editForm.kind_override ?? ''} onChange={ev => setEditForm({ ...editForm, kind_override: ev.target.value || null })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white">
                                <option value="">— parser —</option>
                                {['Edge Protection', 'Harness', 'Height Safety', 'Power Sleaving', 'Safety Set Up', 'Scaffold'].map(k => <option key={k} value={k}>{k}</option>)}
                              </select>
                            </td>
                          )}
                          {isMisc && (
                            <td className="px-3 py-2">
                              <select data-testid={`misc-category-edit-${e._id}`} value={editForm.category_override ?? ''} onChange={ev => setEditForm({ ...editForm, category_override: ev.target.value || null })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white">
                                <option value="">— parser —</option>
                                {['Spinaway', 'Tradesman', 'Strip', 'Labourer', 'Rubbish', 'Adjustment', 'Materials', 'Other'].map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </td>
                          )}
                          {isUrgent && (
                            <td className="px-3 py-2">
                              <select data-testid={`urgent-type-edit-${e._id}`} value={editForm.urgency_type_override ?? ''} onChange={ev => setEditForm({ ...editForm, urgency_type_override: ev.target.value || null })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white">
                                <option value="">— parser —</option>
                                {['Appraisal', 'Remedial', 'Small Job', 'Call Out'].map(u => <option key={u} value={u}>{u}</option>)}
                              </select>
                            </td>
                          )}
                          {isTravel && (
                            <td className="px-3 py-2">
                              <select data-testid={`travel-trade-edit-${e._id}`} value={editForm.trade_override ?? ''} onChange={ev => setEditForm({ ...editForm, trade_override: ev.target.value || null })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white">
                                <option value="">— parser —</option>
                                {['Contractor', 'Painter', 'Iron', 'Concrete', 'Local', 'Mileage'].map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </td>
                          )}
                          {RATE_FIELDS.map(f => (
                            <td key={f} className="px-3 py-2"><input type="number" step="0.01" value={editForm[f] ?? ''} onChange={ev => setEditForm({ ...editForm, [f]: parseFloat(ev.target.value) || 0 })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs text-right" /></td>
                          ))}
                          <td className="px-3 py-2 text-right font-mono text-xs font-bold text-emerald-800 bg-emerald-50/50">${((Number(editForm.materials_rate) || 0) + (Number(editForm.labour_rate) || 0)).toFixed(2)}</td>
                          {/* iter139 — cascade-link editors + Class/Work
                              dropdowns moved here so the edit row mirrors
                              the new header order (Name → structured →
                              Buy/Mat/Lab/Con → Roof Types/Brands/Profiles
                              → Class/Work → text fields → delete). */}
                          <td className="px-3 py-2 bg-orange-50/20">
                            <ChipMultiSelect
                              value={editForm.roof_type_links || []}
                              options={roofTypeOptions}
                              onChange={(v) => setEditForm({ ...editForm, roof_type_links: v, brand_links: [], profile_links: [] })}
                              placeholder="All"
                            />
                          </td>
                          <td className="px-3 py-2 bg-orange-50/20">
                            <ChipMultiSelect
                              value={editForm.brand_links || []}
                              options={roofBrandOptions}
                              loadOptions={() => fetchBrandsForTypes(editForm.roof_type_links || [])}
                              onChange={(v) => setEditForm({ ...editForm, brand_links: v, profile_links: [] })}
                              placeholder="All"
                              hint={(editForm.roof_type_links || []).length ? `For ${(editForm.roof_type_links || []).join(', ')}` : 'All roof types'}
                            />
                          </td>
                          <td className="px-3 py-2 bg-orange-50/20">
                            <ChipMultiSelect
                              value={editForm.profile_links || []}
                              options={profileOptions}
                              loadOptions={() => fetchProfilesForBrands(editForm.brand_links || [])}
                              onChange={(v) => setEditForm({ ...editForm, profile_links: v })}
                              placeholder="All"
                              hint={(editForm.brand_links || []).length ? `For ${(editForm.brand_links || []).join(', ')}` : 'All brands'}
                            />
                          </td>
                          {isRidge && (
                            <>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`ridge-class-edit-${e._id}`}
                                  value={editForm.class_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, class_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Ridge class — operator-managed list. Click 'Manage' on the filter row to add/rename/delete values."
                                >
                                  <option value="">— parser guess —</option>
                                  {ridgeVocab.classes.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`ridge-work-type-edit-${e._id}`}
                                  value={editForm.work_type_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, work_type_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Work type — Bed & Point / Point (mortar labour), Replace (swap out existing ridge), Supply (material only). Blank = parser guess."
                                >
                                  <option value="">— parser guess —</option>
                                  {['Bed & Point', 'Point', 'Replace', 'Supply'].map(w => (
                                    <option key={w} value={w}>{w}</option>
                                  ))}
                                </select>
                              </td>
                            </>
                          )}
                          {isValley && (
                            <>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`valley-class-edit-${e._id}`}
                                  value={editForm.class_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, class_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Valley class — shared with Ridges & Barges (iron / tile / mortar / concrete / clay). Most valley rows are iron press-formed flashings; flip here if you stock a tile/mortar valley."
                                >
                                  <option value="">— parser —</option>
                                  {(valleyVocab.classes || []).map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`valley-work-edit-${e._id}`}
                                  value={editForm.work_type_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, work_type_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Work type — New (supplied + fitted) or Replace (labour-only swap)."
                                >
                                  <option value="">— parser —</option>
                                  {(valleyVocab.work_types || []).map(w => <option key={w} value={w}>{w}</option>)}
                                </select>
                              </td>
                            </>
                          )}
                          {isBargeStruct && (() => {
                            const classOpts = Array.from(new Set([...(bargeVocab.classes || []),  ...Object.keys(bargeBuckets.class || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const workOpts  = Array.from(new Set([...(bargeVocab.work_types || [])])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            return (<>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`barge-class-edit-${e._id}`}
                                  value={editForm.class_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, class_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                >
                                  <option value="">— (parsed: {e.parsed?.class || '—'})</option>
                                  {classOpts.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`barge-work-edit-${e._id}`}
                                  value={editForm.work_type_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, work_type_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                >
                                  <option value="">— (parsed: {e.parsed?.work_type || '—'})</option>
                                  {workOpts.map(w => <option key={w} value={w}>{w}</option>)}
                                </select>
                              </td>
                            </>);
                          })()}
                          {isFastening && (() => {
                            const canonWork = canonicalMap['fastening_work_type'] || [];
                            const workOpts  = Array.from(new Set([...canonWork, ...Object.keys(fastBuckets.work || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            return (
                              <td className="px-3 py-2">
                                <select
                                  data-testid={`fastening-work-edit-${e._id}`}
                                  value={editForm.work_type_override ?? ''}
                                  onChange={ev => setEditForm({ ...editForm, work_type_override: ev.target.value || null })}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  title="Fastening work — Rescrew (over existing), Replace (swap out), or Supply (new install)."
                                >
                                  <option value="">— (parsed: {e.parsed?.work_type || '—'})</option>
                                  {workOpts.map(w => <option key={w} value={w}>{w}</option>)}
                                </select>
                              </td>
                            );
                          })()}
                          {isTile && (() => {
                            const classOpts = Array.from(new Set([...(tileVocab.classes || []), ...Object.keys(tileBuckets.class || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            const workOpts  = Array.from(new Set([...(tileVocab.work_types || []), ...Object.keys(tileBuckets.work || {}).filter(k => k && k !== '—')])).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
                            return (<>
                              <td className="px-3 py-2">
                                <select data-testid={`tile-class-edit-${e._id}`} value={editForm.class_override ?? ''} onChange={ev => setEditForm({ ...editForm, class_override: ev.target.value || null })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white">
                                  <option value="">— (parsed: {e.parsed?.class || '—'})</option>
                                  {classOpts.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                              </td>
                              <td className="px-3 py-2">
                                <select data-testid={`tile-work-edit-${e._id}`} value={editForm.work_type_override ?? ''} onChange={ev => setEditForm({ ...editForm, work_type_override: ev.target.value || null })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white">
                                  <option value="">— (parsed: {e.parsed?.work_type || '—'})</option>
                                  {workOpts.map(w => <option key={w} value={w}>{w}</option>)}
                                </select>
                              </td>
                            </>);
                          })()}
                          {TEXT_FIELDS.map(t => (
                            <td key={t.k} className="px-3 py-2">
                              {t.selectOnly ? (
                                <select
                                  value={editForm[t.k] || ''}
                                  onChange={ev => {
                                    // Auto-save on pick. Strict enum
                                    // = no risk of "wait, still typing".
                                    // Saves the operator scrolling right
                                    // to find the green-check button.
                                    persistEdit({ [t.k]: ev.target.value });
                                  }}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs bg-white"
                                  data-testid={`edit-${t.k}-${e._id}`}
                                  title={`${t.l} — canonical cascade families: ${labMatFamilies.join(' / ')}. Same dropdown used across every component type so labour + material rate changes cascade by roof family. Saves on pick.`}
                                >
                                  <option value="">— select —</option>
                                  {(distinctByField[t.k] || []).map(v => (
                                    <option key={v} value={v}>{v}{!labMatFamilies.includes(v) ? ' (legacy)' : ''}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  value={editForm[t.k] || ''}
                                  onChange={ev => setEditForm({ ...editForm, [t.k]: ev.target.value })}
                                  list={t.useDatalist ? `text-field-options-${t.k}` : undefined}
                                  className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs"
                                  data-testid={t.useDatalist ? `edit-${t.k}-${e._id}` : undefined}
                                  title={t.useDatalist
                                    ? (t.globalSource === 'suppliers'
                                        ? `${t.l} — pick from the Suppliers directory + any supplier already cited on this component (${distinctByField[t.k]?.length || 0} options), or type a new one.`
                                        : `${t.l} — pick an existing code to cascade changes to every row sharing it, or type a new code. Distinct values found: ${distinctByField[t.k]?.length || 0}.`)
                                    : undefined}
                                />
                              )}
                            </td>
                          ))}
                          {extraFields.map(ef => (
                            <td key={ef} className="px-3 py-2"><input type="number" step="0.01" value={editForm[ef] ?? ''} onChange={ev => setEditForm({ ...editForm, [ef]: parseFloat(ev.target.value) || 0 })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs text-right" /></td>
                          ))}
                          <td className="px-3 py-2 text-right sticky right-0 bg-amber-50/95 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.15)] z-10">
                            <div className="flex gap-1 justify-end" data-testid={`edit-actions-${e._id}`}>
                              <button onClick={saveEdit} title="Save changes" className="p-1 text-green-600 hover:bg-green-100 rounded"><Check size={13} /></button>
                              <button onClick={() => setEditId(null)} title="Cancel — discard unsaved text-field changes (Lab/Mat & structured overrides auto-save on pick)" className="p-1 text-slate-400 hover:bg-slate-100 rounded"><X size={13} /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={e._id} data-testid={`rate-row-${e._id}`}
                          ref={(el) => {
                            // Iter 103: when this row matches the
                            // `?highlight=` query, scroll it into view
                            // once. The pulse-ring stays on until the
                            // operator clicks elsewhere (no setTimeout
                            // race with state updates).
                            if (el && e._id === highlightRowId && !el.dataset.highlightScrolled) {
                              el.dataset.highlightScrolled = '1';
                              setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 250);
                            }
                          }}
                          onClick={() => { if (e._id === highlightRowId) setHighlightRowId(''); }}
                          className={`transition-all ${e._id === highlightRowId ? 'bg-emerald-100 ring-2 ring-emerald-400 cursor-pointer' : 'hover:bg-slate-50'}`}>
                        <td className="px-3 py-2 font-medium text-slate-800 text-xs">{e.name}</td>
                        {isAquaseal && <td className="px-3 py-2" data-testid={`aquaseal-variant-${e._id}`}>{variantChip}</td>}
                        {isAquaseal && <td className="px-3 py-2" data-testid={`aquaseal-range-${e._id}`}>{rangeChip}</td>}
                        {isSheet && (
                          <>
                            <td className="px-3 py-2">{sheetChip(sheetCells.profile, sheetCells.has_profile_ovr, 'ring-sky-200', `sheet-profile-${e._id}`)}</td>
                            <td className="px-3 py-2">{sheetChip(sheetCells.material, sheetCells.has_material_ovr, 'ring-emerald-200', `sheet-material-${e._id}`)}</td>
                            <td className="px-3 py-2">{sheetChip(sheetCells.composition, sheetCells.has_composition_ovr, 'ring-amber-200', `sheet-composition-${e._id}`)}</td>
                            <td className="px-3 py-2" data-testid={`sheet-gauge-${e._id}`}>
                              {sheetCells.gauge ? (
                                <span className="font-mono-num text-xs text-slate-700">{sheetCells.gauge}</span>
                              ) : <span className="text-slate-300 text-xs">—</span>}
                              {sheetCells.has_gauge_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the gauge field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`sheet-coverage-${e._id}`}>
                              {sheetCells.coverage_mm != null ? `${sheetCells.coverage_mm}` : <span className="text-slate-300">—</span>}
                              {sheetCells.has_cover_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the coverage field">⚙</span>}
                            </td>
                            <td className="px-3 py-2" data-testid={`sheet-rate-type-${e._id}`}>
                              {sheetCells.rate_type ? (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-100 text-slate-700">{sheetCells.rate_type}</span>
                              ) : <span className="text-slate-300 text-xs">—</span>}
                              {sheetCells.has_rate_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the rate-type field">⚙</span>}
                            </td>
                          </>
                        )}
                        {isRidge && (
                          <>
                            <td className="px-3 py-2">{sheetChip(ridgeCells.material, ridgeCells.has_material_ovr, 'ring-emerald-200', `ridge-material-${e._id}`)}</td>
                            <td className="px-3 py-2" data-testid={`ridge-width-${e._id}`}>
                              {ridgeCells.width ? (
                                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white ring-1 ring-slate-300 text-slate-700 ${ridgeCells.width === 'Wide' ? 'font-bold' : ''}`}>{ridgeCells.width}</span>
                              ) : <span className="text-slate-300 text-xs">—</span>}
                              {ridgeCells.has_width_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the width field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`ridge-length-${e._id}`}>
                              {ridgeCells.length_mm != null && ridgeCells.length_mm > 0 ? (
                                <span className="inline-flex items-center gap-0.5">{ridgeCells.length_mm} <span className="text-slate-400 text-[10px]">mm</span></span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 ring-1 ring-amber-300 text-amber-800"
                                      data-testid={`ridge-length-missing-${e._id}`}
                                      title="No cap length set — Tin Tile Restoration will fall back to 1200 mm. Click the row's edit button to enter the correct length.">
                                  ⚠ no length
                                </span>
                              )}
                              {ridgeCells.has_length_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the length field">⚙</span>}
                            </td>
                          </>
                        )}
                        {isValley && (
                          <>
                            <td className="px-3 py-2">{sheetChip(valleyCells.material, valleyCells.has_material_ovr, 'ring-emerald-200', `valley-material-${e._id}`)}</td>
                            <td className="px-3 py-2">{sheetChip(valleyCells.profile, valleyCells.has_profile_ovr, 'ring-sky-200', `valley-profile-${e._id}`)}</td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`valley-width-${e._id}`}>
                              {valleyCells.width_mm != null ? `${valleyCells.width_mm}` : <span className="text-slate-300">—</span>}
                              {valleyCells.has_width_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the width field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`valley-folds-${e._id}`}>
                              {valleyCells.folds != null ? `${valleyCells.folds}` : <span className="text-slate-300">—</span>}
                              {valleyCells.has_folds_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the folds field">⚙</span>}
                            </td>
                          </>
                        )}
                        {isFlashing && (
                          <>
                            <td className="px-3 py-2">{sheetChip(flashingCells.material, flashingCells.has_material_ovr, 'ring-emerald-200', `flashing-material-${e._id}`)}</td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`flashing-width-${e._id}`}>
                              {flashingCells.width_mm != null ? `${flashingCells.width_mm}` : <span className="text-slate-300">—</span>}
                              {flashingCells.has_width_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the width field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`flashing-folds-${e._id}`}>
                              {flashingCells.folds != null ? `${flashingCells.folds}` : <span className="text-slate-300">—</span>}
                              {flashingCells.has_folds_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the folds field">⚙</span>}
                            </td>
                          </>
                        )}
                        {isSpouting && (
                          <>
                            <td className="px-3 py-2">{sheetChip(spoutingCells.kind, spoutingCells.has_kind_ovr, spoutingKindRing, `spouting-kind-${e._id}`)}</td>
                            <td className="px-3 py-2">{sheetChip(spoutingCells.profile, spoutingCells.has_profile_ovr, 'ring-sky-200', `spouting-profile-${e._id}`)}</td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`spouting-width-${e._id}`}>
                              {spoutingCells.width_mm != null ? `${spoutingCells.width_mm}` : <span className="text-slate-300">—</span>}
                              {spoutingCells.has_width_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the width field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`spouting-thickness-${e._id}`}>
                              {spoutingCells.thickness_mm != null ? spoutingCells.thickness_mm.toFixed(2) : <span className="text-slate-300">—</span>}
                              {spoutingCells.has_thickness_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the thickness field">⚙</span>}
                            </td>
                            <td className="px-3 py-2">{sheetChip(spoutingCells.material, spoutingCells.has_material_ovr, 'ring-emerald-200', `spouting-material-${e._id}`)}</td>
                          </>
                        )}
                        {isUnderlayStruct && (
                          <>
                            <td className="px-3 py-2">{sheetChip(underlayCells.brand, underlayCells.has_brand_ovr, 'ring-violet-200', `underlay-brand-${e._id}`)}</td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`underlay-grade-${e._id}`}>
                              {underlayCells.grade ? underlayCells.grade : <span className="text-slate-300">—</span>}
                              {underlayCells.has_grade_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the grade field">⚙</span>}
                            </td>
                            <td className="px-3 py-2">{sheetChip(underlayCells.variant, underlayCells.has_variant_ovr, underlayVariantRing, `underlay-variant-${e._id}`)}</td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`underlay-coverage-${e._id}`}>
                              {underlayCells.coverage_m2 != null ? `${underlayCells.coverage_m2}` : <span className="text-slate-300">—</span>}
                              {underlayCells.has_coverage_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the coverage field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`underlay-width-${e._id}`}>
                              {underlayCells.width_mm != null ? `${underlayCells.width_mm}` : <span className="text-slate-300">—</span>}
                              {underlayCells.has_width_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the width field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`underlay-length-${e._id}`}>
                              {underlayCells.length_m != null ? `${underlayCells.length_m}` : <span className="text-slate-300">—</span>}
                              {underlayCells.has_length_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the length field">⚙</span>}
                            </td>
                          </>
                        )}
                        {isBargeStruct && (
                          <>
                            <td className="px-3 py-2">{sheetChip(bargeCells.material, bargeCells.has_material_ovr, 'ring-emerald-200', `barge-material-${e._id}`)}</td>
                            <td className="px-3 py-2">{sheetChip(bargeCells.profile, bargeCells.has_profile_ovr, 'ring-sky-200', `barge-profile-${e._id}`)}</td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`barge-width-${e._id}`}>
                              {bargeCells.width_mm != null ? `${bargeCells.width_mm}` : <span className="text-slate-300">—</span>}
                              {bargeCells.has_width_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the width field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`barge-folds-${e._id}`}>
                              {bargeCells.folds != null ? `${bargeCells.folds}` : <span className="text-slate-300">—</span>}
                              {bargeCells.has_folds_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the folds field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`barge-thickness-${e._id}`}>
                              {bargeCells.thickness_mm != null ? bargeCells.thickness_mm.toFixed(2) : <span className="text-slate-300">—</span>}
                              {bargeCells.has_thickness_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the thickness field">⚙</span>}
                            </td>
                          </>
                        )}
                        {isFastening && (
                          <>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`fastening-gauge-${e._id}`}>
                              {fastCells.screw_gauge != null ? `${fastCells.screw_gauge}` : <span className="text-slate-300">—</span>}
                              {fastCells.has_gauge_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the gauge field">⚙</span>}
                            </td>
                            <td className="px-3 py-2 text-right font-mono-num text-xs" data-testid={`fastening-length-${e._id}`}>
                              {fastCells.screw_length_mm != null ? `${fastCells.screw_length_mm}` : <span className="text-slate-300">—</span>}
                              {fastCells.has_length_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the length field">⚙</span>}
                            </td>
                            <td className="px-3 py-2" data-testid={`fastening-rate-${e._id}`}>
                              {fastCells.rate_type ? (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono font-semibold bg-slate-100 text-slate-700">{fastCells.rate_type}</span>
                              ) : <span className="text-slate-300 text-xs">—</span>}
                              {fastCells.has_rate_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the rate-type field">⚙</span>}
                            </td>
                            <td className="px-3 py-2">{sheetChip(fastCells.variant, fastCells.has_variant_ovr, 'ring-violet-200', `fastening-variant-${e._id}`)}</td>
                          </>
                        )}
                        {isCleaning && (
                          <td className="px-3 py-2">{sheetChip(cleaningCells.clean_type, cleaningCells.has_clean_type_ovr, cleaningRing, `cleaning-clean-type-${e._id}`)}</td>
                        )}
                        {isRecoat && (
                          <td className="px-3 py-2">{sheetChip(recoatCells.recoat_type, recoatCells.has_recoat_type_ovr, recoatRing, `recoat-type-${e._id}`)}</td>
                        )}
                        {isTile && (
                          <>
                            <td className="px-3 py-2">{sheetChip(e.effective_tile_type || '', !!e.has_tile_type_override, 'ring-emerald-200', `tile-material-${e._id}`)}</td>
                            <td className="px-3 py-2">{sheetChip(e.effective_profile || '', !!e.has_profile_override, 'ring-sky-200', `tile-profile-${e._id}`)}</td>
                          </>
                        )}
                        {isSafety && (
                          <td className="px-3 py-2">{sheetChip(e.effective_kind || '', !!e.has_kind_override, 'ring-amber-200', `safety-kind-${e._id}`)}</td>
                        )}
                        {isMisc && (
                          <td className="px-3 py-2">{sheetChip(e.effective_category || '', !!e.has_category_override, 'ring-violet-200', `misc-category-${e._id}`)}</td>
                        )}
                        {isUrgent && (
                          <td className="px-3 py-2">{sheetChip(e.effective_urgency_type || '', !!e.has_urgency_type_override, 'ring-rose-200', `urgent-type-${e._id}`)}</td>
                        )}
                        {isTravel && (
                          <td className="px-3 py-2">{sheetChip(e.effective_trade || '', !!e.has_trade_override, 'ring-sky-200', `travel-trade-${e._id}`)}</td>
                        )}
                        <td className="px-3 py-2 text-right font-mono text-xs">${(e.buy_cost || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">${(e.materials_rate || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">${(e.labour_rate || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">${(e.contractor_rate || 0).toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs font-bold text-emerald-800 bg-emerald-50/50" data-testid={`rtr-rate-${e._id}`}>${((Number(e.materials_rate) || 0) + (Number(e.labour_rate) || 0)).toFixed(2)}</td>
                        {/* iter139 — cascade-link columns + categorical
                            (Class / Work) columns relocated here per
                            operator request. View-mode mirrors the new
                            header order, so the table scans
                            "name → numbers → tags" left-to-right. */}
                        <td className="px-3 py-2 align-top bg-orange-50/20" data-testid={`cell-roof-type-links-${e._id}`}>
                          <ChipMultiSelect
                            value={e.roof_type_links || []}
                            options={roofTypeOptions}
                            onChange={(v) => saveCascadeLinks(e._id, { roof_type_links: v, brand_links: [], profile_links: [] })}
                            placeholder="All"
                          />
                        </td>
                        <td className="px-3 py-2 align-top bg-orange-50/20" data-testid={`cell-brand-links-${e._id}`}>
                          <ChipMultiSelect
                            value={e.brand_links || []}
                            options={roofBrandOptions}
                            loadOptions={() => fetchBrandsForTypes(e.roof_type_links || [])}
                            onChange={(v) => saveCascadeLinks(e._id, { brand_links: v, profile_links: [] })}
                            placeholder="All"
                            hint={(e.roof_type_links || []).length ? `For ${(e.roof_type_links || []).join(', ')}` : 'All roof types'}
                          />
                        </td>
                        <td className="px-3 py-2 align-top bg-orange-50/20" data-testid={`cell-profile-links-${e._id}`}>
                          <ChipMultiSelect
                            value={e.profile_links || []}
                            options={profileOptions}
                            loadOptions={() => fetchProfilesForBrands(e.brand_links || [])}
                            onChange={(v) => saveCascadeLinks(e._id, { profile_links: v })}
                            placeholder="All"
                            hint={(e.brand_links || []).length ? `For ${(e.brand_links || []).join(', ')}` : 'All brands'}
                          />
                        </td>
                        {isRidge && (
                          <>
                            <td className="px-3 py-2">{sheetChip(ridgeCells.cls, ridgeCells.has_class_ovr, classRing, `ridge-class-${e._id}`)}</td>
                            <td className="px-3 py-2" data-testid={`ridge-work-type-${e._id}`}>
                              {ridgeCells.work_type ? (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 ring-1 ring-amber-200 text-amber-800">{ridgeCells.work_type}</span>
                              ) : <span className="text-slate-300 text-xs">—</span>}
                              {ridgeCells.has_work_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the work-type field">⚙</span>}
                            </td>
                          </>
                        )}
                        {isValley && (
                          <>
                            <td className="px-3 py-2">{sheetChip(valleyCells.class_val, valleyCells.has_class_ovr, 'ring-violet-200', `valley-class-${e._id}`)}</td>
                            <td className="px-3 py-2">{sheetChip(valleyCells.work_type, valleyCells.has_work_ovr, valleyWorkRing, `valley-work-${e._id}`)}</td>
                          </>
                        )}
                        {isBargeStruct && (
                          <>
                            <td className="px-3 py-2">{sheetChip(bargeCells.cls, bargeCells.has_class_ovr, bargeClassRing, `barge-class-${e._id}`)}</td>
                            <td className="px-3 py-2" data-testid={`barge-work-${e._id}`}>
                              {bargeCells.work_type ? (
                                <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-50 ring-1 ring-amber-200 text-amber-800">{bargeCells.work_type}</span>
                              ) : <span className="text-slate-300 text-xs">—</span>}
                              {bargeCells.has_work_ovr && <span className="ml-1 text-[9px] text-slate-400" title="Operator override on the work-type field">⚙</span>}
                            </td>
                          </>
                        )}
                        {isFastening && (
                          <td className="px-3 py-2">{sheetChip(fastCells.work_type, fastCells.has_work_ovr, fastWorkRing, `fastening-work-${e._id}`)}</td>
                        )}
                        {isTile && (
                          <>
                            <td className="px-3 py-2">{sheetChip(e.effective_class || '', !!e.has_class_override, 'ring-amber-200', `tile-class-${e._id}`)}</td>
                            <td className="px-3 py-2">{sheetChip(e.effective_work_type || '', !!e.has_work_type_override, 'ring-violet-200', `tile-work-${e._id}`)}</td>
                          </>
                        )}
                        {TEXT_FIELDS.map(t => (
                          <td key={t.k} className="px-3 py-2 text-xs text-slate-500 truncate max-w-[140px]" title={e[t.k] || ''}>{e[t.k] || '-'}</td>
                        ))}
                        {extraFields.map(ef => <td key={ef} className="px-3 py-2 text-right font-mono text-xs">{e[ef] || '-'}</td>)}
                        <td className="px-3 py-2 text-right" style={{ position: 'sticky', right: 0, background: 'inherit' }}>
                          <div className="flex gap-1 justify-end">
                            <button data-testid={`edit-rate-${e._id}`} onClick={() => { setEditId(e._id); setEditForm({ ...e }); }} className="p-1.5 text-slate-400 hover:text-[#D35400] rounded hover:bg-orange-50" title="Edit"><Edit2 size={14} /></button>
                            <button data-testid={`delete-rate-${e._id}`} onClick={() => deleteEntry(e._id)} className="p-1.5 text-red-400 hover:text-red-600 rounded hover:bg-red-50" title="Delete"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Bottom horizontal slider — same pills as the top, so after the user
          has scrolled through a long rate table they can jump straight to
          another component without travelling back up the page. */}
      <ComponentSlider testIdSuffix="bottom" components={COMPONENTS} active={active} counts={counts} onPick={(k) => { setActive(k); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />

      {/* Iter 118 — dedicated bulk-edit grid for the 36 maintenance
          size-tier rows. Lives below the standard component table so
          office staff can sanity-check / adjust every Small / Medium /
          Large rate without opening each appraisal. */}
      <MaintenanceSizeTiersSection api={api} />

      {reviewOpen && (
        <BulkNormaliseModal
          api={api}
          entries={entries}
          component={active}
          componentLabel={compLabel}
          onClose={() => setReviewOpen(false)}
          onSaved={() => { loadEntries(); }}
        />
      )}
    </div>
  );
}

/* -- BulkNormaliseModal -------------------------------------------------- */
/* Steps the operator through every row of the active component one at a   */
/* time so they can review + rename the catalogue entries without juggling */
/* the full table. For Aquaseal it pre-computes smart suggestions          */
/* (e.g. "WITH" → "WITH CLIPS" for retrofit Dektites the parser is missing */
/* the keyword on). Generic suggestions (trim whitespace, collapse double  */
/* spaces, force UPPERCASE) are applied across every component.            */
function BulkNormaliseModal({ api, entries, component, componentLabel, onClose, onSaved }) {
  // ─────────────────────────────────────────────────────────────
  // Per-component smart normalisation rules. Each rule receives the
  // current name (already whitespace-collapsed), mutates it, and
  // returns the new name. Rules are pure — they never call the API.
  // The composite `computeSuggestion()` runs them in order and
  // returns either the changed name or null (meaning "no
  // suggestion"). The active rule names are also returned so the
  // UI can surface a friendly "what changed" explainer.
  // ─────────────────────────────────────────────────────────────

  // Materials that should sit at the END of a flashing-style name so
  // the catalogue sorts by section first ("Apron 200 Galv" alongside
  // "Apron 200 Alu"). Recognises the legacy typo "Galvansied" too.
  const MATERIAL_TOKENS = ['Galv', 'Galvansied', 'Galvanised', 'Alu', 'AluGuard', 'Maxam', 'Maxx', 'ZinaCore', 'Zinc', 'Coloured', 'Copper', 'Colorsteel', 'COLORSTEEL'];

  // Normalise NZ steel gauge codes. Detects:
  //   "0.55mm BMT", "0.55 BMT", "0,55BMT", "0.40Gauge", "0.55 mm TCT"
  // → "0.55BMT" (canonical — per user's stated convention).
  // Note: `Gauge` is treated as a synonym of BMT because in NZ steel
  // catalogues the prefix-decimal "0.40Gauge" historically maps onto
  // the modern "0.40BMT" (base metal thickness) — the operator
  // confirmed they want the BMT spelling everywhere.
  const normaliseGauge = (s) => {
    const re = /\b(\d)[.,](\d{2})\s*(?:mm)?\s*(BMT|TCT|Gauge)\b/gi;
    let changed = false;
    const next = s.replace(re, (_m, whole, decimal, suffix) => {
      changed = true;
      const canon = suffix.toLowerCase() === 'gauge' ? 'BMT' : suffix.toUpperCase();
      return `${whole}.${decimal}${canon}`;
    });
    return changed ? next : null;
  };

  // Material → back rule. Scans ALL tokens for the FIRST material
  // token; if found anywhere other than the last position, lifts it
  // out and appends it to the end so the catalogue sorts by section
  // first. Handles:
  //   "Galv Apron 200" → "Apron 200 Galv"      (user's example)
  //   "Alu 1000W/1F"   → "1000W/1F Alu"        (flashings)
  //   "Ridge Galv (SE)"→ "Ridge (SE) Galv"     (ridges)
  // Already-canonical rows (material at the end) are skipped, as are
  // names that don't contain a recognised material token.
  const materialToBack = (s) => {
    if (!s) return null;
    const tokens = s.split(/\s+/);
    if (tokens.length < 2) return null;
    const isMaterial = (w) => MATERIAL_TOKENS.some((m) => w.toLowerCase() === m.toLowerCase());
    const lastIdx = tokens.length - 1;
    if (isMaterial(tokens[lastIdx])) return null;
    const matIdx = tokens.findIndex((t, i) => i < lastIdx && isMaterial(t));
    if (matIdx === -1) return null;
    const material = tokens[matIdx];
    const normalised = material.toLowerCase().startsWith('galv') ? 'Galv' : material;
    const remainder = tokens.filter((_, i) => i !== matIdx);
    return remainder.concat(normalised).join(' ');
  };

  const computeSuggestion = useCallback((row) => {
    const name = (row.name || '').trim();
    if (!name) return { next: null, rules: [], extraPatch: {} };
    let next = name;
    const rulesFired = [];
    const extraPatch = {};   // Fields other than `name` that the rule
                             // wants to update on save (e.g. supplier_code).

    // 1) Universal cleanup — collapse whitespace runs.
    const compact = next.replace(/\s+/g, ' ');
    if (compact !== next) { rulesFired.push('whitespace'); next = compact; }

    // 2) Aquaseal: WITH → WITH CLIPS so the parser tags retrofits.
    if (component === 'aquaseal'
        && /\bWITH\b/i.test(next)
        && !/\bWITH\s+CLIPS\b/i.test(next)) {
      next = next.replace(/\bWITH\b/i, 'WITH CLIPS');
      rulesFired.push('aquaseal-retrofit-clips');
    }

    // 2b) Aquaseal: canonicalise "Lead Based" / "Leadbased" →
    //     "Lead-Based" so the parser always tags lead rows and the
    //     catalogue sorts cleanly. Idempotent — already-canonical
    //     names are left alone.
    if (component === 'aquaseal') {
      const leadRe = /\bLead[\s-]*based\b/i;
      const m = next.match(leadRe);
      if (m && m[0] !== 'Lead-Based') {
        next = next.replace(leadRe, 'Lead-Based');
        rulesFired.push('aquaseal-lead-canonical');
      }
    }

    // 3c) Ridge: structured-field extract. Same pattern as Sheet —
    //     pulls class / material / width / length_mm / work_type out
    //     of the row name and writes them into operator-override
    //     columns. Never overwrites a pre-existing override.
    if (component === 'ridge') {
      const IRON_MATS = ['ZinaCore', 'Maxam', 'Galv', 'Maxx', 'Zinc'];   // longest first; retired names fold via canonicalMaterial
      const TILE_STYLES = ['Modern', 'Old', 'Clay', 'Tudor', 'Steep', 'Conc'];
      let pickedClass = '';
      let pickedMaterial = '';
      let pickedWidth = '';
      let pickedLen = null;
      let pickedWork = '';
      // Length
      const lm = next.match(/(\d{3,4})\s*mm/);
      if (lm) { const v = parseInt(lm[1], 10); if (v >= 100 && v <= 1500) pickedLen = v; }
      // Iron
      for (const m of IRON_MATS) {
        if (new RegExp(`\\b${m}\\b`, 'i').test(next)) { pickedMaterial = canonicalMaterial(m); pickedClass = 'iron'; break; }
      }
      // Width — only iron
      if (/\bWide\b/i.test(next)) pickedWidth = 'Wide';
      else if (pickedClass === 'iron') pickedWidth = 'Standard';
      // Mortar work types
      if (/\bBed\s*&\s*Point\b/i.test(next)) { pickedWork = 'Bed & Point'; pickedClass = 'mortar'; }
      else if (/\bPoint\b/i.test(next) && !/\bBed\s*&\s*Point\b/i.test(next)) { pickedWork = 'Point'; pickedClass = 'mortar'; }
      else if (/\bReplace\b/i.test(next)) pickedWork = 'Replace';
      else if (/\bSupply\b/i.test(next)) pickedWork = 'Supply';
      // Tile style — fallback when class isn't already iron
      if ((pickedClass === '' || pickedClass === 'mortar' || pickedClass === 'tile') && !pickedMaterial) {
        for (const m of [...TILE_STYLES].sort((a, b) => b.length - a.length)) {
          if (new RegExp(`\\b${m}\\b`, 'i').test(next)) {
            pickedMaterial = m === 'Clay' ? 'Clay' : 'Concrete';
            if (pickedClass === '') pickedClass = 'tile';
            break;
          }
        }
      }
      if (pickedClass === '' && pickedLen) pickedClass = 'tile';
      const assignIfFresh = (key, val) => {
        if (val === null || val === '') return false;
        const existing = row[key];
        if (existing !== undefined && existing !== null && existing !== '') return false;
        extraPatch[key] = val;
        return true;
      };
      let touched = false;
      if (assignIfFresh('class_override', pickedClass)) touched = true;
      if (assignIfFresh('material_override', pickedMaterial)) touched = true;
      if (assignIfFresh('width_override', pickedWidth)) touched = true;
      if (assignIfFresh('length_mm_override', pickedLen)) touched = true;
      if (assignIfFresh('work_type_override', pickedWork)) touched = true;
      if (touched) rulesFired.push('ridge-structured-extract');
    }

    // 3d) Valley: structured-field extract. Mirrors the Sheet/Ridge
    //     rule. Pulls work_type / material / width_mm / folds out of
    //     "New Galv Valley (300G/3F)" style names. Aliases for
    //     ZinaCore (ZCore / Zcore) collapse onto the canonical name.
    if (component === 'valley') {
      const VALLEY_MATS = [
        { canon: 'Maxam',    re: /\bMaxam\b|\bMaxx\b|\bZ(?:ina)?Core\b/i },
        { canon: 'Galv',     re: /\bGalv\b/i },
        { canon: 'Zinc',     re: /\bZinc\b/i },
      ];
      let pickedWork = '';
      let pickedMat = '';
      let pickedWidth = null;
      let pickedFolds = null;
      if (/\bReplace\b/i.test(next)) pickedWork = 'Replace';
      else if (/\bNew\b/i.test(next)) pickedWork = 'New';
      for (const { canon, re } of VALLEY_MATS) {
        if (re.test(next)) { pickedMat = canon; break; }
      }
      const gm = next.match(/(\d{2,4})\s*G/i);
      if (gm) {
        const v = parseInt(gm[1], 10);
        if (v >= 100 && v <= 1000) pickedWidth = v;
      }
      const fm = next.match(/(\d{1,2})\s*F\b/i);
      if (fm) {
        const v = parseInt(fm[1], 10);
        if (v >= 1 && v <= 15) pickedFolds = v;
      }
      const assignIfFresh = (key, val) => {
        if (val === null || val === '') return false;
        const existing = row[key];
        if (existing !== undefined && existing !== null && existing !== '') return false;
        extraPatch[key] = val;
        return true;
      };
      let touched = false;
      if (assignIfFresh('work_type_override', pickedWork)) touched = true;
      if (assignIfFresh('material_override', pickedMat)) touched = true;
      if (assignIfFresh('width_mm_override', pickedWidth)) touched = true;
      if (assignIfFresh('folds_override', pickedFolds)) touched = true;
      if (touched) rulesFired.push('valley-structured-extract');
    }
    if (component === 'sheet') {
      const g = normaliseGauge(next);
      if (g && g !== next) { next = g; rulesFired.push('gauge-canonical'); }
    }

    // 3b) Sheet: structured field extract. Parses the row name and
    //     writes profile / material / gauge / coverage / rate_type
    //     into operator-override columns so the structured filters
    //     and per-row chips light up consistently. Only fires when
    //     at least one field can be extracted; never overwrites a
    //     pre-existing override.
    if (component === 'sheet') {
      // Profile list — Translucent / Polycarbonate are NOT here.
      // They're materials, not profiles. Composition (Fibreglass vs
      // Polycarbonate) is extracted into a separate column.
      const SHEET_PROFILES = ['Corrugate', 'MultiRib', 'MultiDek', 'TrimRib', 'RibLine', 'Eurolite', 'Topglass'];
      const SHEET_MATERIALS = ['Galv', 'Maxam', 'Maxx', 'ZinaCore', 'Zinc', 'Alu', 'AluGuard', 'Colorsteel', 'Coloured', 'Copper', 'Translucent', 'Polycarbonate', 'Polycarb', 'Fibreglass', 'Fiberglass', 'Suntuf', 'Laserlite'];
      // Profile — first canonical match, case-insensitive, with
      // optional trailing 'd' (Corrugate/Corrugated).
      let pickedProfile = '';
      for (const p of SHEET_PROFILES) {
        const re = new RegExp(`\\b${p}d?\\b`, 'i');
        if (re.test(next)) { pickedProfile = p; break; }
      }
      // Material — first canonical match. Polycarb / Fibreglass /
      // Suntuf / Laserlite all map to the unified "Translucent"
      // material so the cascade family stays consistent.
      const TRANSLUCENT_ALIASES = ['Translucent', 'Polycarbonate', 'Polycarb', 'Fibreglass', 'Fiberglass', 'Suntuf', 'Laserlite'];
      // Composition is the substance under a Translucent material —
      // either Fibreglass or Polycarbonate. Suntuf is polycarb;
      // Laserlite is fibreglass.
      const COMPOSITION_MAP = {
        'Polycarbonate': 'Polycarbonate', 'Polycarb': 'Polycarbonate', 'Suntuf': 'Polycarbonate',
        'Fibreglass': 'Fibreglass', 'Fiberglass': 'Fibreglass', 'Laserlite': 'Fibreglass',
      };
      let pickedMaterial = '';
      let pickedComposition = '';
      for (const m of SHEET_MATERIALS) {
        const re = new RegExp(`\\b${m}\\b`, 'i');
        if (re.test(next)) {
          if (TRANSLUCENT_ALIASES.includes(m)) {
            pickedMaterial = 'Polycarbonate';
            if (COMPOSITION_MAP[m]) pickedComposition = COMPOSITION_MAP[m];
          } else {
            pickedMaterial = canonicalMaterial(m);
          }
          break;
        }
      }
      // Rate type — accept /mtr, /m2, /m², /sheet, /lin.m
      let pickedRate = '';
      const rtMatch = next.match(/\/\s*(mtr|m2|m²|sheet|lin\.?\s*m)\b/i);
      if (rtMatch) {
        const rv = rtMatch[1].toLowerCase().replace(/\s|\./g, '');
        if (rv === 'mtr' || rv === 'linm') pickedRate = '/mtr';
        else if (rv === 'm2' || rv === 'm²') pickedRate = '/m²';
        else if (rv === 'sheet') pickedRate = '/sheet';
      }
      // Gauge — canonical 0.NNBMT form.
      let pickedGauge = '';
      const gMatch = next.match(/(\d)[.,](\d{2})\s*(?:mm)?\s*(?:Gauge|BMT|TCT)/i);
      if (gMatch) pickedGauge = `${gMatch[1]}.${gMatch[2]}BMT`;
      // Coverage — first plausible NNNmm token.
      let pickedCover = null;
      const cMatch = next.match(/(\d{3,4}(?:[.,]\d+)?)\s*mm/);
      if (cMatch) {
        const v = parseFloat(cMatch[1].replace(',', '.'));
        if (v >= 200 && v <= 2000) pickedCover = Number.isInteger(v) ? v : v;
      }

      const assignIfFresh = (key, val) => {
        if (val === null || val === '') return false;
        const existing = row[key];
        if (existing !== undefined && existing !== null && existing !== '') return false;
        extraPatch[key] = val;
        return true;
      };
      let touched = false;
      if (assignIfFresh('profile_override', pickedProfile)) touched = true;
      if (assignIfFresh('material_override', pickedMaterial)) touched = true;
      if (assignIfFresh('composition_override', pickedComposition)) touched = true;
      if (assignIfFresh('rate_type_override', pickedRate)) touched = true;
      if (assignIfFresh('gauge_override', pickedGauge)) touched = true;
      if (assignIfFresh('coverage_mm_override', pickedCover)) touched = true;
      if (touched) rulesFired.push('sheet-structured-extract');
    }

    // 4) Material → back for the flashing-style components. Same
    //    rule applies to anything that historically prefixed the
    //    material onto the front of the row name.
    if (['flashing', 'ridge', 'valley', 'barge', 'spouting'].includes(component)) {
      const m = materialToBack(next);
      if (m && m !== next) { next = m; rulesFired.push('material-to-back'); }
    }

    // 5) Supplier-code extract — applies to every component. Detects
    //    a trailing uppercase alnum token (looks like a supplier
    //    part-number, e.g. "DFE101BC") and strips it from the name
    //    AND writes it onto the row's supplier_code field so the
    //    code lives only in its dedicated column. Only fires when:
    //      - There's a recognisable trailing CODE token (2+ uppercase
    //        letters followed by ≥3 letters/digits, total ≥5 chars).
    //      - The supplier_code field is currently empty OR matches
    //        the extracted code (the rule never overwrites a
    //        pre-existing different code).
    //    The hint message tells the operator both halves of the patch.
    const trailing = next.match(/^(.*?)[\s·\-—|]+([A-Z]{2,}[A-Z0-9]{3,})\s*$/);
    if (trailing) {
      const stem = trailing[1].trim();
      const code = trailing[2];
      const existingCode = (row.supplier_code || '').trim();
      if (stem && (!existingCode || existingCode === code)) {
        next = stem;
        if (!existingCode) extraPatch.supplier_code = code;
        rulesFired.push(existingCode ? 'supplier-code-strip' : 'supplier-code-extract');
      }
    }

    if (next === name && Object.keys(extraPatch).length === 0) {
      return { next: null, rules: [], extraPatch: {} };
    }
    return { next: next !== name ? next : name, rules: rulesFired, extraPatch };
  }, [component]);

  // Human-readable explainer for each rule id so the UI doesn't just
  // show cryptic flags.
  const RULE_LABELS = {
    'whitespace': 'collapsed double spaces',
    'aquaseal-retrofit-clips': 'appended "CLIPS" — this row looks like a Retrofit Dektite so the parser will tag it correctly',
    'aquaseal-lead-canonical': 'canonicalised "Lead Based" / "Leadbased" → "Lead-Based" — used on tile roofs',
    'gauge-canonical': 'normalised gauge code to canonical "0.55BMT" form',
    'material-to-back': 'moved the material prefix to the end (e.g. "Galv Apron 200" → "Apron 200 Galv")',
    'sheet-structured-extract': 'extracted Profile / Material / Gauge / Coverage / Rate-type from the row name into their own columns',
    'ridge-structured-extract': 'extracted Class / Material / Width / Length / Work-type from the row name into their own columns',
    'valley-structured-extract': 'extracted Work / Material / Width / Folds from the row name into their own columns (Maxx/ZinaCore/Zcore canonicalised to Maxam)',
    'supplier-code-extract': 'extracted the trailing supplier code and copied it into the Supplier Code column',
    'supplier-code-strip': 'stripped the trailing supplier code from the name (it already lives in the Supplier Code column)',
  };

  const ordered = entries; // already sorted by name from the GET
  const [idx, setIdx] = useState(0);
  const [draftName, setDraftName] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [skippedCount, setSkippedCount] = useState(0);
  const row = ordered[idx];
  const sg = row ? computeSuggestion(row) : { next: null, rules: [] };
  const suggestion = sg.next;
  // Seed the draft when the operator advances/retreats. If a smart
  // suggestion exists, pre-fill it so the operator can hit Save
  // immediately to accept; otherwise pre-fill the original name.
  useEffect(() => {
    if (!row) return;
    setDraftName(suggestion || (row.name || ''));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row?._id]);

  const suggestionsCount = ordered.reduce((n, r) => n + (computeSuggestion(r).next ? 1 : 0), 0);
  if (!row) return null;

  const advance = () => {
    if (idx < ordered.length - 1) setIdx(idx + 1);
    else onClose();
  };

  const skip = () => {
    setSkippedCount((c) => c + 1);
    advance();
  };

  const save = async () => {
    const clean = (draftName || '').trim();
    if (!clean) return;
    const samePatch = clean === (row.name || '').trim() && Object.keys(sg.extraPatch || {}).length === 0;
    if (samePatch) { skip(); return; }
    setBusy(true);
    try {
      await api.put(`/component-rate-tables/${row._id}`, { ...row, ...(sg.extraPatch || {}), name: clean });
      setSavedCount((c) => c + 1);
      onSaved();
      advance();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  // Apply all remaining suggestions in one shot — handy for the
  // Aquaseal "WITH" → "WITH CLIPS" pass once the operator has eyeballed
  // a couple and trusts the heuristic.
  const applyAllRemaining = async () => {
    if (!window.confirm(`Apply ${suggestionsCount - savedCount} suggested renames in one batch? You can still edit individual rows afterwards.`)) return;
    setBusy(true);
    let ok = 0;
    for (const r of ordered) {
      const s = computeSuggestion(r);
      if (!s.next) continue;
      try {
        await api.put(`/component-rate-tables/${r._id}`, { ...r, ...(s.extraPatch || {}), name: s.next });
        ok += 1;
      } catch (e) { /* keep going */ }
    }
    setBusy(false);
    setSavedCount((c) => c + ok);
    onSaved();
    onClose();
  };

  // Active rules for the current component — shown as chips in the
  // header so the operator can see at-a-glance what the wizard is
  // scanning for.
  const ACTIVE_RULES_BY_COMPONENT = {
    aquaseal:  ['whitespace', 'aquaseal-retrofit-clips', 'aquaseal-lead-canonical', 'supplier-code-extract'],
    sheet:     ['whitespace', 'gauge-canonical', 'sheet-structured-extract', 'supplier-code-extract'],
    flashing:  ['whitespace', 'material-to-back', 'supplier-code-extract'],
    ridge:     ['whitespace', 'material-to-back', 'ridge-structured-extract', 'supplier-code-extract'],
    valley:    ['whitespace', 'material-to-back', 'valley-structured-extract', 'supplier-code-extract'],
    barge:     ['whitespace', 'material-to-back', 'supplier-code-extract'],
    spouting:  ['whitespace', 'material-to-back', 'supplier-code-extract'],
  };
  const activeRules = ACTIVE_RULES_BY_COMPONENT[component] || ['whitespace'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="bulk-normalise-modal" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl ring-1 ring-slate-300 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 px-5 py-3 border-b border-slate-200">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500">{componentLabel} — review names</div>
            <h3 className="text-lg font-bold text-[#0F172A]">Row {idx + 1} of {ordered.length}</h3>
            <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3">
              <span><strong className="text-emerald-700">{savedCount}</strong> saved</span>
              <span><strong className="text-slate-600">{skippedCount}</strong> skipped</span>
              <span>·</span>
              <span><strong className="text-[#D35400]">{suggestionsCount}</strong> suggestion{suggestionsCount === 1 ? '' : 's'} detected</span>
            </div>
            {/* Active-rule chips so the operator knows what the
                wizard is scanning for on this component. */}
            <div className="flex flex-wrap items-center gap-1 mt-1.5" data-testid="bulk-normalise-active-rules">
              <span className="text-[9px] uppercase font-bold tracking-wider text-slate-400">Rules:</span>
              {activeRules.map((r) => (
                <span
                  key={r}
                  className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 ring-1 ring-slate-200"
                  title={RULE_LABELS[r] || r}
                >{r}</span>
              ))}
            </div>
          </div>
          <button onClick={onClose} data-testid="bulk-normalise-close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        <div className="p-5 space-y-3">
          {/* Side-by-side current vs draft (and the suggestion when present). */}
          <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500">Current name</div>
          <div className="font-mono text-sm text-slate-700 ring-1 ring-slate-200 bg-slate-50 rounded p-2" data-testid="bulk-normalise-current">{row.name}</div>

          {suggestion && (
            <div className="ring-1 ring-amber-200 bg-amber-50 rounded p-2 text-xs flex items-start gap-2" data-testid="bulk-normalise-suggestion">
              <Sparkles size={14} className="text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase font-bold tracking-wide text-amber-700">Suggested rename</div>
                <div className="font-mono text-amber-900 break-words">{suggestion}</div>
                {sg.rules.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-[10px] text-amber-800">
                    {sg.rules.map((r) => (
                      <li key={r} data-testid={`bulk-normalise-rule-${r}`}>
                        <span className="font-semibold">·</span> {RULE_LABELS[r] || r}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button
                onClick={() => setDraftName(suggestion)}
                className="px-2 py-1 rounded bg-amber-100 text-amber-800 ring-1 ring-amber-300 text-[10px] font-semibold hover:bg-amber-200"
                data-testid="bulk-normalise-accept-suggestion"
              >accept</button>
            </div>
          )}

          <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500">Edit name</div>
          <input
            data-testid="bulk-normalise-draft"
            value={draftName}
            onChange={(e) => setDraftName(e.target.value)}
            className="w-full px-3 py-2 ring-1 ring-slate-300 focus:ring-[#D35400] rounded text-sm font-mono outline-none"
          />

          {/* Row context: supplier_code + rates so the operator can sanity-
              check before renaming. */}
          <div className="text-[10px] text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1">
            {row.supplier_code && <span>Supplier: <code className="font-mono text-slate-700">{row.supplier_code}</code></span>}
            {row.buy_cost ? <span>Buy: <span className="font-mono-num">${(row.buy_cost || 0).toFixed(2)}</span></span> : null}
            {row.materials_rate ? <span>Materials: <span className="font-mono-num">${(row.materials_rate || 0).toFixed(2)}</span></span> : null}
            {row.labour_rate ? <span>Labour: <span className="font-mono-num">${(row.labour_rate || 0).toFixed(2)}</span></span> : null}
          </div>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50/60">
          <button
            data-testid="bulk-normalise-prev"
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0 || busy}
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200 rounded hover:bg-white disabled:opacity-40"
          ><ChevronLeft size={13} className="inline" /> Prev</button>
          <button
            data-testid="bulk-normalise-skip"
            onClick={skip}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200 rounded hover:bg-white disabled:opacity-40"
          >Skip</button>
          {suggestionsCount > 0 && (
            <button
              data-testid="bulk-normalise-apply-all"
              onClick={applyAllRemaining}
              disabled={busy}
              className="px-3 py-1.5 text-xs font-semibold text-amber-800 bg-amber-100 ring-1 ring-amber-300 rounded hover:bg-amber-200 disabled:opacity-40"
              title="Apply every suggested rename in one batch"
            >Apply all {suggestionsCount}</button>
          )}
          <div className="flex-1" />
          <button
            data-testid="bulk-normalise-save"
            onClick={save}
            disabled={busy || !draftName.trim()}
            className="px-4 py-1.5 text-xs font-bold text-white bg-[#D35400] hover:bg-[#A04000] rounded disabled:opacity-50"
          >{busy ? '…' : 'Save & next'} <ChevronRight size={13} className="inline" /></button>
        </div>
      </div>
    </div>
  );
}

/* -- ComponentSlider ---------------------------------------------------- */
/* Horizontally-scrollable strip of component pills with chevron arrows.   */
/* Shared by the top & bottom of the Component Rate Tables page so the     */
/* user can navigate between components without scrolling all the way to   */
/* the left-side nav.                                                      */
function ComponentSlider({ testIdSuffix, components, active, counts, onPick }) {
  const ref = useRef(null);
  const scroll = (dir) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };
  return (
    <div data-testid={`component-slider-${testIdSuffix}`} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-2 py-2">
      <button
        type="button"
        data-testid={`slider-${testIdSuffix}-prev`}
        onClick={() => scroll(-1)}
        className="flex-shrink-0 w-8 h-8 rounded-md border border-slate-200 text-slate-500 hover:text-[#D35400] hover:border-[#D35400] flex items-center justify-center transition-all"
        title="Scroll left"
      >
        <ChevronLeft size={16} />
      </button>
      <div
        ref={ref}
        className="flex-1 flex items-center gap-1.5 overflow-x-auto scroll-smooth py-1"
        style={{ scrollbarWidth: 'thin' }}
      >
        {components.map(c => {
          const isActive = active === c.key;
          const ct = counts[c.key];
          return (
            <button
              key={c.key}
              type="button"
              data-testid={`slider-${testIdSuffix}-${c.key}`}
              onClick={() => onPick(c.key)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-[#D35400] text-white border-[#D35400] shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-[#D35400] hover:text-[#D35400]'
              }`}
            >
              <span>{c.label}</span>
              {ct && <span className={`text-[10px] font-mono-num ${isActive ? 'text-white/80' : 'text-slate-400'}`}>{ct.type_count}</span>}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        data-testid={`slider-${testIdSuffix}-next`}
        onClick={() => scroll(1)}
        className="flex-shrink-0 w-8 h-8 rounded-md border border-slate-200 text-slate-500 hover:text-[#D35400] hover:border-[#D35400] flex items-center justify-center transition-all"
        title="Scroll right"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}
